import { formatGymPrice } from '../../../utils/gymMoney';
import {
  formatDurationLabel,
  guestPassDurationLabel,
  subscriptionDurationLabel,
  cleanSalePackageName,
  durationFromSalePackage,
  GUEST_PASS_DURATION_OPTIONS,
  SALE_DURATION_OPTIONS,
  SPA_OXYBAR_DURATION_OPTIONS,
  SUBSCRIPTION_DURATION_OPTIONS,
} from '../../receptionist/sales/saleDurationUtils';
import {
  parsePaymentSplitsFromRow,
  paymentMethodLabel,
  methodKeyFromLabel,
} from '../../../utils/splitPaymentUtils';
import { parseSalePeriodFromNote } from '../../receptionist/sales/salePeriodUtils';
import { membershipDurationDisplay } from '../../../utils/membershipDates';
import {
  saleRowPaid,
  saleRowOriginalPaid,
  isRemainingClearPayment,
  isPaymentWalkIn,
} from '../../receptionist/sales/salePaymentUtils';
import { isDashboardRenewalPayment } from '../../../services/shared/dashboardSaleMetrics';

/** Built-in chart of accounts for the gym ledger */
export const DEFAULT_CHART_OF_ACCOUNTS = [
  { code: '1000', name: 'Cash', type: 'asset', ledgerGroup: 'cash', description: 'Cash collections and cash payments' },
  { code: '1010', name: 'Bank', type: 'asset', ledgerGroup: 'bank', description: 'Card, bank transfer, and online deposits' },
  { code: '1100', name: 'Accounts Receivable', type: 'asset', ledgerGroup: 'bank', description: 'Outstanding / pending member payments' },
  { code: '4000', name: 'Membership Revenue', type: 'income', ledgerGroup: 'bank', description: 'Package and subscription sales' },
  { code: '4100', name: 'Guest Pass Revenue', type: 'income', ledgerGroup: 'bank', description: 'Guest / day pass fees' },
  { code: '4200', name: 'Other Income', type: 'income', ledgerGroup: 'bank', description: 'Registration, PT, classes, late fees' },
  { code: '5000', name: 'Operating Expenses', type: 'expense', ledgerGroup: 'cash', description: 'General gym operating costs' },
  { code: '5100', name: 'Trainer Salaries', type: 'expense', ledgerGroup: 'bank', description: 'Monthly trainer salary cost' },
  { code: '5200', name: 'Rent & Utilities', type: 'expense', ledgerGroup: 'bank', description: 'Rent, electricity, water, internet' },
  { code: '5300', name: 'Equipment & Maintenance', type: 'expense', ledgerGroup: 'cash', description: 'Equipment purchase and repair' },
  { code: '5400', name: 'Marketing', type: 'expense', ledgerGroup: 'cash', description: 'Ads and promotions' },
  { code: '5900', name: 'Refunds & Returns', type: 'expense', ledgerGroup: 'bank', description: 'Refunded payments' },
];

/** @deprecated use DEFAULT_CHART_OF_ACCOUNTS / getChartOfAccountsForGym */
export const CHART_OF_ACCOUNTS = DEFAULT_CHART_OF_ACCOUNTS;

export const ACCOUNT_TYPES = [
  { value: 'asset', label: 'Asset' },
  { value: 'income', label: 'Income' },
  { value: 'expense', label: 'Expense' },
];

export const ACCOUNT_SIDES = [
  { value: 'credit', label: 'Credit' },
  { value: 'debit', label: 'Debit' },
];

/** Cash vs Card (card amounts roll into Bank) */
export const ACCOUNT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
];

export const ACCOUNT_LEDGER_GROUPS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank', label: 'Bank' },
];

export const EXPENSE_CATEGORIES = [
  'Rent',
  'Utilities',
  'Equipment',
  'Maintenance',
  'Marketing',
  'Supplies',
  'Salaries',
  'Other',
];

function chartStorageKey(gymId) {
  return `gym:${gymId || 'default'}:chart_of_accounts_custom`;
}

/** Map cash/card/bank into ledger groups used by Chart of Accounts. */
export function normalizeLedgerGroup(formOrRow = {}) {
  const raw = String(
    formOrRow.ledgerGroup || formOrRow.method || formOrRow.tender || '',
  ).trim().toLowerCase();
  if (raw === 'cash') return 'cash';
  if (raw === 'card' || raw === 'bank') return 'bank';
  // Legacy: debit custom accounts → cash, credit → bank
  const side = String(formOrRow.side || '').trim().toLowerCase();
  if (side === 'credit') return 'bank';
  if (side === 'debit') return 'cash';
  const type = String(formOrRow.type || '').trim().toLowerCase();
  if (type === 'expense') return 'cash';
  if (type === 'income' || type === 'asset') return 'bank';
  return 'cash';
}

export function normalizeAccountMethod(formOrRow = {}) {
  const raw = String(formOrRow.method || formOrRow.tender || '').trim().toLowerCase();
  if (raw === 'cash' || raw === 'card') return raw;
  return normalizeLedgerGroup(formOrRow) === 'bank' ? 'card' : 'cash';
}

/** Map credit/debit (or legacy type) to a ledger type used by reports. */
export function normalizeAccountSide(formOrRow = {}) {
  const method = normalizeAccountMethod(formOrRow);
  // Cash entries do not use debit/credit — treat as debit for storage
  if (method === 'cash' && !formOrRow.side) return 'debit';
  const rawSide = String(formOrRow.side || '').trim().toLowerCase();
  if (rawSide === 'credit' || rawSide === 'debit') return rawSide;
  const type = String(formOrRow.type || '').trim().toLowerCase();
  if (type === 'income' || type === 'asset') return 'credit';
  return 'debit';
}

export function typeFromSide(side) {
  return side === 'credit' ? 'income' : 'expense';
}

function normalizeCustomPosting(entry, fallbackDate = localDateKey()) {
  const amount = Math.max(0, Number(entry?.amount) || 0);
  if (amount <= 0) return null;
  return {
    id: String(entry?.id || `POST-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
    date: String(entry?.date || fallbackDate).slice(0, 10),
    amount,
    description: String(entry?.description || '').trim(),
  };
}

function normalizeCustomAccount(row) {
  const side = normalizeAccountSide(row);
  const createdAt = String(row.createdAt || row.date || '').slice(0, 10)
    || localDateKey();
  const description = String(row.description || '').trim();

  let postings = Array.isArray(row.postings)
    ? row.postings.map((p) => normalizeCustomPosting(p, createdAt)).filter(Boolean)
    : [];

  // Migrate legacy single-amount accounts into one posting
  if (!postings.length) {
    const legacyAmount = Math.max(0, Number(row.amount) || 0);
    if (legacyAmount > 0) {
      postings = [{
        id: `OPEN-${String(row.code || '').trim() || 'legacy'}`,
        date: createdAt,
        amount: legacyAmount,
        description: description || 'Manual account balance',
      }];
    }
  }

  const amount = postings.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const method = normalizeAccountMethod(row);
  const ledgerGroup = normalizeLedgerGroup({ ...row, method });

  return {
    code: String(row.code || '').trim(),
    name: String(row.name || '').trim(),
    side,
    method,
    ledgerGroup,
    type: typeFromSide(side),
    amount,
    description,
    createdAt,
    postings,
    isCustom: true,
  };
}

export function loadCustomChartAccounts(gymId) {
  if (!gymId || typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(chartStorageKey(gymId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((row) => row && row.code && row.name)
      .map(normalizeCustomAccount)
      .filter((row) => row.code && row.name);
  } catch {
    return [];
  }
}

function saveCustomChartAccounts(gymId, rows) {
  if (!gymId || typeof window === 'undefined') return;
  localStorage.setItem(chartStorageKey(gymId), JSON.stringify(rows || []));
}

/** Default + custom accounts for a gym (unique by code). */
export function getChartOfAccountsForGym(gymId) {
  const custom = loadCustomChartAccounts(gymId);
  const byCode = new Map();
  DEFAULT_CHART_OF_ACCOUNTS.forEach((a) => {
    byCode.set(a.code, {
      ...a,
      side: a.type === 'expense' ? 'debit' : 'credit',
      method: a.ledgerGroup === 'cash' ? 'cash' : 'card',
      ledgerGroup: a.ledgerGroup || 'bank',
      amount: 0,
      isCustom: false,
    });
  });
  custom.forEach((a) => {
    if (!byCode.has(a.code)) byCode.set(a.code, a);
  });
  return [...byCode.values()].sort((a, b) => String(a.code).localeCompare(String(b.code)));
}

export function suggestNextAccountCode(sideOrType, gymId) {
  const chart = getChartOfAccountsForGym(gymId);
  const side = sideOrType === 'credit' || sideOrType === 'debit'
    ? sideOrType
    : normalizeAccountSide({ type: sideOrType });
  const prefix = side === 'credit' ? '4' : '5';
  const used = new Set(chart.map((a) => a.code));
  for (let n = 0; n < 900; n += 1) {
    const code = `${prefix}${String(100 + n).padStart(3, '0')}`;
    if (!used.has(code) && !DEFAULT_CHART_OF_ACCOUNTS.some((a) => a.code === code)) {
      if (prefix === '4' && Number(code) < 4300) continue;
      if (prefix === '5' && Number(code) < 5500) continue;
      return code;
    }
  }
  return `${prefix}${Date.now().toString().slice(-3)}`;
}

export function addCustomChartAccount(gymId, form) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const name = String(form.name || '').trim();
  if (!name) throw new Error('Account name is required.');

  const method = normalizeAccountMethod(form);
  const resolvedSide = method === 'cash'
    ? 'debit'
    : normalizeAccountSide({ ...form, method });
  const amount = Number(form.amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('Enter a valid amount greater than zero.');
  }

  let code = String(form.code || '').trim();
  if (!code) code = suggestNextAccountCode(resolvedSide, gymId);
  if (!/^\d{3,6}$/.test(code)) {
    throw new Error('Account code must be 3–6 digits.');
  }

  const custom = loadCustomChartAccounts(gymId);
  const byCode = custom.find((a) => a.code === code);
  const byName = custom.find((a) => a.name.toLowerCase() === name.toLowerCase());

  // Same custom account again → add another entry and sum amounts
  if (byCode || byName) {
    const target = byCode || byName;
    const next = appendPostingToCustomAccount(target, {
      ...form,
      name,
      method,
      side: resolvedSide,
      amount,
    });
    const rows = custom.map((row) => (row.code === target.code ? next : row));
    saveCustomChartAccounts(gymId, rows);
    return { ...next, appended: true };
  }

  const existing = getChartOfAccountsForGym(gymId);
  if (existing.some((a) => a.code === code)) {
    throw new Error(`Account code ${code} already exists.`);
  }
  if (existing.some((a) => a.name.toLowerCase() === name.toLowerCase())) {
    throw new Error('An account with this name already exists.');
  }

  const createdAt = localDateKey();
  const row = normalizeCustomAccount({
    code,
    name,
    side: resolvedSide,
    method,
    ledgerGroup: normalizeLedgerGroup({ method }),
    description: form.description,
    createdAt,
    postings: [{
      id: `POST-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      date: createdAt,
      amount,
      description: String(form.description || '').trim() || 'Opening entry',
    }],
  });
  saveCustomChartAccounts(gymId, [...custom, row]);
  return { ...row, appended: false };
}

function appendPostingToCustomAccount(existing, form) {
  const amount = Number(form.amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('Enter a valid amount greater than zero.');
  }
  const method = form.method != null
    ? normalizeAccountMethod(form)
    : normalizeAccountMethod(existing);
  const side = method === 'cash'
    ? 'debit'
    : (form.side != null ? normalizeAccountSide({ ...form, method }) : existing.side);
  const posting = normalizeCustomPosting({
    id: `POST-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    date: String(form.date || localDateKey()).slice(0, 10),
    amount,
    description: String(form.description || '').trim() || existing.description || 'Manual entry',
  }, existing.createdAt);
  const postings = [...(existing.postings || []), posting];
  return normalizeCustomAccount({
    ...existing,
    name: String(form.name || existing.name || '').trim() || existing.name,
    side,
    method,
    ledgerGroup: normalizeLedgerGroup({ method }),
    description: String(form.description || existing.description || '').trim(),
    postings,
  });
}

export function updateCustomChartAccount(gymId, code, form) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const needle = String(code || '').trim();
  if (DEFAULT_CHART_OF_ACCOUNTS.some((a) => a.code === needle)) {
    throw new Error('Built-in accounts cannot be edited.');
  }

  const custom = loadCustomChartAccounts(gymId);
  const index = custom.findIndex((a) => a.code === needle);
  if (index < 0) throw new Error('Custom account not found. Try refreshing the page.');

  const name = String(form.name || '').trim();
  if (!name) throw new Error('Account name is required.');

  const all = getChartOfAccountsForGym(gymId);
  if (all.some((a) => a.code !== needle && a.name.toLowerCase() === name.toLowerCase())) {
    throw new Error('An account with this name already exists.');
  }

  const existing = custom[index];
  const method = form.method != null
    ? normalizeAccountMethod(form)
    : normalizeAccountMethod(existing);
  const side = method === 'cash'
    ? 'debit'
    : normalizeAccountSide({ ...existing, ...form, method });

  // Editing with an amount posts another entry (amounts add up)
  const next = appendPostingToCustomAccount(existing, {
    ...form,
    name,
    method,
    side,
  });
  const rows = custom.slice();
  rows[index] = next;
  saveCustomChartAccounts(gymId, rows);
  return next;
}

export function deleteCustomChartAccount(gymId, code) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const needle = String(code || '').trim();
  if (DEFAULT_CHART_OF_ACCOUNTS.some((a) => a.code === needle)) {
    throw new Error('Built-in accounts cannot be deleted.');
  }
  const custom = loadCustomChartAccounts(gymId);
  const next = custom.filter((a) => a.code !== needle);
  if (next.length === custom.length) {
    throw new Error('Custom account not found. Try refreshing the page.');
  }
  saveCustomChartAccounts(gymId, next);
  return true;
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatCurrency(amount) {
  const abs = Math.abs(Number(amount) || 0);
  const formatted = formatGymPrice(abs);
  return Number(amount) < 0 ? `-${formatted}` : formatted;
}

function sum(list, field = 'amount') {
  return list.reduce((total, row) => total + (Number(row[field]) || 0), 0);
}

function paymentDate(payment) {
  const raw = payment.date || payment.paidAt || payment.paid_at || payment.createdAt || payment.created_at || '';
  const text = String(raw).trim();
  if (!text) return '';

  // Plain calendar date (no time) — trust as-is (avoids UTC shift)
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

  // MM/DD/YYYY
  const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) {
    return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  }

  // ISO timestamps: use the leading calendar date so Detailed Report matches
  // Guest Pass / Renewal Sale history (those pages use paid_at.slice(0, 10)).
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);

  // Other date strings → local calendar day
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    return localDateKey(parsed);
  }

  return '';
}

/**
 * Day cash was actually collected.
 * Membership-day rows force `date` to start_date for listing — use paidAt/collectedDate
 * so CASH SALE / Petty Cash do not pull money from another collection day.
 */
export function paymentCollectedCalendarDay(payment) {
  if (!payment) return '';
  const collectedRaw = payment.collectedDate
    || payment.paidAt
    || payment.paid_at
    || payment.collected_at
    || '';
  if (collectedRaw) {
    const day = paymentDate({ date: collectedRaw, paidAt: collectedRaw });
    if (day) return day;
  }
  if (payment.fromMembershipDay) {
    // last resort: if paid_at missing, do not treat start_date as collection
    return '';
  }
  return paymentDate(payment);
}

/** True when this sale's tenders belong on the Z / Petty Cash report day. */
export function paymentCollectedOnReportDay(payment, day) {
  const target = String(day || '').slice(0, 10);
  if (!target) return false;
  const collected = paymentCollectedCalendarDay(payment);
  if (collected) return collected === target;
  if (payment?.fromMembershipDay) {
    // paid_at missing but tenders exist → treat membership start day as collection day
    // (same-day sales often omit / clear paid_at after edits)
    if (zSaleHasCollectedMoney(payment) || countsTowardCashSale(payment)) {
      const startDay = membershipStartCalendarDay(payment)
        || String(payment.date || '').slice(0, 10);
      return Boolean(startDay) && startDay === target;
    }
    return false;
  }
  const rowDay = paymentDate(payment) || String(payment?.date || '').slice(0, 10);
  return Boolean(rowDay) && rowDay === target;
}

/**
 * Whether a sale should feed Z CASH SALE / Petty Cash receipts.
 * Matches Finance → Cash Accounts (isCoaCollectiblePayment): pending rows with
 * cash in payment_splits count; complimentary excluded.
 */
export function countsTowardCashSale(payment) {
  if (!payment) return false;
  if (payment.complimentary) return false;
  const status = String(payment.status || '').toLowerCase().trim();
  if (status === 'complimentary') return false;
  return isCoaCollectiblePayment(payment);
}

/**
 * Cash for a report day — collection day (paid_at / collectedDate) + cash tender splits.
 * Membership-day rows keep `date` = start_date for listing — cash still follows paidAt.
 */
export function pettyCashOnReportDay(payment, day) {
  if (!countsTowardCashSale(payment)) return 0;
  const target = String(day || '').slice(0, 10);
  if (!target) return 0;
  if (!paymentCollectedOnReportDay(payment, target)) return 0;
  return pettyCashReceiptAmount(payment);
}

/** Bank / Card / QR collected on a report day (Petty Cash counterpart). */
export function bankOnReportDay(payment, day) {
  if (!countsTowardCashSale(payment)) return 0;
  const target = String(day || '').slice(0, 10);
  if (!target) return 0;
  if (!paymentCollectedOnReportDay(payment, target)) return 0;
  return bankReceiptAmount(payment);
}

/** Sum of drawer cash for a day — same number for Petty Cash, Z CASH SALE, Detailed. */
export function sumCashCollectedOnDay(salesRows = [], day) {
  return sumTenderCollectedByMethod(salesRows, day).cash.amount;
}

/**
 * Cash / Card / Bank / QR from payment splits (collection day).
 * Prefer {@link sumDetailedReportModeOfPayment} for Z Report tables.
 */
export function sumTenderCollectedByMethod(salesRows = [], day) {
  const target = String(day || '').slice(0, 10);
  const empty = () => ({ amount: 0, count: 0 });
  const buckets = {
    cash: empty(),
    card: empty(),
    bank: empty(),
    qr: empty(),
  };
  if (!target) return buckets;

  const seenCash = new Set();
  const seenNonCash = new Set();

  (salesRows || []).forEach((row, index) => {
    if (isRefund(row)) return;
    if (!paymentCollectedOnReportDay(row, target)) return;

    const cashAmt = pettyCashOnReportDay(row, target);
    if (cashAmt > 0) {
      const cashKey = String(row.rawId || row.id || `${row.member}-cash-${index}`);
      if (!seenCash.has(cashKey)) {
        seenCash.add(cashKey);
        buckets.cash.amount += cashAmt;
        buckets.cash.count += 1;
      }
    }

    if (row.complimentary || String(row.status || '').toLowerCase() === 'complimentary') return;
    if (!countsTowardCashSale(row)) return;

    // Use sale-day collection only (strip later remaining merged into the original row).
    const lines = ledgerCollectionSplitLines(row).filter((s) => {
      const tender = normalizeTenderMethod(s.method || row.method || 'cash');
      return tender !== 'Cash';
    });

    lines.forEach((split, splitIndex) => {
      const tender = normalizeTenderMethod(split.method || row.method || 'cash');
      if (tender === 'Cash') return;
      const amt = Number(split.amount) || 0;
      if (amt <= 0) return;
      const bucketId = tender === 'Card'
        ? 'card'
        : (tender === 'QR' ? 'qr' : 'bank'); // Bank + Online
      const key = String(
        row.rawId || row.id || `${row.member}-${bucketId}-${splitIndex}-${amt}`,
      );
      if (seenNonCash.has(`${key}-${bucketId}`)) return;
      seenNonCash.add(`${key}-${bucketId}`);
      buckets[bucketId].amount += amt;
      buckets[bucketId].count += 1;
    });
  });

  return buckets;
}

/**
 * Source of truth for Z Report PAYMENT METHOD tables:
 * sum Detailed Report Mode of Payment column amounts separately
 * (cash / credit card / bank / qr). Skips pending & complimentary.
 */
export function sumDetailedReportModeOfPayment(sections = []) {
  const empty = () => ({ amount: 0, count: 0 });
  const buckets = {
    cash: empty(),
    card: empty(),
    bank: empty(),
    qr: empty(),
  };

  const bucketForMode = (modeRaw) => {
    const mode = String(modeRaw || '').toLowerCase().trim();
    if (!mode || mode === 'pending' || mode === 'complementry' || mode === 'complimentary') {
      return '';
    }
    if (mode === 'cash') return 'cash';
    if (mode === 'credit card' || mode === 'card') return 'card';
    if (mode === 'qr') return 'qr';
    if (
      mode === 'bank'
      || mode === 'online'
      || mode.includes('bank')
      || mode.includes('transfer')
    ) {
      return 'bank';
    }
    return '';
  };

  const addToBucket = (modeRaw, amt) => {
    const amount = Number(amt) || 0;
    if (amount <= 0) return;
    const id = bucketForMode(modeRaw);
    if (!id) return;
    buckets[id].amount += amount;
    buckets[id].count += 1;
  };

  (sections || []).forEach((section) => {
    (section?.rows || []).forEach((row) => {
      const amt = Number(row?.amount) || 0;
      const mode = String(row?.modeOfPayment || '').toLowerCase().trim();

      // Prefer Detailed Report display columns (already stripped of later remaining).
      // Do not re-sum raw paymentSplits on expanded rows — that double-counts.
      if (amt > 0 && mode && mode !== 'pending' && mode !== 'complementry' && mode !== 'complimentary') {
        const id = bucketForMode(mode);
        if (id) {
          addToBucket(mode, amt);
          return;
        }
        const parts = mode.split(/\s*\+\s*/);
        if (parts.length > 1) {
          parts.forEach((part) => addToBucket(part, amt / parts.length));
          return;
        }
      }

      // Fallback for unmapped rows: sale-day tenders only
      const lines = ledgerCollectionSplitLines(row).filter((s) => (Number(s.amount) || 0) > 0);
      if (lines.length > 1) {
        lines.forEach((split) => addToBucket(split.method || row.method, split.amount));
        return;
      }
      if (amt > 0 && mode) {
        addToBucket(mode, amt);
      }
    });
  });

  return buckets;
}

/** Membership start day as stored/shown on Memberships list (no timezone shift). */
export function membershipStartCalendarDay(paymentOrMembership) {
  const raw = paymentOrMembership?.startDate
    || paymentOrMembership?.start_date
    || paymentOrMembership?.start
    || '';
  const text = String(raw).trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return paymentDate({ date: text });
}

export function isNewMembershipPaymentSource(payment) {
  if (isDashboardRenewalPayment(payment)) return false;
  const source = String(payment?.source || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  return (
    source === 'subscription'
    || source === 'membership'
    || source === 'new_membership'
    || source === 'subscriptions'
  );
}

/**
 * New Memberships list / SALE SUMMARY count — only memberships whose
 * start_date is the report day (Memberships list / Detailed Report).
 * Subscription cash collected for a different start day stays in payment
 * methods via salesRows, but must not inflate NEW MEMBERSHIP NO.
 */
export function isNewMembershipReportRow(row) {
  if (!row || !row.fromMembershipDay) return false;
  return isNewMembershipPaymentSource(row)
    || String(row.source || '').trim().toLowerCase().replace(/-/g, '_') === 'subscription';
}

/**
 * Subscription payment collected on a day other than memberships.start_date.
 * Must not appear under New Memberships or Miscellaneous on any date —
 * it is already listed on the start_date via fetchMembershipSalesForDate.
 */
export function isOrphanNewMembershipPayment(row) {
  if (!row || row.fromMembershipDay) return false;
  if (isNewMembershipPaymentSource(row)) return true;
  const normalized = normalizeZPaymentSource(row);
  return normalized === 'subscription' && !row.fromMembershipDay;
}

/** True when a payment belongs on the Z / Detailed Report day (local calendar). */
function paymentMatchesReportDay(payment, day) {
  const target = String(day || '').slice(0, 10);
  if (!target) return false;

  // Membership-day rows from fetchMembershipSalesForDate are already day-scoped
  if (payment?.fromMembershipDay) {
    const rowDay = membershipStartCalendarDay(payment)
      || paymentDate(payment)
      || String(payment.date || payment.startDate || '').slice(0, 10);
    return Boolean(rowDay) && rowDay === target;
  }

  // Subscription / new-membership *payment* rows (not fromMembershipDay):
  // match the collection day so CASH SALE lands on paid_at. NEW MEMBERSHIP
  // counts still use fromMembershipDay / isNewMembershipReportRow only.
  if (isNewMembershipPaymentSource(payment)) {
    return paymentDate(payment) === target;
  }

  return paymentDate(payment) === target;
}

/** Inclusive calendar range — used by Z TOTAL SALE SUMMARY Start/End. */
function paymentMatchesReportRange(payment, dateFrom, dateTo) {
  const start = String(dateFrom || '').slice(0, 10);
  const end = String(dateTo || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    return paymentMatchesReportDay(payment, end || start);
  }
  if (start === end) return paymentMatchesReportDay(payment, start);

  const inRange = (day) => Boolean(day) && day >= start && day <= end;

  if (payment?.fromMembershipDay) {
    const rowDay = membershipStartCalendarDay(payment)
      || paymentDate(payment)
      || String(payment.date || payment.startDate || '').slice(0, 10);
    return inRange(rowDay);
  }
  if (isNewMembershipPaymentSource(payment)) {
    return inRange(paymentDate(payment));
  }
  return inRange(paymentDate(payment));
}

function monthKey(dateStr) {
  const text = String(dateStr || '').trim();
  if (/^\d{4}-\d{2}/.test(text)) return text.slice(0, 7);
  if (!text) return '';
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text.slice(0, 7);
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key) {
  if (!key || key.length < 7) return key || '—';
  const [year, month] = key.split('-');
  const label = MONTH_LABELS[Number(month) - 1] || month;
  return `${label} ${year}`;
}

function isCompleted(payment) {
  const status = String(payment.status || '').toLowerCase().trim();
  // Empty status = treat as completed (legacy rows); match CoA / subscription filters
  if (!status) return true;
  return status === 'completed' || status === 'paid' || status === 'success';
}

function isPending(payment) {
  const status = String(payment.status || '').toLowerCase();
  return status === 'pending' || status === 'unpaid' || status === 'overdue';
}

/** Standalone registration rows (amount 0 + leftover splits) duplicate membership fees. */
function isPhantomRegistrationPayment(payment) {
  const source = String(payment?.source || payment?.type || '')
    .toLowerCase()
    .replace(/-/g, '_');
  if (source !== 'registration') return false;
  const header = Number(payment.amount) || Number(payment.totalPaid) || 0;
  const pkg = String(payment.package || payment.package_name || '').toLowerCase();
  return header <= 0 || pkg === 'registration';
}

/** Payment with collected amount that should appear in PAY / SALE COA ledgers. */
export function isCoaCollectiblePayment(payment) {
  if (isRefund(payment)) return false;
  if (isPhantomRegistrationPayment(payment)) return false;
  const status = String(payment.status || '').trim().toLowerCase();
  if (status === 'voided') return false;
  const header = Number(payment.amount) || Number(payment.totalPaid) || 0;
  const fromSplits = parsePaymentSplitsFromRow(payment)
    .reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  const amount = header > 0 ? header : fromSplits;
  if (!Number.isFinite(amount) || amount <= 0) return false;
  if (!status) return true;
  if (status === 'refunded' || status === 'cancelled' || status === 'canceled' || status === 'voided') return false;
  // Same collected-cash statuses as Dashboard / Z (`z_payment_status_ok`).
  // `partial` was dropped before — those paid rows never reached Journal SALE.
  return (
    status === 'completed'
    || status === 'paid'
    || status === 'success'
    || status === 'pending'
    || status === 'unpaid'
    || status === 'overdue'
    || status === 'partial'
    || status === 'complimentary'
  );
}

function isRefund(payment) {
  const status = String(payment.status || '').toLowerCase();
  return status === 'refunded' || Number(payment.amount) < 0;
}

function isGuestPassPayment(payment) {
  const source = String(payment?.source || '').toLowerCase().replace(/-/g, '_');
  if (source === 'guest_pass') return true;
  // Explicit other sale sources must never be reclassified by package text
  // (e.g. package "PT Sale — DAY PASS" where DAY PASS is a trainer name)
  if (
    source === 'pt_sale'
    || source === 'zumba_sale'
    || source === 'yoga_sale'
    || source === 'spa_sale'
    || source === 'locker_sale'
    || source === 'oxybar_sale'
    || source === 'renewal_sale'
    || source === 'renewal'
    || source === 'subscription'
    || source === 'membership'
    || source === 'new_membership'
    || source === 'gym_apparel'
    || source === 'miscellaneous_sale'
  ) {
    return false;
  }
  if (String(payment?.type || '') === 'Guest Pass') return true;
  const pkg = String(payment?.package || payment?.package_name || '');
  // Trainer/item titles like "PT Sale — DAY PASS" are not guest passes
  if (/^(pt|zumba|yoga|spa|locker|oxybar)\s*sale\b/i.test(pkg)) return false;
  // Require "guest pass" / "day pass" — bare "guest" was matching unrelated packages
  return /guest\s*pass|\bday\s*pass\b/i.test(pkg);
}

/**
 * Normalize guest / walk-in names for payment↔guest_passes dedupe.
 * Payments often use "OXYGYM Walk-In Customer" (members.full_name) while
 * guest_passes store "Walk-In Customer" — without this both rows appear.
 */
function guestPassDedupeName(value) {
  const name = String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!name) return '';
  if (/walk[\s-]?in/.test(name) || /oxy.?gym/.test(name)) return 'walk-in customer';
  return name;
}

/** Phone digits from guest_passes.phone or payments payment_note "Phone: …". */
function guestPassPhoneDigits(row) {
  const direct = String(row?.phone || '').replace(/\D/g, '');
  if (direct.length >= 7) return direct;
  const note = String(row?.paymentNote || row?.payment_note || '').trim();
  const match = note.match(/Phone:\s*([0-9+\-\s]+)/i);
  if (match) {
    const digits = String(match[1] || '').replace(/\D/g, '');
    if (digits.length >= 7) return digits;
  }
  return '';
}

/**
 * Guest / day-pass display name for SALE REPORT.
 * Prefer note labels like "ALI & RAJA DAY PASS" over Walk-In Customer.
 */
function guestPassDisplayName(row) {
  const note = String(row?.paymentNote || row?.payment_note || '').trim();
  if (note) {
    const dayPassMatch = note.match(/^(.+?)\s+DAY\s*PASS\b/i);
    if (dayPassMatch) {
      const label = dayPassMatch[1].replace(/^Customer:\s*/i, '').trim();
      if (label) return label;
    }
    const firstPart = note.split(/\s*[·|]\s*/)[0].trim();
    if (
      firstPart
      && !/^phone:/i.test(firstPart)
      && !/^duration:/i.test(firstPart)
      && !/^customer:/i.test(firstPart)
    ) {
      const cleaned = firstPart.replace(/\s+DAY\s*PASS\s*$/i, '').trim();
      if (cleaned && !/walk[\s-]?in/i.test(cleaned) && !/oxy.?gym/i.test(cleaned)) {
        return cleaned;
      }
    }
  }
  const raw = String(row?.member || row?.name || '').trim();
  if (!raw || raw === '—') return 'Walk-In Customer';
  if (/walk[\s-]?in/i.test(raw) || /oxy.?gym\s+walk-?in/i.test(raw)) return 'Walk-In Customer';
  return raw;
}

/** Fixed sale categories shown on Chart of Accounts → Sales Entries. */
export const SALE_CATEGORY_ORDER = [
  'Membership',
  'Guest Pass',
  'Renewal Sale',
  'PT Sale',
  'Locker Sale',
  'Spa Sale',
  'Oxybar Sale',
  'Zumba Sale',
  'Yoga Sale',
  'Gym Apparel',
  'Miscellaneous Sale',
];

const SALE_SOURCE_TO_CATEGORY = {
  subscription: 'Membership',
  guest_pass: 'Guest Pass',
  renewal_sale: 'Renewal Sale',
  renewal: 'Renewal Sale',
  pt_sale: 'PT Sale',
  locker_sale: 'Locker Sale',
  spa_sale: 'Spa Sale',
  oxybar_sale: 'Oxybar Sale',
  zumba_sale: 'Zumba Sale',
  yoga_sale: 'Yoga Sale',
  gym_apparel: 'Gym Apparel',
  miscellaneous_sale: 'Miscellaneous Sale',
};

/** Map a payment / sale row into a Sales Entries category label. */
export function saleCategoryFromPayment(payment) {
  if (isDashboardRenewalPayment(payment)) return 'Renewal Sale';
  const source = String(payment?.source || '').trim().toLowerCase();
  if (source && SALE_SOURCE_TO_CATEGORY[source]) return SALE_SOURCE_TO_CATEGORY[source];
  if (isGuestPassPayment(payment)) return 'Guest Pass';
  if (isRefund(payment)) return 'Other';
  const type = String(payment?.type || '').trim();
  if (type && SALE_CATEGORY_ORDER.includes(type)) return type;
  if (/guest/i.test(type)) return 'Guest Pass';
  if (/membership|subscription/i.test(type) || !source || source === 'subscription') return 'Membership';
  if (source) {
    return source
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
  return 'Membership';
}

function isMembershipPayment(payment) {
  if (isGuestPassPayment(payment) || isRefund(payment)) return false;
  const category = saleCategoryFromPayment(payment);
  return category === 'Membership';
}

function accountForPayment(payment) {
  if (isRefund(payment)) return '5900';
  if (isGuestPassPayment(payment)) return '4100';
  if (isMembershipPayment(payment)) return '4000';
  return '4200';
}

function isPaidExpense(expense) {
  const status = String(expense.status || 'paid').toLowerCase();
  return status === 'paid' || status === 'completed';
}

function accountForExpense(expense) {
  const category = String(expense.category || '').toLowerCase();
  // Category always wins so Chart of Accounts matches how the expense was classified
  if (/rent|utilit|electric|water|internet/i.test(category)) return '5200';
  if (/equip|maint/i.test(category)) return '5300';
  if (/market|ads|promo/i.test(category)) return '5400';
  if (/salary|salaries|payroll/i.test(category)) return '5100';
  if (/suppl/i.test(category)) return '5000';
  if (expense.accountCode && String(expense.accountCode) !== '5000') return expense.accountCode;
  return '5000';
}

function last12MonthKeys() {
  const keys = [];
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date();
    d.setDate(1);
    d.setHours(12, 0, 0, 0);
    d.setMonth(d.getMonth() - i);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    keys.push(`${year}-${month}`);
  }
  return keys;
}

/**
 * Build all ledger views from live gym data.
 */
export function buildFinanceLedgerBundle({
  members = [],
  memberships = [],
  payments = [],
  trainers: _trainers = [],
  guestPasses = [],
  expenses = [],
  branches = [],
  chartOfAccounts = DEFAULT_CHART_OF_ACCOUNTS,
}) {
  const chart = (chartOfAccounts?.length ? chartOfAccounts : DEFAULT_CHART_OF_ACCOUNTS)
    .map((a) => ({
      ...a,
      side: a.side || (a.type === 'expense' ? 'debit' : 'credit'),
      amount: Number(a.amount) || 0,
      isCustom: Boolean(a.isCustom),
    }));
  const accountNameByCode = (code) => chart.find((a) => a.code === code)?.name || code;

  const memberById = new Map(members.map((m) => [m.id, m]));

  const normalizedPayments = payments.map((p) => {
    const member = p.memberId ? memberById.get(p.memberId) : null;
    const date = paymentDate(p);
    // Keep full stored amount here (may include later remaining merge).
    // Cash/method ledgers use saleRowOriginalPaid / ledgerCollectionSplitLines
    // so sale-day stays original and PAY-RC owns the clear-day amount.
    const amount = saleRowPaid(p);
    const accountCode = accountForPayment(p);
    return {
      ...p,
      gymId: (p.gymId && p.gymId !== '—') ? p.gymId : (member?.gymId || '—'),
      member: p.member || member?.name || '—',
      branch: p.branch || member?.branch || '',
      date,
      amount,
      accountCode,
      accountName: accountNameByCode(accountCode),
      type: isRefund(p)
        ? 'Refund'
        : saleCategoryFromPayment(p),
      paidOn: date,
    };
  });

  const completedPayments = normalizedPayments.filter((p) => (
    isCompleted(p) && !isRefund(p) && saleRowPaid(p) > 0
  ));
  const refundRows = normalizedPayments
    .filter((p) => isRefund(p))
    .map((p) => ({
      ...p,
      amount: Math.abs(p.amount),
      reason: p.paymentNote || 'Refund',
    }));
  const outstandingRows = normalizedPayments
    .filter((p) => isPending(p))
    .map((p) => {
      const due = p.date;
      const daysOverdue = due
        ? Math.max(0, Math.floor((Date.now() - new Date(`${due}T00:00:00`).getTime()) / 86400000))
        : 0;
      return { ...p, dueDate: due, daysOverdue };
    });

  const membershipPayments = completedPayments.filter(isMembershipPayment);
  const guestPassIncome = guestPasses.map((g) => {
    const saleDay = paymentDate({ date: g.date || g.createdAt || g.created_at || g.startDate })
      || String(g.date || g.createdAt || g.startDate || '').slice(0, 10);
    return {
      id: g.id,
      member: g.name,
      gymId: g.referredByGymId || '—',
      package: 'Guest Pass',
      duration: g.duration || g.passType || '',
      amount: Number(g.fee) || 0,
      date: saleDay,
      createdAt: g.createdAt || g.created_at || '',
      startDate: g.startDate || '',
      method: 'Cash',
      status: 'completed',
      type: 'Guest Pass',
      source: 'guest_pass',
      accountCode: '4100',
      accountName: 'Guest Pass Revenue',
      branch: '',
      referredBy: g.referredBy || '',
    };
  });

  const expenseRows = expenses.map((e) => {
    const accountCode = accountForExpense(e);
    return {
      ...e,
      accountCode,
      accountName: accountNameByCode(accountCode),
    };
  });
  const paidExpenses = expenseRows.filter(isPaidExpense);

  // Only real expense records — do not invent monthly accruals from trainer profiles
  // (that was inflating Chart of Accounts / P&L with salary that was never paid).
  const trainerSalaryExpenses = paidExpenses.filter((e) => e.accountCode === '5100');
  const operatingExpenses = paidExpenses.filter((e) => e.accountCode !== '5100');
  const trainerSalaries = trainerSalaryExpenses.map((e) => ({
    id: e.id,
    trainer: e.description || e.category,
    branch: e.branch || '',
    amount: Number(e.amount) || 0,
    paidOn: e.date || '',
    status: e.status || 'paid',
    accountCode: '5100',
    accountName: 'Trainer Salaries',
  }));

  // Guest pass fees already captured as payments should not also come from guest_passes
  // (IDs never match across tables — use phone / normalized walk-in name + amount.)
  const guestPaymentKeys = new Set();
  completedPayments.filter(isGuestPassPayment).forEach((p) => {
    const amount = Number(p.amount) || 0;
    const name = guestPassDedupeName(p.member || p.name);
    if (name) guestPaymentKeys.add(`${name}|${amount.toFixed(2)}`);
    if (p.invoiceId) guestPaymentKeys.add(`inv:${String(p.invoiceId)}`);
    const phone = guestPassPhoneDigits(p);
    if (phone) {
      guestPaymentKeys.add(`phone:${phone}|${amount.toFixed(2)}`);
    }
  });
  const uniqueGuestPassIncome = guestPassIncome.filter((g) => {
    const amount = Number(g.amount) || Number(g.fee) || 0;
    const name = guestPassDedupeName(g.member || g.name);
    if (name && guestPaymentKeys.has(`${name}|${amount.toFixed(2)}`)) return false;
    if (g.invoiceId && guestPaymentKeys.has(`inv:${String(g.invoiceId)}`)) return false;
    const phone = guestPassPhoneDigits(g);
    if (phone && guestPaymentKeys.has(`phone:${phone}|${amount.toFixed(2)}`)) return false;
    return true;
  });

  const sumOriginalPaid = (list) => list.reduce(
    (total, row) => total + saleRowOriginalPaid(row),
    0,
  );
  // saleRowOriginalPaid: sale-day keeps original cash; remaining_clear keeps its own amount.
  const membershipIncome = sumOriginalPaid(membershipPayments);
  const guestIncome = sum(uniqueGuestPassIncome)
    + sumOriginalPaid(completedPayments.filter(isGuestPassPayment));
  const otherIncome = sumOriginalPaid(
    completedPayments.filter((p) => !isMembershipPayment(p) && !isGuestPassPayment(p)),
  );
  const refundTotal = sum(refundRows);
  const expenseTotal = sum(operatingExpenses);
  const trainerTotal = sum(trainerSalaryExpenses);
  const outstanding = sum(outstandingRows);
  const revenue = membershipIncome + guestIncome + otherIncome;
  const netProfit = revenue - refundTotal - expenseTotal - trainerTotal;

  const monthKeys = last12MonthKeys();
  const monthlyMembershipLedger = monthKeys.map((key) => {
    const rows = membershipPayments.filter((p) => monthKey(p.date) === key);
    const newMemberships = memberships.filter((m) => (
      monthKey(m.start || m.startDate || m.start_date || '') === key
    ));
    return {
      monthKey: key,
      label: monthLabel(key),
      paymentCount: rows.length,
      newMemberships: newMemberships.length,
      revenue: sumOriginalPaid(rows),
      packages: Object.entries(
        rows.reduce((acc, row) => {
          const pkg = row.package || 'Other';
          acc[pkg] = (acc[pkg] || 0) + saleRowOriginalPaid(row);
          return acc;
        }, {}),
      ).map(([name, amount]) => ({ name, amount })),
    };
  }).filter((row) => row.paymentCount > 0 || row.newMemberships > 0 || row.revenue > 0);

  // Category / account ledgers
  // CoA is manual-only: memberships/sales/payments do NOT auto-post.
  // Use Add Account (Cash or Card) when money is received.
  const categoryLedgers = chart.map((account) => {
    let entries = [];
    let balance = 0;

    entries.sort((a, b) => String(b.date).localeCompare(String(a.date)));

    // Custom accounts: each posted amount is its own entry; totals = sum of postings
    if (account.isCustom) {
      const isCredit = normalizeAccountSide(account) === 'credit';
      const postings = Array.isArray(account.postings) && account.postings.length
        ? account.postings
        : (Number(account.amount) > 0
          ? [{
            id: `OPEN-${account.code}`,
            date: String(account.createdAt || account.date || localDateKey()).slice(0, 10),
            amount: Number(account.amount) || 0,
            description: account.description || 'Manual account balance',
          }]
          : []);

      entries = postings.map((p) => {
        const amt = Math.max(0, Number(p.amount) || 0);
        return {
          id: p.id || `POST-${account.code}-${p.date}`,
          date: String(p.date || localDateKey()).slice(0, 10),
          description: p.description || account.description || 'Manual entry',
          gymId: '—',
          debit: isCredit ? 0 : amt,
          credit: isCredit ? amt : 0,
          ref: account.code,
        };
      }).sort((a, b) => String(b.date).localeCompare(String(a.date)));

      balance = postings.reduce((sumAmt, p) => sumAmt + (Number(p.amount) || 0), 0);
    }

    return {
      ...account,
      side: normalizeAccountSide(account),
      method: normalizeAccountMethod(account),
      ledgerGroup: account.ledgerGroup || normalizeLedgerGroup(account),
      entryCount: entries.length,
      balance,
      totalDebit: sum(entries, 'debit'),
      totalCredit: sum(entries, 'credit'),
      entries,
    };
  });

  // Per-member ledgers
  const memberLedgers = members.map((member) => {
    const memberPayments = normalizedPayments.filter((p) => p.memberId === member.id);
    const memberMemberships = memberships.filter((m) => m.memberId === member.id);
    const memberPasses = guestPasses.filter(
      (g) => g.referredByMemberId === member.id
        || String(g.referredByGymId || '').toLowerCase() === String(member.gymId || '').toLowerCase(),
    );

    const income = memberPayments
      .filter((p) => isCompleted(p) && !isRefund(p) && saleRowOriginalPaid(p) > 0)
      .reduce((total, p) => total + saleRowOriginalPaid(p), 0);
    const refunds = sum(memberPayments.filter((p) => isRefund(p)).map((p) => ({ amount: Math.abs(p.amount) })));
    const pending = sum(memberPayments.filter((p) => isPending(p)));
    const passFees = sum(memberPasses, 'fee');

    const ledgerEntries = [
      ...memberPayments.map((p) => {
        const paid = isRefund(p) ? Math.abs(Number(p.amount) || 0) : saleRowOriginalPaid(p);
        return {
          id: p.id,
          date: p.date,
          type: isRefund(p) ? 'Refund' : (isMembershipPayment(p) ? 'Membership Payment' : (isGuestPassPayment(p) ? 'Guest Pass' : 'Payment')),
          description: p.package || p.type,
          method: p.method || '—',
          debit: isRefund(p) ? paid : 0,
          credit: isRefund(p) ? 0 : paid,
          status: p.status,
          accountCode: p.accountCode,
        };
      }),
      ...memberPasses.map((g) => ({
        id: g.id,
        date: g.startDate || '',
        type: 'Guest Pass (referred)',
        description: `${g.name} — ${g.passType || 'Pass'}`,
        method: '—',
        debit: 0,
        credit: Number(g.fee) || 0,
        status: 'completed',
        accountCode: '4100',
      })),
    ].sort((a, b) => String(b.date).localeCompare(String(a.date)));

    let running = 0;
    const withBalance = [...ledgerEntries].reverse().map((entry) => {
      running += (entry.credit || 0) - (entry.debit || 0);
      return { ...entry, balance: running };
    }).reverse();

    return {
      memberId: member.id,
      gymId: member.gymId,
      name: member.name,
      phone: member.phone,
      email: member.email,
      branch: member.branch,
      status: member.status,
      joiningDate: member.joiningDate,
      package: member.package,
      memberships: memberMemberships,
      guestPasses: memberPasses,
      payments: memberPayments,
      ledgerEntries: withBalance,
      profitLoss: {
        revenue: income + passFees,
        membershipIncome: income,
        guestPassIncome: passFees,
        refunds,
        outstanding: pending,
        netContribution: income + passFees - refunds,
      },
    };
  });

  const paymentHistory = [
    ...normalizedPayments.map((p) => ({
      id: p.id,
      rawId: p.rawId || p.id,
      gymId: p.gymId,
      member: p.member,
      type: p.type,
      package: p.package || p.package_name || '—',
      source: p.source || '',
      branch: p.branch,
      amount: isRefund(p) ? -Math.abs(p.amount) : p.amount,
      date: p.date,
      method: p.method,
      status: p.status,
      accountCode: p.accountCode,
      paymentNote: p.paymentNote || '',
    })),
    ...uniqueGuestPassIncome.map((g) => ({
      id: g.id,
      rawId: g.id,
      gymId: g.gymId,
      member: g.member,
      type: 'Guest Pass',
      package: g.package || 'Guest Pass',
      source: 'guest_pass',
      branch: '',
      amount: g.amount,
      date: g.date,
      method: g.method,
      status: 'completed',
      accountCode: '4100',
      paymentNote: '',
    })),
  ].sort((a, b) => String(b.date).localeCompare(String(a.date)));

  const chartValues = monthKeys.map((key) =>
    sum(
      completedPayments
        .concat(uniqueGuestPassIncome)
        .filter((p) => monthKey(p.date) === key),
    ),
  );

  const branchOptions = [
    ...new Set([
      ...branches.map((b) => b.name).filter(Boolean),
      ...members.map((m) => m.branch).filter(Boolean),
    ]),
  ];

  return {
    members,
    memberships,
    payments: normalizedPayments,
    membershipPayments,
    guestPassIncome: uniqueGuestPassIncome,
    trainerSalaries,
    expenses: expenseRows,
    outstandingPayments: outstandingRows,
    refunds: refundRows,
    paymentHistory,
    monthlyMembershipLedger,
    categoryLedgers,
    memberLedgers,
    branchOptions,
    revenueSummary: {
      monthly: revenue,
      expenses: expenseTotal + trainerTotal,
      profit: netProfit,
      outstanding,
      collected: sum(completedPayments) + guestIncome,
    },
    profitReport: {
      revenue,
      membershipIncome,
      guestPassIncome: guestIncome,
      otherIncome,
      trainerSalaries: trainerTotal,
      expenses: expenseTotal,
      refunds: refundTotal,
      netProfit,
    },
    revenueChartData: {
      labels: monthKeys.map(monthLabel),
      values: chartValues,
    },
  };
}

export function computeFinanceSummary(args) {
  const membershipIncome = sum(
    (args.membershipPayments || []).filter((p) => p.status === 'paid' || p.status === 'completed'),
  );
  const otherIncome = sum(args.lateFees || []);
  const refundTotal = sum(args.refunds || []);
  const expenseTotal = sum(args.expenses || []);
  const trainerTotal = sum(args.trainerSalaries || []);
  const outstanding = sum(args.outstandingPayments || []);
  const collected = (args.paymentHistory || [])
    .filter((p) => (p.status === 'completed' || p.status === 'paid') && p.amount > 0)
    .reduce((total, p) => total + p.amount, 0);
  const revenue = membershipIncome + otherIncome;
  const netProfit = revenue - refundTotal - expenseTotal - trainerTotal;

  return {
    revenueSummary: {
      monthly: revenue,
      expenses: expenseTotal,
      profit: netProfit,
      outstanding,
      collected: collected || revenue,
    },
    profitReport: {
      revenue,
      membershipIncome,
      otherIncome,
      trainerSalaries: trainerTotal,
      expenses: expenseTotal,
      refunds: refundTotal,
      netProfit,
    },
  };
}

export function buildRevenueChartData(historyRows, fallback = { labels: [], values: [] }) {
  const positiveRows = (historyRows || []).filter((p) => p.amount > 0 && p.date);
  if (!positiveRows.length) {
    return { labels: fallback.labels || [], values: (fallback.labels || []).map(() => 0) };
  }
  const keys = last12MonthKeys();
  return {
    labels: keys.map(monthLabel),
    values: keys.map((key) => sum(positiveRows.filter((p) => monthKey(p.date) === key))),
  };
}

/** Local calendar YYYY-MM-DD (avoids UTC shift from toISOString). */
export function localDateKey(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Exact calendar day for vouchers / ledger rows (never shift via timezone).
 * Prefer YYYY-MM-DD from strings; for Date objects use UTC parts (Postgres `date`).
 */
export function calendarDateKey(value, fallback = '') {
  if (value == null || value === '') {
    return fallback ? calendarDateKey(fallback) : '';
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getUTCFullYear();
    const month = String(value.getUTCMonth() + 1).padStart(2, '0');
    const day = String(value.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  const dmy = raw.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  }
  return localDateKey(raw) || (fallback ? calendarDateKey(fallback) : '');
}

/**
 * Map tender / payment method into Z-report buckets:
 * Cash | Card | QR | Bank | Online | Other
 */
export function normalizeTenderMethod(method) {
  const raw = String(method || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
  if (!raw || raw === '—' || raw === '-') return 'Other';

  const hasQr = /\bqr\b|scan to pay|raast/.test(raw);
  const hasCash = /\bcash\b|counter/.test(raw);
  const hasCard = /\bcard\b|visa|master|debit card|credit card|\bpos\b/.test(raw);
  const hasBank = /\bbank\b|transfer|wire|cheque|check|deposit|ibft/.test(raw);
  const hasOnline = /\bonline\b|jazzcash|easypaisa|\bjazz\b|\beasy\b|wallet|mobile|upi|stripe|paypal/.test(raw);
  const hitCount = [hasQr, hasCash, hasCard, hasBank, hasOnline].filter(Boolean).length;
  // Combined labels like "Cash + Card" need payment_splits — do not collapse to Cash
  if (hitCount > 1) return 'Other';

  if (hasQr) return 'QR';
  if (hasCash) return 'Cash';
  if (hasCard) return 'Card';
  if (hasBank) return 'Bank';
  if (hasOnline) return 'Online';
  return 'Other';
}

/** Map a payment method into Cash / Card / QR / Bank Accounts. */
export function financeAccountBucket(method) {
  const tender = normalizeTenderMethod(method);
  if (tender === 'Cash') return 'cash';
  if (tender === 'Card') return 'card';
  if (tender === 'QR') return 'qr';
  // Bank transfers + JazzCash / EasyPaisa (online) land in Bank Accounts
  return 'bank';
}

/**
 * Convert receptionist subscription / sale payments into Cash / Card / QR / Bank
 * account rows so Finance tabs and CoA ledgers stay in sync with Sales.
 * Partial (pending) payments show collected amounts; fully paid updates on refresh.
 */
/**
 * Drop clone payment rows that inflate Payment / SALE ledgers.
 * Keeps one row per identical day+member+amount+source+package+splits (earliest created).
 * Also collapses duplicate payment_ref / rawId.
 */
export function dedupeFinancePayments(payments = []) {
  const byStableId = new Map();
  (payments || []).forEach((p) => {
    if (!p) return;
    const id = String(p.rawId || p.id || '').trim();
    if (!id || id === '—') return;
    if (!byStableId.has(id)) byStableId.set(id, p);
  });
  const list = byStableId.size
    ? [...byStableId.values()]
    : (payments || []).filter(Boolean);

  // Keep every distinct payment id. Collapsing on invoice_id / payment_ref
  // dropped real renewal/sale rows from Journal vs Dashboard.

  const groups = new Map();
  list.forEach((p) => {
    const day = paymentDate(p) || String(p.date || p.paidAt || '').slice(0, 10);
    const splits = parsePaymentSplitsFromRow(p)
      .map((s) => `${methodKeyFromLabel(s.method)}:${Number(s.amount) || 0}`)
      .sort()
      .join(',');
    const header = Number(p.amount) || Number(p.totalPaid) || 0;
    const splitSum = parsePaymentSplitsFromRow(p)
      .reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    const amount = header > 0 ? header : splitSum;
    const sourceNorm = String(p.source || p.type || '').toLowerCase().replace(/-/g, '_');
    // Each live payment row is unique — do not collapse walk-ins with same name/price/day.
    const paymentStableId = String(
      p.rawId || p.id || p.invoiceId || p.paymentId || p.payment_ref || '',
    ).trim();
    const fp = [
      day,
      String(p.member || '').trim().toLowerCase(),
      amount,
      sourceNorm,
      String(p.package || p.package_name || '').trim().toLowerCase(),
      splits,
      paymentStableId,
    ].join('|');
    const arr = groups.get(fp) || [];
    arr.push(p);
    groups.set(fp, arr);
  });

  const out = [];
  groups.forEach((arr) => {
    if (arr.length === 1) {
      out.push(arr[0]);
      return;
    }
    arr.sort((a, b) => {
      const ca = String(a.createdAt || a.created_at || a.date || a.paidAt || '');
      const cb = String(b.createdAt || b.created_at || b.date || b.paidAt || '');
      if (ca && cb && ca !== cb) return ca.localeCompare(cb);
      return String(a.rawId || a.id || '').localeCompare(String(b.rawId || b.id || ''));
    });
    out.push(arr[0]);
  });
  return dropClonedFinancePayments(out);
}

function normalizeLedgerMemberName(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/\b(mr|mrs|ms|miss)\.?\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function ledgerDayValue(payment) {
  const day = paymentDate(payment) || String(payment?.date || payment?.paidAt || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : '';
}

function ledgerCollectedAmount(payment) {
  const header = Number(payment?.amount) || Number(payment?.totalPaid) || 0;
  if (header > 0) return Math.round(header * 100) / 100;
  return Math.round(parsePaymentSplitsFromRow(payment)
    .reduce((sum, s) => sum + (Number(s.amount) || 0), 0) * 100) / 100;
}

function ledgerPackageKey(payment) {
  return String(payment?.package || payment?.package_name || '')
    .trim()
    .toLowerCase();
}

function membersLikelySame(a, b) {
  const na = normalizeLedgerMemberName(a);
  const nb = normalizeLedgerMemberName(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const ta = na.split(' ').filter((t) => t.length > 2);
  const tb = nb.split(' ').filter((t) => t.length > 2);
  const setB = new Set(tb);
  const shared = ta.filter((t) => setB.has(t));
  if (shared.length >= 2) return true;
  if (shared.length >= 1) {
    const lastA = ta[ta.length - 1] || '';
    const lastB = tb[tb.length - 1] || '';
    if (lastA && lastB && lastA.slice(0, 3) === lastB.slice(0, 3)) return true;
  }
  return false;
}

function calendarDayDiff(a, b) {
  if (!a || !b) return 99;
  const da = new Date(`${a}T12:00:00`);
  const db = new Date(`${b}T12:00:00`);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return 99;
  return Math.abs(Math.round((da.getTime() - db.getTime()) / 86400000));
}

/**
 * Import retries / walk-in drafts landed as extra CoA card rows:
 * Ashfaq/Safeerah 31 Jul clones of 30 Jul; Walk-In Azadi drafts vs named members.
 * Keep the named / earlier row; drop the clone from live ledgers only.
 */
function dropClonedFinancePayments(payments = []) {
  const rows = (payments || []).filter(Boolean);
  const ranked = [...rows].sort((a, b) => {
    const aWalk = isPaymentWalkIn(a) ? 1 : 0;
    const bWalk = isPaymentWalkIn(b) ? 1 : 0;
    if (aWalk !== bWalk) return aWalk - bWalk;
    const da = ledgerDayValue(a);
    const db = ledgerDayValue(b);
    if (da !== db) return da.localeCompare(db);
    return String(a.createdAt || a.created_at || '')
      .localeCompare(String(b.createdAt || b.created_at || ''));
  });
  const kept = [];
  ranked.forEach((row) => {
    if (isRemainingClearPayment(row)) {
      kept.push(row);
      return;
    }
    const amount = ledgerCollectedAmount(row);
    const pkg = ledgerPackageKey(row);
    const day = ledgerDayValue(row);
    const walkIn = isPaymentWalkIn(row);
    const clone = kept.find((other) => {
      if (isRemainingClearPayment(other)) return false;
      if (Math.abs(ledgerCollectedAmount(other) - amount) > 0.02) return false;
      if (ledgerPackageKey(other) !== pkg) return false;
      const otherDay = ledgerDayValue(other);
      const days = calendarDayDiff(day, otherDay);
      if (walkIn && !isPaymentWalkIn(other)) {
        return days <= 0;
      }
      if (!walkIn && isPaymentWalkIn(other)) return false;
      if (days > 1) return false;
      return membersLikelySame(other.member, row.member);
    });
    if (!clone) kept.push(row);
  });
  return kept;
}

export function buildSubscriptionAccountRows(payments = []) {
  const cash = [];
  const card = [];
  const qr = [];
  const bank = [];

  const pushRow = (bucket, base, amount) => {
    const paid = Number(amount) || 0;
    if (paid <= 0) return;
    if (bucket === 'cash') {
      cash.push({ ...base, payment: paid, method: 'Cash' });
      return;
    }
    if (bucket === 'card') {
      card.push({ ...base, credit: 0, debit: paid, method: 'Card' });
      return;
    }
    if (bucket === 'qr') {
      qr.push({ ...base, credit: 0, debit: paid, method: 'QR' });
      return;
    }
    bank.push({ ...base, credit: 0, debit: paid, method: 'Bank' });
  };

  dedupeFinancePayments(payments).forEach((p) => {
    if (!isCoaCollectiblePayment(p)) return;

    const code = String(p.id || p.paymentId || p.rawId || '')
      .replace(/\s+/g, '')
      .slice(0, 16) || `S${Date.now().toString().slice(-6)}`;
    const accountTitle = String(p.package || p.type || 'Membership').trim() || 'Membership';
    const memberLabel = (p.member && p.member !== '—')
      ? String(p.member).trim()
      : 'Payment';
    const partialLabel = isPending(p) ? ' (partial)' : '';
    const gymId = (p.gymId && p.gymId !== '—') ? String(p.gymId).trim() : '—';
    const date = paymentDate(p) || String(p.date || p.paidOn || '').slice(0, 10);
    if (!date) return;

    // Sale-day tenders exclude later remaining merged into amount/splits;
    // remaining_clear rows keep their own clear-day amount.
    const lines = ledgerCollectionSplitLines(p);

    lines.forEach((split, index) => {
      const splitAmount = Number(split.amount) || 0;
      if (splitAmount <= 0) return;
      const bucket = financeAccountBucket(split.method || paymentMethodLabel(split.method));
      const base = {
        id: `sub-${bucket}-${p.rawId || p.id || code}-${index}`,
        code,
        gymId,
        accountTitle,
        description: `${memberLabel}${partialLabel}`,
        date,
        readOnly: true,
        source: p.source || 'subscription',
        status: p.status || '',
        createdAt: date,
      };
      pushRow(bucket, base, splitAmount);
    });
  });

  const byDateDesc = (a, b) => String(b.date).localeCompare(String(a.date));
  return {
    cash: cash.sort(byDateDesc),
    card: card.sort(byDateDesc),
    qr: qr.sort(byDateDesc),
    bank: bank.sort(byDateDesc),
  };
}

/** Calendar day for CoA Sales / Sales Entries ledgers (matches Dashboard sale window). */
export function coaSaleLedgerDate(payment) {
  if (!payment) return '';

  // RPC `sale_day` = paid_at::date — same day Dashboard uses for 1–16 filters.
  const rpcDay = String(payment.saleDay || payment.sale_day || '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(rpcDay)) return rpcDay;

  // Guest pass table rows: sale/created day, not visit start alone
  const type = String(payment.type || '').trim();
  if (isGuestPassPayment(payment) || type === 'Guest Pass') {
    const saleDay = paymentDate(payment)
      || paymentDate({ date: payment.createdAt || payment.created_at || payment.paidOn })
      || membershipStartCalendarDay(payment);
    if (saleDay) return saleDay;
  }

  // Collection day (paid_at). Period start_date is not the Journal/Dashboard window.
  return paymentDate(payment)
    || membershipStartCalendarDay(payment)
    || '';
}

/** Build sales entry rows for CoA SALE children and Sales Entries tab. */
export function buildCoaSalesEntryRows(pool = []) {
  const seen = new Set();
  return dedupeFinancePayments(pool)
    .filter((p) => {
      if (!isCoaCollectiblePayment(p)) return false;
      const key = String(p.rawId || p.id || `${p.member}-${p.amount}-${p.date}-${p.method}-${p.source || p.type}`);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((p) => {
      const category = saleCategoryFromPayment(p);
      const code = String(p.id || p.paymentId || p.rawId || '')
        .replace(/\s+/g, '')
        .slice(0, 16) || '—';
      const isPartial = isPending(p);
      const date = coaSaleLedgerDate(p) || '—';
      return {
        id: String(p.rawId || p.id || code),
        code,
        gymId: (p.gymId && p.gymId !== '—') ? p.gymId : '—',
        member: p.member || '—',
        category,
        item: p.package || p.accountTitle || p.type || category,
        method: normalizeTenderMethod(p.method),
        amount: saleRowOriginalPaid(p),
        date,
        status: p.status || '',
        isPartial,
      };
    })
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
}

export function buildCoaSalesEntryRowsFromFinance(data = {}) {
  return buildCoaSalesEntryRows(financePaymentPool(data));
}

/** Unique live payments for admin finance (payments + orphan guest_passes only). */
export function financePaymentPool(data = {}) {
  const seen = new Set();
  const pool = [];
  const push = (row) => {
    if (!row) return;
    const key = String(row.rawId || row.id || '').trim();
    if (key) {
      if (seen.has(key)) return;
      seen.add(key);
    }
    pool.push(row);
  };
  (data.payments || []).forEach(push);
  (data.guestPassIncome || []).forEach(push);
  return dropClonedFinancePayments(pool);
}

const Z_TENDER_ORDER = ['Cash', 'Card', 'QR', 'Bank', 'Online', 'Other'];

/** Package list price for Z-report credit (falls back to collected amount). */
/** Full membership / sale amount (package + registration + PT + classes − discount). */
function packageCreditAmount(row) {
  const packagePrice = Number(
    row.packagePrice ?? row.packageFee ?? row.package_price ?? row.fee,
  ) || 0;
  const registration = Number(row.registrationFee ?? row.registration_fee) || 0;
  const pt = Number(row.personalTrainingFee ?? row.personal_training_fee) || 0;
  const classes = Number(row.classesFee ?? row.classes_fee) || 0;
  const discount = Number(row.discountAmount ?? row.discount_amount) || 0;
  const totalDue = Math.max(0, packagePrice - discount + registration + pt + classes);
  if (totalDue > 0) return totalDue;
  // Fall back to money collected (splits / header)
  const collected = Number(row.amount) || Number(row.totalPaid) || 0;
  if (collected > 0) return collected;
  const fromSplits = parsePaymentSplitsFromRow(row)
    .reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  return fromSplits > 0 ? fromSplits : 0;
}

function paymentPendingAmount(row) {
  const credit = packageCreditAmount(row);
  const collected = Number(row.amount) || 0;
  const explicit = Number(row.pendingAmount ?? row.pending_amount);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  return Math.max(0, credit - collected);
}

/** Split a payment into cash vs bank (non-cash) collected amounts. */
function paymentCashBankAmounts(payment) {
  let cash = 0;
  let bank = 0;
  ledgerCollectionSplitLines(payment).forEach((split) => {
    const amount = Number(split.amount) || 0;
    if (amount <= 0) return;
    if (normalizeTenderMethod(split.method || payment.method || payment.payment_method || 'cash') === 'Cash') {
      cash += amount;
    } else {
      bank += amount;
    }
  });
  return { cash, bank };
}

/**
 * Calendar day for an expense (report date).
 * Prefer expense_date / entry_date / date — never createdAt (UTC day shift).
 */
export function expenseCalendarDay(expense) {
  if (!expense) return '';
  const raw = expense.date
    || expense.expense_date
    || expense.entry_date
    || expense.paidOn
    || expense.paid_on
    || '';
  const text = String(raw).trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  return '';
}

function isCashInHandExpenseRow(expense) {
  const code = String(expense?.accountCode || expense?.code || '').toUpperCase();
  return code === 'CIH' || code.startsWith('CIH-') || code === 'CIH-CASH';
}

function isCoaGymExpenseAccount(expense) {
  const parent = String(expense?.parentCode || '').trim().toUpperCase();
  const code = String(expense?.accountCode || expense?.code || '').trim().toUpperCase();
  const hit = (value) => (
    value === 'ADM' || value.startsWith('ADM-')
    || value === 'SEL' || value.startsWith('SEL-')
    || value === 'OEXP' || value.startsWith('OEXP-')
  );
  return hit(parent) || hit(code);
}

/** Long Term / Short Term loan CoA accounts (PEARL GROUP, Xinhua, etc.). */
export function isCoaLoanAccount(expense) {
  const parent = String(expense?.parentCode || '').trim().toUpperCase();
  const code = String(expense?.accountCode || expense?.code || '').trim().toUpperCase();
  const hit = (value) => (
    value === 'LTL' || value.startsWith('LTL-')
    || value === 'STL' || value.startsWith('STL-')
  );
  return hit(parent) || hit(code);
}

/**
 * Map an expense to cash vs bank for Z Report.
 * Order: payment method / Type → voucher ref (CPV/BPV) → default cash.
 * Do not guess from category (salary/rent) — that misfiled cash expenses as bank.
 */
function expenseCashBankBucket(expense) {
  const method = expense.method || expense.paymentMethod || expense.type || expense.tender || expense.paidVia;
  if (method != null && String(method).trim() !== '') {
    const raw = String(method).trim().toLowerCase();
    if (raw && raw !== 'expense' && raw !== '—' && raw !== '-') {
      if (
        raw === 'bank' || raw === 'card' || raw === 'qr' || raw === 'online'
        || raw === 'transfer' || raw === 'bank transfer' || raw === 'credit card'
      ) {
        return 'bank';
      }
      if (raw === 'cash' || raw === 'counter') return 'cash';
      return normalizeTenderMethod(method) === 'Cash' ? 'cash' : 'bank';
    }
  }
  const ref = String(expense.ref || '').trim().toUpperCase();
  if (/^BPV\b|^BPV[-_]|\bBPV\d/.test(ref) || /^BRV\b|^BRV[-_]|\bBRV\d/.test(ref)) return 'bank';
  if (/^CPV\b|^CPV[-_]|\bCPV\d/.test(ref) || /^CRV\b|^CRV[-_]|\bCRV\d/.test(ref)) return 'cash';
  return 'cash';
}

function expenseZReportDedupeKey(expense) {
  const id = String(expense?.id || '').replace(/-(dr|cr)$/i, '').trim();
  if (id && id !== 'undefined' && id !== 'null') return `id:${id}`;
  const day = expenseCalendarDay(expense);
  const code = String(expense?.accountCode || expense?.code || '').toUpperCase();
  const amount = Number(expense?.amount) || Number(expense?.debit) || 0;
  const desc = String(expense?.description || expense?.accountTitle || '').trim().toLowerCase();
  const ref = String(expense?.ref || '').trim().toUpperCase();
  return `row:${day}|${code}|${amount}|${desc}|${ref}`;
}

/**
 * Z Report EXPENSES only:
 * 1) report date  2) cash/bank tender  3) real gym expense (not CIH / opening / receipts)
 * CoA: ADM / SEL / OEXP (gym) + LTL / STL (loans → PEARL GROUP auto). Manual: paid outflows.
 */
export function countsTowardZReportExpense(expense, day) {
  if (!expense) return false;
  const target = String(day || '').slice(0, 10);
  if (!target) return false;

  const rowDay = expenseCalendarDay(expense);
  if (!rowDay || rowDay !== target) return false;

  if (isOpeningBalanceLedgerEntry(expense)) return false;
  if (isCashInHandExpenseRow(expense)) return false;
  if (isPettyCashCreditReceipt(expense)) return false;

  const amount = Number(expense.amount) || Number(expense.debit) || 0;
  if (amount <= 0) return false;

  const side = String(expense.side || '').toLowerCase().trim();
  const credit = Number(expense.credit) || 0;
  const debit = Number(expense.debit) || 0;
  // Credit-only lines are not expense outflows
  if (side === 'credit' && debit <= 0) return false;
  if (credit > 0 && debit <= 0 && side !== 'debit') return false;

  if (!isPaidExpense(expense) && String(expense.status || 'paid').toLowerCase() !== 'paid') {
    return false;
  }

  const source = String(expense.source || '').toLowerCase();
  if (source === 'coa_ledger' || source === 'coa') {
    // Gym expenses (ADM/SEL/OEXP) + loan outflows (LTL/STL) — never CIH / ORCV from petty merge
    return isCoaGymExpenseAccount(expense) || isCoaLoanAccount(expense);
  }
  return true;
}

/** Sum Z Report cash / bank gym expenses only (ADM / SEL / OEXP) — excludes loans. */
export function sumZReportExpensesByTender(expenses = [], day) {
  const target = String(day || '').slice(0, 10);
  let cash = 0;
  let bank = 0;
  const seen = new Set();
  (expenses || []).forEach((expense) => {
    if (!countsTowardZReportExpense(expense, target)) return;
    const source = String(expense.source || '').toLowerCase();
    if ((source === 'coa_ledger' || source === 'coa') && isCoaLoanAccount(expense)) return;
    const key = expenseZReportDedupeKey(expense);
    if (seen.has(key)) return;
    seen.add(key);
    const amount = Number(expense.amount) || Number(expense.debit) || 0;
    if (expenseCashBankBucket(expense) === 'bank') bank += amount;
    else cash += amount;
  });
  return { cash, bank, total: cash + bank };
}

/** Inclusive date-range version of sumZReportExpensesByTender. */
export function sumZReportExpensesByTenderRange(expenses = [], dateFrom, dateTo) {
  const start = String(dateFrom || '').slice(0, 10);
  const end = String(dateTo || dateFrom || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    return { cash: 0, bank: 0, total: 0 };
  }
  if (start === end) return sumZReportExpensesByTender(expenses, start);
  let cash = 0;
  let bank = 0;
  const seen = new Set();
  (expenses || []).forEach((expense) => {
    const rowDay = expenseCalendarDay(expense);
    if (!rowDay || rowDay < start || rowDay > end) return;
    if (!countsTowardZReportExpense(expense, rowDay)) return;
    const source = String(expense.source || '').toLowerCase();
    if ((source === 'coa_ledger' || source === 'coa') && isCoaLoanAccount(expense)) return;
    const key = expenseZReportDedupeKey(expense);
    if (seen.has(key)) return;
    seen.add(key);
    const amount = Number(expense.amount) || Number(expense.debit) || 0;
    if (expenseCashBankBucket(expense) === 'bank') bank += amount;
    else cash += amount;
  });
  return { cash, bank, total: cash + bank };
}

/** Sum Long Term / Short Term loan outflows for Daily Z PEARL GROUP auto-fill. */
export function sumZReportLoanExpensesByTender(expenses = [], day) {
  const target = String(day || '').slice(0, 10);
  let cash = 0;
  let bank = 0;
  const seen = new Set();
  (expenses || []).forEach((expense) => {
    if (!countsTowardZReportExpense(expense, target)) return;
    if (!isCoaLoanAccount(expense)) return;
    const key = expenseZReportDedupeKey(expense);
    if (seen.has(key)) return;
    seen.add(key);
    const amount = Number(expense.amount) || Number(expense.debit) || 0;
    if (expenseCashBankBucket(expense) === 'bank') bank += amount;
    else cash += amount;
  });
  return { cash, bank, total: cash + bank };
}

/** Inclusive date-range version of sumZReportLoanExpensesByTender. */
export function sumZReportLoanExpensesByTenderRange(expenses = [], dateFrom, dateTo) {
  const start = String(dateFrom || '').slice(0, 10);
  const end = String(dateTo || dateFrom || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    return { cash: 0, bank: 0, total: 0 };
  }
  if (start === end) return sumZReportLoanExpensesByTender(expenses, start);
  let cash = 0;
  let bank = 0;
  const seen = new Set();
  (expenses || []).forEach((expense) => {
    const rowDay = expenseCalendarDay(expense);
    if (!rowDay || rowDay < start || rowDay > end) return;
    if (!countsTowardZReportExpense(expense, rowDay)) return;
    if (!isCoaLoanAccount(expense)) return;
    const key = expenseZReportDedupeKey(expense);
    if (seen.has(key)) return;
    seen.add(key);
    const amount = Number(expense.amount) || Number(expense.debit) || 0;
    if (expenseCashBankBucket(expense) === 'bank') bank += amount;
    else cash += amount;
  });
  return { cash, bank, total: cash + bank };
}

function ledgerRowDate(row) {
  return String(row.date || row.paidOn || row.createdAt || '').slice(0, 10);
}

function applyLedgerInflow(amount, rowDay, day, beforeFn, dayFn) {
  const amt = Number(amount) || 0;
  if (amt <= 0 || !rowDay) return;
  if (rowDay < day) beforeFn(amt);
  else if (rowDay === day) dayFn(amt);
}

function applyLedgerOutflow(amount, rowDay, day, beforeFn, dayFn) {
  applyLedgerInflow(amount, rowDay, day, beforeFn, dayFn);
}

/**
 * Cash / bank drawers for the selected Z-report day only.
 * Opening starts at 0 so prior-day history does not invent balances when
 * there are no payments on the report date.
 */
function buildCashBankSummary({
  day,
  payments = [],
  guestPassIncome = [],
  expenses = [],
  gymId = '',
  cashLedgerRows = null,
  bankLedgerRows = null,
  cardLedgerRows = null,
  qrLedgerRows = null,
  manualExpenses = null,
} = {}) {
  let dayCashIn = 0;
  let dayBankIn = 0;
  let dayCashExpense = 0;
  let dayBankExpense = 0;

  // Prefer the same tender-split rules as PAYMENT METHOD (sales rows for the day).
  // Legacy ledger-row args still supported when callers pass them explicitly.
  if (cashLedgerRows || bankLedgerRows || cardLedgerRows || qrLedgerRows) {
    const isReportDay = (row) => ledgerRowDate(row) === day;
    (cashLedgerRows || []).filter(isReportDay).forEach((row) => {
      dayCashIn += Number(row.payment) || Number(row.debit) || 0;
    });
    [...(bankLedgerRows || []), ...(cardLedgerRows || []), ...(qrLedgerRows || [])]
      .filter(isReportDay)
      .forEach((row) => {
        dayBankIn += Number(row.debit) || Number(row.payment) || 0;
      });
  } else {
    const salePool = [
      ...(payments || []).filter((p) => !isRefund(p) && isCoaCollectiblePayment(p)),
      ...(guestPassIncome || []).filter((g) => (Number(g.amount) || 0) > 0),
    ];
    salePool.forEach((row) => {
      // Membership-day rows keep start_date on `date` — only count tenders on collection day.
      // Skip complimentary / pending-zero placeholders (same as CASH SALE / Petty).
      if (!countsTowardCashSale(row)) return;
      if (!paymentCollectedOnReportDay(row, day)) return;
      const { cash, bank } = paymentCashBankAmounts(row);
      dayCashIn += cash;
      dayBankIn += bank;
    });
  }

  const localExpenses = manualExpenses != null
    ? manualExpenses
    : (gymId ? loadManualExpenses(gymId) : []);
  const expensePool = [
    ...(expenses || []),
    ...localExpenses.map((e) => ({
      id: e.id,
      description: e.description || e.accountTitle || 'Expense',
      accountTitle: e.accountTitle,
      category: e.category || 'Other',
      amount: Number(e.amount) || 0,
      date: e.date,
      status: e.status || 'paid',
      accountCode: e.accountCode || accountForExpense(e),
      method: e.method || e.paymentMethod,
      ref: e.ref,
      side: e.side,
      debit: e.debit,
      credit: e.credit,
      pettyColumn: e.pettyColumn,
      code: e.code,
    })),
  ];

  // Date → cash/bank → Z expense (deduped; no CIH / petty double-count)
  const { cash: cashOut, bank: bankOut } = sumZReportExpensesByTender(expensePool, day);
  dayCashExpense = cashOut;
  dayBankExpense = bankOut;

  const closingCash = dayCashIn - dayCashExpense;
  const closingBank = dayBankIn - dayBankExpense;

  return {
    cash: {
      opening: 0,
      cashIn: dayCashIn,
      expenseCash: dayCashExpense,
      closing: closingCash,
      balance: closingCash,
    },
    bank: {
      opening: 0,
      bankIn: dayBankIn,
      expenseBank: dayBankExpense,
      closing: closingBank,
      balance: closingBank,
    },
  };
}

/**
 * End-of-day Z Report: package sales (credit = package amount),
 * collections by tender (debit = paid), expenses, pending, and cash/bank drawers.
 */
export function buildZReport({
  date,
  dateFrom = '',
  dateTo = '',
  payments = [],
  guestPassIncome = [],
  expenses = [],
  gymId = '',
  manualExpenses = null,
  /** When true, caller already scoped `payments` to the target day/range. */
  paymentsAlreadyScoped = false,
} = {}) {
  const day = String(date || localDateKey()).slice(0, 10);
  const rangeStart = String(dateFrom || '').slice(0, 10);
  const rangeEnd = String(dateTo || dateFrom || day).slice(0, 10);
  const useRange = /^\d{4}-\d{2}-\d{2}$/.test(rangeStart)
    && /^\d{4}-\d{2}-\d{2}$/.test(rangeEnd)
    && rangeStart !== rangeEnd;
  const resolvedGymId = gymId || '';
  const localExpenses = manualExpenses != null
    ? manualExpenses
    : (resolvedGymId ? loadManualExpenses(resolvedGymId) : []);
  const expenseInput = [
    ...(expenses || []),
    ...localExpenses.map((e) => ({
      id: e.id,
      description: e.description || e.accountTitle || 'Expense',
      accountTitle: e.accountTitle,
      category: e.category || 'Other',
      amount: Number(e.amount) || 0,
      date: e.date,
      status: e.status || 'paid',
      accountCode: e.accountCode,
      method: e.method || e.paymentMethod,
    })),
  ];

  const seenPaymentIds = new Set();
  const dayPayments = (payments || []).filter((p) => {
    if (isRefund(p)) return false;
    const headerAmt = Number(p.amount) || Number(p.totalPaid) || 0;
    const splitAmt = parsePaymentSplitsFromRow(p)
      .reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    const amount = headerAmt > 0 ? headerAmt : splitAmt;
    const credit = packageCreditAmount(p);
    const membershipDayRow = Boolean(p.fromMembershipDay);
    const complimentary = Boolean(p.complimentary)
      || String(p.status || '').toLowerCase() === 'complimentary';
    // Include completed memberships for the day even when fee columns are blank
    if (amount <= 0 && credit <= 0 && !membershipDayRow && !complimentary) return false;
    // Include completed and pending / partial sales for the day
    if (!isCompleted(p) && !isPending(p) && !complimentary && !membershipDayRow) {
      const status = String(p.status || '').toLowerCase().trim();
      if (status && status !== 'partial') return false;
    }
    if (!paymentsAlreadyScoped) {
      if (useRange) {
        if (!paymentMatchesReportRange(p, rangeStart, rangeEnd) && !membershipDayRow) return false;
        if (membershipDayRow) {
          const rowDay = membershipStartCalendarDay(p)
            || paymentDate(p)
            || String(p.date || p.startDate || '').slice(0, 10);
          if (rowDay && (rowDay < rangeStart || rowDay > rangeEnd)) return false;
        }
      } else {
        if (!paymentMatchesReportDay(p, day) && !membershipDayRow) return false;
        // Membership-day rows are already scoped to the report date by the loader
        if (membershipDayRow) {
          const rowDay = membershipStartCalendarDay(p)
            || paymentDate(p)
            || String(p.date || p.startDate || '').slice(0, 10);
          if (rowDay && rowDay !== day) return false;
        }
      }
    }
    // Do not drop orphan subscription payments when start_date ≠ paid_at —
    // they feed CASH SALE on the collection day (NEW MEMBERSHIP uses fromMembershipDay).
    const stableId = String(p.rawId || p.id || '').trim();
    if (stableId) {
      if (seenPaymentIds.has(stableId)) return false;
      seenPaymentIds.add(stableId);
      return true;
    }
    const key = `${p.member}-${amount}-${paymentDate(p)}-${p.method}-${p.source || ''}`;
    if (seenPaymentIds.has(key)) return false;
    seenPaymentIds.add(key);
    return true;
  });

  const seenExpenseIds = new Set();
  const dayExpenses = expenseInput.filter((e) => {
    // Same date → cash/bank rules as Z Report CASH/BANK EXPENSES
    if (!countsTowardZReportExpense(e, day)) return false;
    const key = expenseZReportDedupeKey(e);
    if (seenExpenseIds.has(key)) return false;
    seenExpenseIds.add(key);
    return true;
  });

  // Day Pass / guest sales come ONLY from payments with source guest_pass
  // (same filter as Guest Pass page). Orphan guest_passes are excluded.
  // Membership-day rows only feed New Memberships — never Day Pass / Renewal.
  const salesRows = dayPayments
    .filter((row) => {
      if (!row?.fromMembershipDay) return true;
      const src = String(row.source || '').toLowerCase().replace(/-/g, '_');
      return src === 'subscription'
        || src === 'membership'
        || src === 'new_membership'
        || src === 'subscriptions'
        || !src;
    })
    .map((row) => ({
      ...row,
      source: normalizeZPaymentSource(row),
    }));

  const packageMap = new Map();
  salesRows.forEach((row) => {
    const name = row.package || row.type || 'Other';
    const collected = Number(row.amount) || 0;
    const credit = packageCreditAmount(row);
    const existing = packageMap.get(name) || {
      package: name,
      count: 0,
      credit: 0,
      debit: 0,
      byMethod: { Cash: 0, Card: 0, QR: 0, Bank: 0, Online: 0, Other: 0 },
    };
    existing.count += 1;
    existing.credit += credit;
    existing.debit += collected;
    const tender = normalizeTenderMethod(row.method);
    existing.byMethod[tender] = (existing.byMethod[tender] || 0) + collected;
    packageMap.set(name, existing);
  });

  const packageSales = [...packageMap.values()].sort((a, b) => b.credit - a.credit);
  const totalSalesCredit = packageSales.reduce((t, row) => t + (Number(row.credit) || 0), 0);
  const totalCollectedDebit = packageSales.reduce((t, row) => t + (Number(row.debit) || 0), 0);

  const pendingPaymentRows = salesRows
    .map((row) => {
      const credit = packageCreditAmount(row);
      const collected = Number(row.amount) || 0;
      const pending = paymentPendingAmount(row);
      return {
        id: String(row.rawId || row.id || `${row.member}-${row.package}-${row.date}`),
        member: row.member || '—',
        gymId: row.gymId || '—',
        package: row.package || row.type || 'Other',
        credit,
        collected,
        pending,
        method: normalizeTenderMethod(row.method),
      };
    })
    .filter((row) => row.pending > 0.009)
    .sort((a, b) => b.pending - a.pending);

  const paymentPending = pendingPaymentRows.reduce((t, row) => t + row.pending, 0);

  // Detailed Report first — Z PAYMENT METHOD copies these Mode of Payment totals.
  // For date ranges, omit a single reportDay so sale-day amounts (with later
  // remaining stripped) are used for every row in the window.
  const saleReport = buildZSaleReport(salesRows, useRange ? '' : day);
  const modeTotals = sumDetailedReportModeOfPayment(saleReport.saleReportSections);
  const collectionsByMethod = Z_TENDER_ORDER.map((method) => {
    if (method === 'Cash') {
      return { method, amount: modeTotals.cash.amount, count: modeTotals.cash.count };
    }
    if (method === 'Card') {
      return { method, amount: modeTotals.card.amount, count: modeTotals.card.count };
    }
    if (method === 'QR') {
      return { method, amount: modeTotals.qr.amount, count: modeTotals.qr.count };
    }
    if (method === 'Bank') {
      return { method, amount: modeTotals.bank.amount, count: modeTotals.bank.count };
    }
    return { method, amount: 0, count: 0 };
  }).filter((row) => (
    row.amount > 0
    || row.method === 'Cash'
    || row.method === 'Card'
    || row.method === 'QR'
    || row.method === 'Bank'
  ));

  const expenseTotal = sum(dayExpenses);
  const expenseByCategoryMap = new Map();
  dayExpenses.forEach((e) => {
    const cat = e.category || 'Other';
    expenseByCategoryMap.set(cat, (expenseByCategoryMap.get(cat) || 0) + (Number(e.amount) || 0));
  });
  const expensesByCategory = [...expenseByCategoryMap.entries()]
    .map(([category, amount]) => ({ category, amount }))
    .sort((a, b) => b.amount - a.amount);

  const netDayResult = totalSalesCredit - expenseTotal;
  // Drawers must use day-only sales (not full ledger history / undated→today rows)
  const { cash: cashSummary, bank: bankSummary } = buildCashBankSummary({
    day,
    payments: dayPayments,
    // Guest pass cash is already in dayPayments — do not add orphan guest_passes
    guestPassIncome: [],
    expenses: dayExpenses,
    gymId: resolvedGymId,
    cashLedgerRows: null,
    bankLedgerRows: null,
    cardLedgerRows: null,
    qrLedgerRows: null,
    manualExpenses: [],
  });

  return {
    date: day,
    totalSalesCredit,
    totalCollectedDebit,
    paymentPending,
    pendingPaymentRows,
    packageSales,
    collectionsByMethod,
    expenses: dayExpenses,
    expensesByCategory,
    expenseTotal,
    saleCount: salesRows.length,
    expenseCount: dayExpenses.length,
    netDayResult,
    salesRows,
    cashSummary,
    bankSummary,
    ...saleReport,
  };
}

/**
 * AUTO values for the spreadsheet-style Daily Z-Report
 * (Cash/Bank sale+expenses, Sale Summary counts, Payment Method counts).
 */
export function buildDailyZSheetAuto({
  date,
  dateFrom = '',
  dateTo = '',
  payments = [],
  guestPassIncome = [],
  expenses = [],
  gymId = '',
  paymentsAlreadyScoped = false,
} = {}) {
  const z = buildZReport({
    date,
    dateFrom,
    dateTo,
    payments,
    guestPassIncome,
    expenses,
    gymId,
    manualExpenses: [],
    paymentsAlreadyScoped,
  });
  const day = z.date;
  const salesRows = z.salesRows || [];

  // One row per receptionist sale *page* (payment.source) — never group by item name.
  const bucketDefs = [
    { id: 'new_membership', label: 'NEW MEMBERSHIP', sources: ['subscription'], note: 'STANDARD+OFF PEAK+LADIES ONLY' },
    { id: 'renewal', label: 'RENEWAL MEMBERSHIP', sources: ['renewal_sale'] },
    { id: 'pt_sale', label: 'PT SALE', sources: ['pt_sale'] },
    { id: 'guest_pass', label: 'GUEST PASS', sources: ['guest_pass'] },
    { id: 'locker_sale', label: 'LOCKER SALE', sources: ['locker_sale'] },
    { id: 'spa_sale', label: 'SPA SALE', sources: ['spa_sale'] },
    { id: 'oxybar', label: 'OXYBAR SALE', sources: ['oxybar_sale'] },
    { id: 'zumba_sale', label: 'ZUMBA SALE', sources: ['zumba_sale'] },
    { id: 'yoga_sale', label: 'YOGA SALE', sources: ['yoga_sale'] },
    { id: 'gym_apparel', label: 'GYM APPAREL', sources: ['gym_apparel'] },
    { id: 'miscellaneous_sale', label: 'MISCELLANEOUS SALE', sources: ['miscellaneous_sale'] },
    { id: 'remaining_clear', label: 'REMAINING CLEAR', sources: ['remaining_clear'], match: /remaining|balance\s*clear|pay\s*balance|clear\s*balance/i },
  ];

  const pageSources = new Set(
    bucketDefs.flatMap((def) => def.sources).filter((s) => s !== 'remaining_clear'),
  );

  const saleSummary = bucketDefs.map((def) => {
    const rows = salesRows.filter((row) => {
      // Use the already-normalized page source on salesRows (from payment.source)
      const source = String(row.source || normalizeZPaymentSource(row)).trim().toLowerCase();
      if (def.id === 'new_membership') {
        // Match Detailed Report / Memberships list (start_date day only)
        return isNewMembershipReportRow(row);
      }
      if (def.sources.includes(source)) return true;
      if (def.id === 'remaining_clear') {
        if (source === 'remaining_clear') return true;
        // Legacy notes only when source is not another sale page
        if (pageSources.has(source)) return false;
        const hay = `${row.package || ''} ${row.paymentNote || ''} ${row.type || ''}`;
        return def.match.test(hay);
      }
      return false;
    });

    const count = rows.length;
    const value = rows.reduce((sum, row) => {
      const amt = def.id === 'remaining_clear'
        ? remainingClearPaidAmount(row)
        : saleRowOriginalPaid(row);
      return sum + amt;
    }, 0);
    return {
      id: def.id,
      label: def.label,
      note: def.note || '',
      count,
      value,
    };
  });

  const totalSaleCount = saleSummary.reduce((s, r) => s + r.count, 0);
  const totalSaleValue = saleSummary.reduce((s, r) => s + r.value, 0);

  // Z PAYMENT METHOD = Detailed Report Mode of Payment sums (cash/card/bank/qr)
  const fromDetail = (id) => {
    const row = (z.saleReportMethodTotals || []).find((m) => m.id === id);
    return {
      amount: Number(row?.amount) || 0,
      count: Number(row?.count) || 0,
    };
  };
  const paymentMethods = [
    { id: 'cash', label: 'CASH', tender: 'Cash', ...fromDetail('cash') },
    { id: 'card', label: 'CREDIT CARD', tender: 'Card', ...fromDetail('card') },
    { id: 'bank', label: 'BANK TRANSFER', tender: 'Bank', ...fromDetail('bank') },
    { id: 'qr', label: 'QR', tender: 'QR', ...fromDetail('qr') },
  ];

  const totalReceivedCount = paymentMethods.reduce((s, r) => s + r.count, 0);
  const totalReceivedValue = paymentMethods.reduce((s, r) => s + r.amount, 0);

  // CASH SUMMARY CASH SALE = Detailed Report Mode of Payment cash total
  const cashSale = Number(paymentMethods.find((m) => m.id === 'cash')?.amount) || 0;
  // Non-cash tenders for bank drawer / sale method (card + bank + qr)
  const bankSaleAuto = (
    (Number(paymentMethods.find((m) => m.id === 'card')?.amount) || 0)
    + (Number(paymentMethods.find((m) => m.id === 'bank')?.amount) || 0)
    + (Number(paymentMethods.find((m) => m.id === 'qr')?.amount) || 0)
  );
  // Date → cash/bank from the loaded expense pool (same rules as cashSummary)
  const rangeStart = String(dateFrom || '').slice(0, 10);
  const rangeEnd = String(dateTo || dateFrom || day).slice(0, 10);
  const useExpenseRange = /^\d{4}-\d{2}-\d{2}$/.test(rangeStart)
    && /^\d{4}-\d{2}-\d{2}$/.test(rangeEnd)
    && rangeStart !== rangeEnd;
  const expenseSplit = useExpenseRange
    ? sumZReportExpensesByTenderRange(expenses, rangeStart, rangeEnd)
    : sumZReportExpensesByTender(expenses, day);
  const cashExpenses = Number(expenseSplit.cash) || 0;
  const bankExpenses = Number(expenseSplit.bank) || 0;
  // Long Term / Short Term Loans → PEARL GROUP auto (separate from gym expenses)
  const loanSplit = useExpenseRange
    ? sumZReportLoanExpensesByTenderRange(expenses, rangeStart, rangeEnd)
    : sumZReportLoanExpensesByTender(expenses, day);
  const loanCashExpenses = Number(loanSplit.cash) || 0;
  const loanBankExpenses = Number(loanSplit.bank) || 0;

  return {
    date: day,
    cashSale,
    bankSaleAuto,
    cashExpenses,
    bankExpenses,
    loanCashExpenses,
    loanBankExpenses,
    saleSummary,
    totalSaleCount,
    totalSaleValue,
    paymentMethods,
    totalReceivedCount,
    totalReceivedValue,
    paymentPending: Number(z.paymentPending) || 0,
    expenseTotal: Number(z.expenseTotal) || 0,
  };
}

/** Closing cash = opening + received + cash sale − expenses − pearl group */
export function calcZClosingCash({
  openingCash = 0,
  cashReceived = 0,
  cashSale = 0,
  cashExpenses = 0,
  pearlGroupCash = 0,
} = {}) {
  return (Number(openingCash) || 0)
    + (Number(cashReceived) || 0)
    + (Number(cashSale) || 0)
    - (Number(cashExpenses) || 0)
    - (Number(pearlGroupCash) || 0);
}

/** Closing bank = opening + received + bank sale − expenses − pearl group */
export function calcZClosingBank({
  openingBank = 0,
  bankReceived = 0,
  bankSale = 0,
  bankExpenses = 0,
  pearlGroupBank = 0,
} = {}) {
  return (Number(openingBank) || 0)
    + (Number(bankReceived) || 0)
    + (Number(bankSale) || 0)
    - (Number(bankExpenses) || 0)
    - (Number(pearlGroupBank) || 0);
}

/** Sale-report sections — same categories as receptionist Sales pages (real data only). */
export const Z_SALE_REPORT_SECTIONS = [
  { id: 'subscriptions', title: 'New Memberships', sources: ['subscription'], columns: 'standard' },
  { id: 'day_pass', title: 'Day Pass', sources: ['guest_pass'], columns: 'standard' },
  { id: 'renewal', title: 'Renewal Sale', sources: ['renewal_sale'], columns: 'standard' },
  { id: 'pt_sales', title: 'PT Sale', sources: ['pt_sale'], columns: 'standard' },
  { id: 'locker_sale', title: 'Locker Sale', sources: ['locker_sale'], columns: 'standard' },
  { id: 'spa', title: 'Spa Sale', sources: ['spa_sale'], columns: 'spa' },
  { id: 'oxybar', title: 'Oxybar Sale', sources: ['oxybar_sale'], columns: 'oxybar' },
  { id: 'zumba', title: 'Zumba Sale', sources: ['zumba_sale'], columns: 'standard' },
  { id: 'yoga', title: 'Yoga Sale', sources: ['yoga_sale'], columns: 'standard' },
  { id: 'gym_apparel', title: 'Gym Apparel', sources: ['gym_apparel'], columns: 'standard' },
  { id: 'remaining_clear', title: 'Remaining', sources: ['remaining_clear'], columns: 'standard' },
  { id: 'misc_sale', title: 'Miscellaneous Sale', sources: ['miscellaneous_sale'], columns: 'standard' },
];

/** Known payment.source values from receptionist sale pages. */
const KNOWN_Z_SALE_SOURCES = new Set(
  Z_SALE_REPORT_SECTIONS.flatMap((s) => s.sources),
);

/**
 * Map free-text package labels back to a sale source when `source` was lost
 * (blank / unknown). Do NOT apply these when source is already `subscription`.
 */
/** Infer page source only from sale-page titles — not product/item names.
 * Never infer guest_pass / renewal_sale here — Guest Pass & Renewal Sale pages
 * only list rows with those exact payment.source values.
 */
const Z_SALE_LABEL_TO_SOURCE = [
  { source: 'oxybar_sale', match: /oxy\s*bar\s*sale|oxybar\s*sale/i },
  { source: 'spa_sale', match: /\bspa\s*sale\b/i },
  { source: 'zumba_sale', match: /\bzumba\s*sale\b/i },
  { source: 'yoga_sale', match: /\byoga\s*sale\b/i },
  { source: 'locker_sale', match: /\blocker\s*sale\b/i },
  { source: 'pt_sale', match: /\bpt\s*sale\b|personal\s*train(?:ing)?\s*sale/i },
  { source: 'gym_apparel', match: /\bgym\s*apparel\b/i },
  { source: 'remaining_clear', match: /remaining\s*clear|balance\s*payment|pay\s*balance|clear\s*balance/i },
  { source: 'miscellaneous_sale', match: /miscellaneous\s*sale|misc\.?\s*sale/i },
];

/**
 * Normalize payment.source for Z / SALE REPORT grouping.
 * Keeps New Memberships (`subscription`) and Renewal (`renewal_sale`) separate.
 * Explicit DB source always wins — package name must not steal memberships / PT / etc.
 */
export function normalizeZPaymentSource(row) {
  let source = String(row?.source || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');

  if (source === 'new_membership' || source === 'membership' || source === 'subscriptions') {
    source = 'subscription';
  }
  if (source === 'renewal' || source === 'renewals') {
    source = 'renewal_sale';
  }
  if (
    source === 'remaining'
    || source === 'remaining_clear'
    || source === 'balance_clear'
    || source === 'balance_payment'
    || source === 'pay_balance'
  ) {
    source = 'remaining_clear';
  }

  // Trust explicit known sources first — never let package text override
  // (fixes "PT Sale — DAY PASS" landing under Day Pass)
  if (source && KNOWN_Z_SALE_SOURCES.has(source)) {
    return source;
  }

  // Day Pass / Renewal: only explicit payment.source (same as Guest Pass / Renewal Sale pages).
  // Do not infer from package/note text — that pulled PT "DAY PASS" and similar extras in.

  const hay = [
    row?.package,
    row?.package_name,
    row?.type,
    row?.item,
    row?.paymentNote,
    row?.payment_note,
  ].map((v) => String(v || '')).join(' ');

  // Only when source is missing/unknown — and never to guest_pass / renewal_sale
  if (!source && hay.trim()) {
    for (const { source: inferred, match } of Z_SALE_LABEL_TO_SOURCE) {
      if (match.test(hay)) return inferred;
    }
  }

  if (KNOWN_Z_SALE_SOURCES.has(source)) return source;
  // Unknown / blank → Miscellaneous (never default to subscription — that caused
  // paid-on-other-day memberships to spill into Misc on every date).
  return source || 'miscellaneous_sale';
}

function zSaleMembershipCode(row) {
  const source = String(row.source || '').toLowerCase();
  if (source === 'guest_pass') return '';
  const serial = row.serialNo != null && row.serialNo !== ''
    ? String(row.serialNo)
    : String(row.gymId || '').replace(/^id[\s\-:_]*/i, '').trim();
  if (!serial || serial === '—' || serial === '1') {
    // Serial 1 is walk-in — still show code if present
    if (serial === '1') return '1';
    return '';
  }
  return serial;
}

function zSaleMemberName(row) {
  const source = String(row?.source || '').toLowerCase().replace(/-/g, '_');
  if (source === 'guest_pass' || isGuestPassPayment(row)) {
    return guestPassDisplayName(row);
  }
  const name = String(row.member || row.name || '').trim();
  if (!name || name === '—') return 'Walk-In Customer';
  if (/oxy.?gym\s+walk-?in/i.test(name) || /walk[\s-]?in/i.test(name)) return 'Walk-In Customer';
  return name;
}

function zSaleDurationLabel(row) {
  const source = String(row.source || '').toLowerCase().replace(/-/g, '_');
  const fromNote = parseSalePeriodFromNote(row.paymentNote || row.payment_note || '');
  const start = String(
    row.startDate || row.start_date || row.start || fromNote?.startDate || '',
  ).slice(0, 10);
  const end = String(
    row.endDate || row.end_date || row.expiry || row.expiryDate || fromNote?.endDate || '',
  ).slice(0, 10);
  const soldDuration = String(
    row.duration || row.durationLabel || fromNote?.durationLabel || '',
  ).trim();

  // Note / RPC already store human labels ("1 day", "1 month") — show them directly.
  // Guest/PT used to fall through guestPassDurationLabel which only accepts keys like "1d".
  if (soldDuration && soldDuration.toLowerCase() !== 'tbc' && soldDuration !== '—') {
    if (/^\d+(\.\d+)?\s*(day|days|month|months)$/i.test(soldDuration)) {
      return soldDuration;
    }
  }

  // New memberships / renewals: same logic as Memberships table (start→end, sold duration)
  if (source === 'subscription' || source === 'renewal_sale' || source === 'renewal') {
    const membershipLike = membershipDurationDisplay({
      duration: soldDuration,
      start,
      startDate: start,
      expiry: end,
      expiryDate: end,
      datesTbc: row.datesTbc || row.dates_tbc,
      lifetime: row.lifetime,
      package: row.package || row.package_name || '',
    });
    if (membershipLike && membershipLike !== '—') return membershipLike;
  }

  const raw = soldDuration;
  if (raw && raw.toLowerCase() !== 'tbc') {
    if (source === 'spa_sale' || source === 'oxybar_sale') {
      const spaLabel = formatDurationLabel(raw, SPA_OXYBAR_DURATION_OPTIONS);
      if (spaLabel && spaLabel !== '—') return spaLabel;
    }
    if (source === 'guest_pass') {
      const guestLabel = guestPassDurationLabel({ ...row, duration: raw });
      if (guestLabel && guestLabel !== '—') return guestLabel;
    }
    if (/^\d+(\.\d+)?$/.test(raw)) {
      const monthsLabel = subscriptionDurationLabel(Number(raw));
      if (monthsLabel) return monthsLabel;
    }
    const fromSale = formatDurationLabel(raw, SALE_DURATION_OPTIONS);
    if (fromSale && fromSale !== '—') return fromSale;
    const fromSpa = formatDurationLabel(raw, SPA_OXYBAR_DURATION_OPTIONS);
    if (fromSpa && fromSpa !== '—') return fromSpa;
    const fromSub = formatDurationLabel(Number(raw), SUBSCRIPTION_DURATION_OPTIONS);
    if (fromSub && fromSub !== '—') return fromSub;
    const fromGuest = formatDurationLabel(raw, GUEST_PASS_DURATION_OPTIONS);
    if (fromGuest && fromGuest !== '—') return fromGuest;
    return raw;
  }
  if (source === 'guest_pass') {
    const guestLabel = guestPassDurationLabel(row);
    if (guestLabel && guestLabel !== '—') return guestLabel;
  }
  const fromPackage = durationFromSalePackage(
    row.package || row.package_name || row.type || row.passType || row.item || '',
  );
  if (fromPackage) return fromPackage;
  if (fromNote?.durationLabel) return fromNote.durationLabel;

  // Fallback: derive from start/end for any sale type
  const fromDates = membershipDurationDisplay({
    start,
    startDate: start,
    expiry: end,
    expiryDate: end,
    datesTbc: row.datesTbc || row.dates_tbc,
    lifetime: row.lifetime,
  });
  if (fromDates && fromDates !== '—') return fromDates;

  // Day Pass default when note/dates were never stored (legacy imports)
  if (source === 'guest_pass') return '1 day';

  return '—';
}

/** Package / item name only — duration is shown in its own column. */
function zSalePackageLabel(row) {
  const source = String(row.source || '').toLowerCase().replace(/-/g, '_');
  // PT / Zumba / Yoga store trainer in package_name — Package column shows sale type
  if (source === 'pt_sale') return 'PT Sale';
  if (source === 'zumba_sale') return 'Zumba Sale';
  if (source === 'yoga_sale') return 'Yoga Sale';
  if (source === 'remaining_clear') {
    const rawPkg = String(row.package || row.type || row.item || '').trim();
    const cleaned = cleanSalePackageName(rawPkg, '').replace(/^Remaining\s*[—\-]\s*/i, '').trim();
    return cleaned ? `Remaining — ${cleaned}` : 'Remaining Clear';
  }

  const rawPkg = String(row.package || row.type || row.passType || row.item || '').trim();
  const cleaned = cleanSalePackageName(rawPkg, '');
  if (cleaned) {
    // Drop sale-type prefix when present ("Locker Sale — Medium")
    const stripped = cleaned.replace(
      /^(PT Sale|Zumba Sale|Yoga Sale|Spa Sale|Locker Sale|Oxybar Sale|Gym Apparel|Miscellaneous Sale|Renewal Sale)\s*[—\-]\s*/i,
      '',
    ).trim();
    return stripped || cleaned;
  }
  if (source === 'guest_pass') {
    return 'Guest Pass';
  }
  return rawPkg || '—';
}

function zSaleModeLabel(method) {
  const tender = normalizeTenderMethod(method);
  if (tender === 'Card') return 'credit card';
  if (tender === 'Other' && method) return String(method).toLowerCase();
  return String(tender || 'cash').toLowerCase();
}

function zSaleModeOfPayment(row) {
  if (
    row.complimentary
    || String(row.status || '').toLowerCase() === 'complimentary'
    || (Number(row.amount) === 0 && Number(row.packagePrice || row.packageFee || 0) === 0)
  ) {
    return 'complementry';
  }
  const splits = parsePaymentSplitsFromRow(row).filter((s) => Number(s.amount) > 0);
  if (splits.length === 1) {
    return zSaleModeLabel(splits[0].method || row.method);
  }
  if (splits.length > 1) {
    // Callers should expand via mapZSaleDetailRows — fallback combined label
    return splits.map((s) => zSaleModeLabel(s.method)).join(' + ');
  }
  return zSaleModeLabel(row.method || 'cash');
}

function zSaleRecNo(row) {
  const isUuid = (s) => /^[0-9a-f-]{36}$/i.test(String(s || '').trim());
  const candidates = [
    row.invoiceId,
    row.invoice_id,
    row.paymentRef,
    row.payment_ref,
    row.paymentId,
    row.id,
    row.rawId,
  ].map((v) => String(v || '').trim()).filter(Boolean);
  for (const raw of candidates) {
    if (isUuid(raw)) continue;
    const cleaned = raw.replace(/^(INV|PAY|REC)[\s\-:_]*/i, '').trim();
    if (cleaned && /^\d+$/.test(cleaned)) return cleaned;
    if (cleaned && !isUuid(cleaned)) return cleaned;
  }
  return '';
}

/** Numeric key for ascending Rec NO. sort (blank / non-numeric last). */
function zSaleRecNoSortKey(recNo) {
  const raw = String(recNo || '—').replace(/^(INV|PAY|REC)[\s\-:_]*/i, '').trim();
  if (!raw || raw === '—') return Number.MAX_SAFE_INTEGER;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : Number.MAX_SAFE_INTEGER;
}

function sortZSaleDetailRowsByRecNo(rows = []) {
  return [...rows].sort((a, b) => {
    const diff = zSaleRecNoSortKey(a.recNo) - zSaleRecNoSortKey(b.recNo);
    if (diff !== 0) return diff;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

function zSaleTrainerNote(row) {
  const source = String(row.source || '').toLowerCase().replace(/-/g, '_');
  if (source === 'locker_sale') {
    return String(row.package || row.paymentNote || '').trim() || '';
  }
  // PT / Zumba / Yoga: trainer name lives in package_name (same payments column)
  if (source === 'pt_sale' || source === 'zumba_sale' || source === 'yoga_sale') {
    const raw = String(row.package || row.item || row.type || '').trim();
    const cleaned = cleanSalePackageName(raw, '');
    const trainer = cleaned.replace(
      /^(PT Sale|Zumba Sale|Yoga Sale)\s*[—\-]\s*/i,
      '',
    ).trim();
    return trainer || cleaned || '';
  }
  if (source === 'spa_sale') {
    const note = String(row.trainer || row.paymentNote || '').trim();
    if (note && note.length < 40) return note;
    return String(row.trainer || '').trim() || '';
  }
  return String(row.trainer || '').trim() || '';
}

function mapZSaleDetailRowBase(row, index) {
  return {
    id: String(row.rawId || row.id || `${row.member}-${index}`),
    membershipCode: zSaleMembershipCode(row),
    srNo: index + 1,
    name: zSaleMemberName(row),
    package: zSalePackageLabel(row),
    duration: zSaleDurationLabel(row),
    recNo: zSaleRecNo(row) || '—',
    trainer: zSaleTrainerNote(row),
    source: row.source || '',
    paymentSplits: row.paymentSplits || row.payment_splits,
    payment_splits: row.payment_splits || row.paymentSplits,
    method: row.method || row.payment_method,
  };
}

/** Actual tender label(s) from splits / method — never status words like "pending". */
function zSalePaidModeOfPayment(row) {
  const splits = parsePaymentSplitsFromRow(row).filter((s) => Number(s.amount) > 0);
  if (splits.length === 1) {
    return zSaleModeLabel(splits[0].method || row.method);
  }
  if (splits.length > 1) {
    return splits.map((s) => zSaleModeLabel(s.method)).join(' + ');
  }
  const method = String(row.method || row.payment_method || '').trim();
  if (method && method !== '—' && method !== '-') {
    return zSaleModeLabel(method);
  }
  return '';
}

/** True when the row actually took money (splits or paid amount) — not merely a default method label. */
function zSaleHasCollectedMoney(row) {
  const collected = Number(row?.amount) || Number(row?.totalPaid) || 0;
  if (collected > 0) return true;
  return parsePaymentSplitsFromRow(row).some((s) => (Number(s.amount) || 0) > 0);
}

/**
 * One sale row → one or more report lines.
 * Split payments (cash + card, etc.) become separate lines with the same
 * member / package / rec no, each showing its own method and amount.
 *
 * Mode of Payment always shows the real tender (cash / credit card / bank / qr)
 * when money was collected — "pending" only if unpaid.
 * Z Report PAYMENT METHOD sums these Mode of Payment amounts.
 */
function remainingClearPaidAmount(row) {
  const header = Number(row.amount) || Number(row.totalPaid) || 0;
  const splitSum = parsePaymentSplitsFromRow(row)
    .reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  if (header > 0) return header;
  return splitSum > 0 ? splitSum : 0;
}

/** Installments recorded on the original sale note after a remaining pay. */
function remainingInstallmentsFromNote(row) {
  const note = String(row.paymentNote || row.payment_note || '');
  const re = /Balance payment on (\d{4}-\d{2}-\d{2})(?::[^·]*)?\s*·\s*([\d.]+)/gi;
  const items = [];
  let match;
  while ((match = re.exec(note))) {
    items.push({
      day: match[1],
      amount: Number(match[2]) || 0,
    });
  }
  return items;
}

/**
 * Tender lines for cash / method ledgers on this payment's own collection day.
 * When remaining was later merged into the original sale (`amount` + splits),
 * strip that later cash so sale-day keeps the original paid (e.g. 20k) and the
 * separate remaining_clear row owns the clear-day amount (e.g. 2.5k).
 */
function ledgerCollectionSplitLines(payment) {
  const splits = parsePaymentSplitsFromRow(payment).filter((s) => (Number(s.amount) || 0) > 0);
  const headerAmt = Number(payment?.amount) || Number(payment?.totalPaid) || 0;
  const lines = splits.length
    ? splits.map((s) => ({
      method: s.method || payment.method || payment.payment_method || 'cash',
      amount: Number(s.amount) || 0,
    }))
    : (headerAmt > 0
      ? [{
        method: payment.method || payment.payment_method || 'cash',
        amount: headerAmt,
      }]
      : []);

  if (!lines.length) return lines;
  if (isRemainingClearPayment(payment)) return lines;

  const laterRemaining = remainingInstallmentsFromNote(payment)
    .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  if (!(laterRemaining > 0)) return lines;

  const splitSum = lines.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  // Prefer splits when header was already reduced to sale-day original but
  // splits still hold the merged total (header + later remaining).
  let mergedTotal = headerAmt > 0 ? headerAmt : splitSum;
  if (
    splitSum > 0
    && headerAmt > 0
    && splitSum > headerAmt + 0.01
    && Math.abs(splitSum - (headerAmt + laterRemaining)) < 0.02
  ) {
    mergedTotal = splitSum;
  } else if (!(headerAmt > 0) && splitSum > 0) {
    mergedTotal = splitSum;
  }
  const original = Math.max(0, Math.round((mergedTotal - laterRemaining) * 100) / 100);
  if (!(original > 0)) return [];
  if (!(splitSum > 0)) {
    return [{
      method: payment.method || payment.payment_method || 'cash',
      amount: original,
    }];
  }
  if (Math.abs(splitSum - original) < 0.01) return lines;

  // Remaining was merged as extra split line(s). Subtract that amount from the
  // matching tender — do not scale every method (that leaked cash onto a card sale).
  return subtractRemainingFromSplitLines(lines, laterRemaining);
}

/**
 * Strip later remaining-clear cash from merged splits.
 * Example: sale 15,000 card + remaining 5,000 cash merged → keep 15,000 card only.
 */
function subtractRemainingFromSplitLines(lines, laterRemaining) {
  const due = Math.round((Number(laterRemaining) || 0) * 100) / 100;
  if (!(due > 0) || !Array.isArray(lines) || !lines.length) return lines || [];
  const next = lines.map((s) => ({
    method: s.method,
    amount: Math.round((Number(s.amount) || 0) * 100) / 100,
  }));
  let left = due;

  const exactIdx = next.findIndex((s) => s.amount > 0 && Math.abs(s.amount - left) < 0.02);
  if (exactIdx >= 0) {
    next[exactIdx].amount = 0;
    return next.filter((s) => s.amount > 0.005);
  }

  const order = [...next.keys()].sort((a, b) => {
    const aCash = /cash/i.test(String(next[a].method || '')) ? 0 : 1;
    const bCash = /cash/i.test(String(next[b].method || '')) ? 0 : 1;
    return aCash - bCash;
  });
  for (const i of order) {
    if (left <= 0) break;
    const take = Math.min(next[i].amount, left);
    next[i].amount = Math.round((next[i].amount - take) * 100) / 100;
    left = Math.round((left - take) * 100) / 100;
  }
  return next.filter((s) => s.amount > 0.005);
}

function mapZSaleDetailRows(row, startIndex = 0, day = '', { expandSplits = true } = {}) {
  const base = mapZSaleDetailRowBase(row, startIndex);
  const collected = Number(row.amount) || Number(row.totalPaid) || 0;
  const credit = packageCreditAmount(row);
  const target = String(day || '').slice(0, 10);
  const splits = parsePaymentSplitsFromRow(row).filter((s) => Number(s.amount) > 0);
  const paidMode = zSalePaidModeOfPayment(row);
  const hasMoney = zSaleHasCollectedMoney(row);
  const onCollectionDay = !target || paymentCollectedOnReportDay(row, target);
  const remainingSource = normalizeZPaymentSource(row) === 'remaining_clear';

  if (remainingSource) {
    const remainingPaid = remainingClearPaidAmount(row);
    const splitSum = splits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    if (expandSplits && splits.length > 1 && remainingPaid > 0 && Math.abs(splitSum - remainingPaid) < 0.01) {
      return splits.map((split, i) => ({
        ...base,
        id: `${base.id}-split-${i}`,
        srNo: startIndex + i + 1,
        amount: Number(split.amount) || 0,
        modeOfPayment: zSaleModeLabel(split.method || row.method),
        paymentSplits: [split],
        payment_splits: [split],
      }));
    }
    return [{
      ...base,
      amount: remainingPaid,
      modeOfPayment: paidMode || zSaleModeOfPayment(row) || 'cash',
    }];
  }

  const laterRemaining = remainingInstallmentsFromNote(row)
    .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const originalCollected = Math.max(0, collected - laterRemaining);

  if (
    row.complimentary
    || String(row.status || '').toLowerCase() === 'complimentary'
  ) {
    return [{
      ...base,
      amount: credit > 0 ? credit : collected,
      modeOfPayment: 'complementry',
    }];
  }

  // Truly unpaid — no splits / paid amount
  if (!hasMoney) {
    const displayAmt = credit > 0 ? credit : 0;
    if (displayAmt <= 0) {
      return [{
        ...base,
        amount: 0,
        modeOfPayment: 'complementry',
      }];
    }
    return [{
      ...base,
      amount: displayAmt,
      modeOfPayment: 'pending',
      trainer: base.trainer || 'Unpaid',
    }];
  }

  // Split tenders → one line per method with money actually collected
  // (exclude remaining-clear installments that belong in Remaining).
  if (expandSplits && splits.length > 1) {
    const ledgerSplits = laterRemaining > 0
      ? ledgerCollectionSplitLines(row)
      : splits;
    if (ledgerSplits.length > 1) {
      const paidDay = paymentCollectedCalendarDay(row);
      const collectedNote = (!onCollectionDay && paidDay && target && paidDay !== target)
        ? `Collected ${paidDay}`
        : (base.trainer || '');
      return ledgerSplits.map((split, i) => ({
        ...base,
        id: `${base.id}-split-${i}`,
        srNo: startIndex + i + 1,
        amount: Number(split.amount) || 0,
        modeOfPayment: zSaleModeLabel(split.method || row.method),
        trainer: collectedNote,
        // Single tender line — don't keep full merged splits (avoids double-sum).
        paymentSplits: [split],
        payment_splits: [split],
      }));
    }
  }

  const paidAmount = originalCollected > 0
    ? originalCollected
    : (splits.length === 1 ? (Number(splits[0].amount) || 0) : collected);
  const mode = paidMode || zSaleModeOfPayment(row) || 'cash';

  // New memberships (start_date list): show amount actually paid (sales collected),
  // not full package+registration when only a partial was taken (e.g. 5459 → 15000).
  if (row.fromMembershipDay) {
    const saleAmt = paidAmount > 0
      ? paidAmount
      : (collected > 0 ? collected : 0);
    const paidDay = paymentCollectedCalendarDay(row);
    if (saleAmt <= 0) {
      const due = credit > 0 ? credit : 0;
      return [{
        ...base,
        amount: due,
        modeOfPayment: due > 0 ? 'pending' : 'complementry',
        trainer: base.trainer || (due > 0 ? 'Unpaid' : ''),
      }];
    }
    return [{
      ...base,
      amount: saleAmt,
      modeOfPayment: mode,
      trainer: (!onCollectionDay && paidDay && target && paidDay !== target)
        ? `Collected ${paidDay}`
        : (base.trainer || ''),
    }];
  }

  // Collection day (renewals / other sales): money taken + real method
  if (onCollectionDay && paidAmount > 0) {
    return [{
      ...base,
      amount: paidAmount,
      modeOfPayment: mode,
    }];
  }

  // Listed on another day but already paid — show paid amount + real method
  const displayAmt = paidAmount > 0 ? paidAmount : (collected > 0 ? collected : 0);
  const paidDay = paymentCollectedCalendarDay(row);
  if (displayAmt <= 0) {
    const due = credit > 0 ? credit : 0;
    return [{
      ...base,
      amount: due,
      modeOfPayment: due > 0 ? 'pending' : 'complementry',
      trainer: base.trainer || (due > 0 ? 'Unpaid' : ''),
    }];
  }
  return [{
    ...base,
    amount: displayAmt,
    modeOfPayment: mode,
    trainer: (paidDay && target && paidDay !== target)
      ? `Collected ${paidDay}`
      : (base.trainer || ''),
  }];
}

/** @deprecated prefer mapZSaleDetailRows for split-aware lines */
function mapZSaleDetailRow(row, index) {
  return mapZSaleDetailRows(row, index)[0];
}

function saleReportRowKey(row) {
  return String(row.rawId || row.id || `${row.member}-${row.package}-${row.date}-${row.amount}`);
}

/**
 * Package-wise SALE REPORT from real payment / guest-pass sales
 * (same sources as receptionist Sales pages).
 * Pass `day` so Mode of Payment cash matches Petty Cash / Z Report for every date.
 */
export function buildZSaleReport(salesRows = [], day = '') {
  const reportDay = String(day || '').slice(0, 10);
  const bySource = new Map();
  const assignedRowKeys = new Set();

  // Consume orphan new-membership payments for EVERY date first — they must not
  // land in New Memberships, Miscellaneous, or any other section.
  (salesRows || []).forEach((row) => {
    if (!isOrphanNewMembershipPayment(row)) return;
    assignedRowKeys.add(saleReportRowKey(row));
  });

  (salesRows || []).forEach((row) => {
    if (isOrphanNewMembershipPayment(row)) return;
    const source = normalizeZPaymentSource(row);
    if (!bySource.has(source)) bySource.set(source, []);
    bySource.get(source).push({ ...row, source });
  });

  const sections = Z_SALE_REPORT_SECTIONS.map((def) => {
    const rows = [];
    def.sources.forEach((src) => {
      (bySource.get(src) || []).forEach((row) => {
        // New Memberships = start_date day only (same as Memberships list / Z SALE SUMMARY)
        if (def.id === 'subscriptions' && !isNewMembershipReportRow(row)) {
          assignedRowKeys.add(saleReportRowKey(row));
          return;
        }
        // Never let a membership-day row appear under Day Pass / Misc / etc.
        if (row.fromMembershipDay && def.id !== 'subscriptions') {
          return;
        }
        assignedRowKeys.add(saleReportRowKey(row));
        rows.push(row);
      });
    });
    let detailRows = [];
    const expandSplits = def.columns !== 'oxybar';
    rows.forEach((row) => {
      detailRows.push(...mapZSaleDetailRows(row, detailRows.length, reportDay, { expandSplits }));
    });
    detailRows = sortZSaleDetailRowsByRecNo(detailRows);
    detailRows.forEach((r, i) => {
      r.srNo = i + 1;
    });
    const total = detailRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

    if (def.columns === 'oxybar') {
      return {
        id: def.id,
        title: def.title,
        columns: def.columns,
        rows: detailRows,
        methodRows: [],
        total,
        hasSales: detailRows.length > 0,
      };
    }

    return {
      id: def.id,
      title: def.title,
      columns: def.columns,
      rows: detailRows,
      methodRows: [],
      total,
      hasSales: detailRows.length > 0,
    };
  });

  // Leftover unknown sources → Miscellaneous Sale only (never new memberships).
  const misc = sections.find((s) => s.id === 'misc_sale');
  if (misc) {
    (salesRows || []).forEach((row) => {
      const key = saleReportRowKey(row);
      if (assignedRowKeys.has(key)) return;
      if (isOrphanNewMembershipPayment(row)) return;
      if (row?.fromMembershipDay) return;
      if (isNewMembershipPaymentSource(row)) return;
      assignedRowKeys.add(key);
      misc.rows.push(...mapZSaleDetailRows(row, misc.rows.length, reportDay));
    });
    misc.rows = sortZSaleDetailRowsByRecNo(misc.rows);
    misc.rows.forEach((r, i) => {
      r.srNo = i + 1;
    });
    misc.total = misc.rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    misc.hasSales = misc.rows.length > 0;
  }

  // Grand Total = sum of all section line amounts (split lines already expanded)
  const grandTotal = sections.reduce((sum, s) => sum + (Number(s.total) || 0), 0);

  // 1) Sum Mode of Payment from Detailed Report rows (cash / card / bank / qr)
  // 2) Z Report PAYMENT METHOD tables use these exact totals
  const modeTotals = sumDetailedReportModeOfPayment(sections);
  const cashTotal = modeTotals.cash.amount;
  const saleReportMethodTotals = [
    { id: 'cash', label: 'CASH', amount: modeTotals.cash.amount, count: modeTotals.cash.count },
    { id: 'card', label: 'CREDIT CARD', amount: modeTotals.card.amount, count: modeTotals.card.count },
    { id: 'bank', label: 'BANK TRANSFER', amount: modeTotals.bank.amount, count: modeTotals.bank.count },
    { id: 'qr', label: 'QR', amount: modeTotals.qr.amount, count: modeTotals.qr.count },
  ];

  return {
    saleReportSections: sections,
    saleReportMethodTotals,
    saleReportCashTotal: cashTotal,
    saleReportTotalAmount: grandTotal,
    saleReportPaymentsTotal: grandTotal,
    saleReportGrandTotal: grandTotal,
  };
}

function pettyCashOpeningKey(gymId, date) {
  return `gym:${gymId || 'default'}:petty-cash-opening:${String(date || '').slice(0, 10)}`;
}

function pettyCashBackupKey(date) {
  return `petty-cash-backup:${String(date || '').slice(0, 10)}`;
}

/** Manual opening balance for Petty Cash Detail (per gym + date). */
export function loadPettyCashOpening(gymId, date) {
  if (typeof window === 'undefined') return 0;
  try {
    const keys = [
      pettyCashOpeningKey(gymId, date),
      // Legacy saves when gym id was not ready yet
      ...(gymId ? [pettyCashOpeningKey('default', date), pettyCashOpeningKey('', date)] : []),
    ];
    for (const key of keys) {
      const raw = localStorage.getItem(key);
      if (raw == null || raw === '') continue;
      const n = Number(raw);
      if (Number.isFinite(n)) {
        // Migrate legacy key → current gym key
        if (gymId && key !== keys[0]) {
          localStorage.setItem(keys[0], String(n));
        }
        return n;
      }
    }
    const backup = localStorage.getItem(pettyCashBackupKey(date));
    if (backup) {
      const parsed = JSON.parse(backup);
      const n = Number(parsed?.openingBalance);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  } catch {
    return 0;
  }
}

export function savePettyCashOpening(gymId, date, amount) {
  if (typeof window === 'undefined') return;
  const n = Math.max(0, Number(amount) || 0);
  const gid = gymId || 'default';
  localStorage.setItem(pettyCashOpeningKey(gid, date), String(n));
}

function pettyCashManualRowsKey(gymId, date) {
  return `gym:${gymId || 'default'}:petty-cash-manual-rows:${String(date || '').slice(0, 10)}`;
}

export function emptyPettyCashManualRow(date = '') {
  return {
    id: `pc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    date: String(date || localDateKey()).slice(0, 10),
    description: '',
    receipts: '',
    payments: '',
  };
}

function parseStoredPettyRows(raw, date) {
  if (!raw) return [];
  const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
  if (!Array.isArray(parsed)) return [];
  return parsed.map((row) => ({
    id: String(row.id || `pc-${Math.random().toString(36).slice(2, 8)}`),
    date: String(row.date || date || '').slice(0, 10),
    description: String(row.description || ''),
    receipts: row.receipts === '' || row.receipts == null ? '' : String(row.receipts),
    payments: row.payments === '' || row.payments == null ? '' : String(row.payments),
  }));
}

/** Manual petty-cash line items (admin Detailed Report). */
export function loadPettyCashManualRows(gymId, date) {
  if (typeof window === 'undefined') return [];
  try {
    const keys = [
      pettyCashManualRowsKey(gymId, date),
      ...(gymId ? [pettyCashManualRowsKey('default', date), pettyCashManualRowsKey('', date)] : []),
    ];
    for (const key of keys) {
      const raw = localStorage.getItem(key);
      const rows = parseStoredPettyRows(raw, date);
      if (rows.length) {
        if (gymId && key !== keys[0]) {
          localStorage.setItem(keys[0], JSON.stringify(rows));
        }
        return rows;
      }
    }
    const backup = localStorage.getItem(pettyCashBackupKey(date));
    if (backup) {
      const parsed = JSON.parse(backup);
      return parseStoredPettyRows(JSON.stringify(parsed?.rows || []), date);
    }
    return [];
  } catch {
    return [];
  }
}

export function savePettyCashManualRows(gymId, date, rows) {
  if (typeof window === 'undefined') return;
  const cleaned = (rows || []).map((row) => ({
    id: String(row.id || `pc-${Math.random().toString(36).slice(2, 8)}`),
    date: String(row.date || date || '').slice(0, 10),
    description: String(row.description || ''),
    receipts: row.receipts === '' || row.receipts == null ? '' : String(row.receipts),
    payments: row.payments === '' || row.payments == null ? '' : String(row.payments),
  }));
  const gid = gymId || 'default';
  localStorage.setItem(pettyCashManualRowsKey(gid, date), JSON.stringify(cleaned));
}

/** Full local backup so a report still reloads if gym id key mismatches. */
export function savePettyCashLocalBackup(date, { openingBalance = 0, rows = [] } = {}) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(pettyCashBackupKey(date), JSON.stringify({
      openingBalance: Math.max(0, Number(openingBalance) || 0),
      rows: (rows || []).map((row) => ({
        id: String(row.id || `pc-${Math.random().toString(36).slice(2, 8)}`),
        date: String(row.date || date || '').slice(0, 10),
        description: String(row.description || ''),
        receipts: row.receipts === '' || row.receipts == null ? '' : String(row.receipts),
        payments: row.payments === '' || row.payments == null ? '' : String(row.payments),
      })),
      savedAt: new Date().toISOString(),
    }));
  } catch {
    // ignore quota errors
  }
}

/**
 * Build Petty Cash Detail from manual rows.
 * Opening (manual) → editable lines → Closing (auto). Balance runs automatically.
 */
export function buildManualPettyCashDetail({
  date,
  openingBalance = 0,
  manualRows = [],
} = {}) {
  const day = String(date || localDateKey()).slice(0, 10);
  const opening = Number(openingBalance) || 0;
  let running = opening;
  let totalReceipts = 0;
  let totalPayments = 0;

  const rows = [
    {
      id: 'opening',
      date: '',
      description: 'Opening Balance',
      receipts: 0,
      payments: 0,
      balance: opening,
      kind: 'opening',
    },
  ];

  (manualRows || []).forEach((row, index) => {
    const receipts = Math.max(0, Number(row.receipts) || 0);
    const payments = Math.max(0, Number(row.payments) || 0);
    totalReceipts += receipts;
    totalPayments += payments;
    running += receipts - payments;
    const kind = String(row.kind || '').trim() || 'manual';
    rows.push({
      id: String(row.id || `manual-${index}`),
      date: String(row.date || day).slice(0, 10),
      description: String(row.description || '').trim() || '—',
      receipts,
      payments,
      balance: running,
      kind,
      source: row.source || '',
      editable: true,
    });
  });

  const closingBalance = opening + totalReceipts - totalPayments;
  const cashSaleTotal = (manualRows || []).reduce((sum, row) => {
    const kind = String(row.kind || '').trim();
    const id = String(row.id || '');
    const isSale = kind === 'sale'
      || (id.startsWith('rcpt-') && !id.startsWith('rcpt-coa-') && kind !== 'coa_receipt');
    if (!isSale) return sum;
    return sum + Math.max(0, Number(row.receipts) || 0);
  }, 0);

  rows.push({
    id: 'closing',
    date: '',
    description: 'Closing Balance',
    receipts: totalReceipts,
    payments: totalPayments,
    balance: closingBalance,
    kind: 'closing',
  });

  return {
    date: day,
    openingBalance: opening,
    totalReceipts,
    cashSaleTotal,
    totalPayments,
    closingBalance,
    rows,
  };
}

function pettyCashSaleLabel(row) {
  const source = normalizeZPaymentSource(row);
  const map = {
    subscription: 'membership',
    guest_pass: 'day pass',
    renewal_sale: 'renewal',
    pt_sale: 'pt sale',
    locker_sale: 'locker sale',
    spa_sale: 'spa sale',
    oxybar_sale: 'oxy bar cash',
    zumba_sale: 'zumba sale',
    yoga_sale: 'yoga sale',
    gym_apparel: 'gym apparel',
    miscellaneous_sale: 'misc sale',
    remaining_clear: 'remaining clear',
  };
  if (map[source]) return map[source];
  const pkg = String(row.package || row.item || row.type || '').trim();
  if (pkg && pkg !== '—') return pkg.toLowerCase();
  return saleCategoryFromPayment(row).toLowerCase();
}

function pettyCashCustomerName(row) {
  const raw = String(row.member || row.name || '').trim();
  const gymId = String(row.gymId || row.serialNo || '').trim();
  const lower = raw.toLowerCase();
  const isWalkIn = (
    gymId === '1'
    || !raw
    || raw === '—'
    || /walk[-\s]?in/i.test(raw)
    || /oxy.?gym\s+walk/i.test(raw)
    || lower === 'walk-in customer'
    || lower === 'walk in customer'
  );
  if (isWalkIn) return 'Walk-In Customer';
  return raw;
}

function pettyCashReceiptDescription(row) {
  const label = pettyCashSaleLabel(row);
  const name = pettyCashCustomerName(row);
  const serial = String(row.gymId || row.serialNo || '').trim();
  const source = normalizeZPaymentSource(row);
  if (source === 'remaining_clear') {
    const idPart = serial && serial !== '—' ? ` #${serial}` : '';
    return `${name}${idPart} remaining clear`.trim();
  }
  return `${name} ${label}`.trim();
}

function pettyCashExpenseDescription(expense) {
  const title = String(expense.accountTitle || expense.description || expense.category || 'Expense').trim();
  const note = String(expense.description || '').trim();
  if (expense.accountTitle && note && note.toLowerCase() !== title.toLowerCase()) {
    return `${title} — ${note}`;
  }
  return title || 'Expense';
}

function isSellingExpense(expense) {
  const cat = String(expense.category || '').toLowerCase();
  const title = String(expense.accountTitle || expense.description || '').toLowerCase();
  const code = String(expense.accountCode || expense.code || expense.parentCode || '').toUpperCase();
  if (code === 'SEL' || code.startsWith('SEL-')) return true;
  return /sell|sale|marketing|ads|promo|commission/i.test(`${cat} ${title}`);
}

function isAdminExpense(expense) {
  const cat = String(expense.category || '').toLowerCase();
  const title = String(expense.accountTitle || expense.description || '').toLowerCase();
  const code = String(expense.accountCode || expense.code || expense.parentCode || '').toUpperCase();
  if (code === 'ADM' || code.startsWith('ADM-')) return true;
  return /\badmin\b/i.test(`${cat} ${title}`);
}

function isOtherLedgerExpense(expense) {
  const cat = String(expense.category || '').toLowerCase();
  const title = String(expense.accountTitle || expense.description || '').toLowerCase();
  const code = String(expense.accountCode || expense.code || expense.parentCode || '').toUpperCase();
  if (code === 'OEXP' || code.startsWith('OEXP-')) return true;
  return /\bother\b/i.test(`${cat} ${title}`);
}

/**
 * Load CoA ledger cash lines for a day (localStorage fallback).
 * Cash in Hand / CPV/CRV: Debit → Receipts, Credit → Payments (single effect).
 */
export function loadPettyCashCoaExpenses(gymId, date) {
  const day = String(date || '').slice(0, 10);
  if (!gymId || !day) return [];
  const out = [];
  const seen = new Set();
  (COA_PARENT_ACCOUNTS || []).forEach(({ code: parentCode, name: parentName }) => {
    getCoaLedgerEntries(gymId, { parentCode }).forEach((entry) => {
      const rowDay = String(entry.date || '').slice(0, 10);
      if (rowDay !== day) return;
      const code = String(entry.childCode || entry.accountCode || parentCode || '').toUpperCase();
      const ref = String(entry.ref || '').toUpperCase();
      const cashInHand = code === 'CIH' || code.startsWith('CIH-') || code === 'CIH-CASH';
      const cashVoucher = /^CPV\b/.test(ref) || ref.startsWith('CPV-') || /^CPV\d/.test(ref)
        || /^CRV\b/.test(ref) || ref.startsWith('CRV-') || /^CRV\d/.test(ref);
      // CPV/CRV: only Cash in Hand leg
      if (cashVoucher && !cashInHand) return;

      const debit = Number(entry.debit) || 0;
      const credit = Number(entry.credit) || 0;
      if (debit <= 0 && credit <= 0) return;

      const pushSide = (side, amount, pettyColumn) => {
        if (amount <= 0) return;
        const row = {
          id: String(entry.id || `${parentCode}-${entry.ref}-${rowDay}-${side}-${amount}`),
          accountTitle: entry.childName || entry.description || parentName || parentCode,
          description: entry.description || '',
          category: parentName || parentCode,
          parentCode,
          amount,
          debit: side === 'debit' ? amount : 0,
          credit: side === 'credit' ? amount : 0,
          side,
          pettyColumn,
          date: rowDay,
          status: 'paid',
          method: entry.method || entry.paymentMethod || 'cash',
          paymentMethod: entry.paymentMethod || entry.method || 'cash',
          accountCode: code || parentCode,
          code: code || parentCode,
          ref: entry.ref || '',
          source: 'coa_ledger',
        };
        if (expenseCashBankBucket(row) !== 'cash' && !cashInHand && !cashVoucher) return;
        const key = `${row.id}-${side}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({ ...row, id: key });
      };

      if (cashInHand || cashVoucher) {
        pushSide('debit', debit, 'receipts');
        pushSide('credit', credit, 'payments');
      } else {
        pushSide('debit', debit, 'payments');
      }
    });
  });
  return out;
}

/** Local Cash at Bank / BPV/BRV lines for Bank Detail (same shape as petty CoA rows). */
export function loadBankCoaExpenses(gymId, date) {
  const day = String(date || '').slice(0, 10);
  if (!gymId || !day) return [];
  const out = [];
  const seen = new Set();
  (COA_PARENT_ACCOUNTS || []).forEach(({ code: parentCode, name: parentName }) => {
    getCoaLedgerEntries(gymId, { parentCode }).forEach((entry) => {
      const rowDay = String(entry.date || '').slice(0, 10);
      if (rowDay !== day) return;
      const code = String(entry.childCode || entry.accountCode || parentCode || '').toUpperCase();
      const ref = String(entry.ref || '').toUpperCase();
      const cashAtBank = isCashAtBankCode(code, parentCode);
      const bankVoucher = /^BPV\b/.test(ref) || ref.startsWith('BPV-') || /^BPV\d/.test(ref)
        || /^BRV\b/.test(ref) || ref.startsWith('BRV-') || /^BRV\d/.test(ref);
      if (bankVoucher && !cashAtBank) return;

      const debit = Number(entry.debit) || 0;
      const credit = Number(entry.credit) || 0;
      if (debit <= 0 && credit <= 0) return;

      const pushSide = (side, amount, pettyColumn) => {
        if (amount <= 0) return;
        const row = {
          id: String(entry.id || `${parentCode}-${entry.ref}-${rowDay}-${side}-${amount}`),
          accountTitle: entry.childName || entry.description || parentName || parentCode,
          description: entry.description || '',
          category: parentName || parentCode,
          parentCode,
          amount,
          debit: side === 'debit' ? amount : 0,
          credit: side === 'credit' ? amount : 0,
          side,
          pettyColumn,
          date: rowDay,
          status: 'paid',
          method: entry.method || entry.paymentMethod || 'bank',
          paymentMethod: entry.paymentMethod || entry.method || 'bank',
          accountCode: code || parentCode,
          code: code || parentCode,
          ref: entry.ref || '',
          source: 'coa_ledger',
        };
        if (expenseCashBankBucket(row) !== 'bank' && !cashAtBank && !bankVoucher) return;
        const key = `${row.id}-${side}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({ ...row, id: key });
      };

      if (cashAtBank || bankVoucher) {
        pushSide('debit', debit, 'receipts');
        pushSide('credit', credit, 'payments');
      } else {
        pushSide('debit', debit, 'payments');
      }
    });
  });
  return out;
}

/**
 * Whether an expense should appear on Petty Cash Detail payments (cash outflows only).
 */
function includeExpenseInPettyCash(expense) {
  return expenseCashBankBucket(expense) === 'cash';
}

function includeExpenseInBank(expense) {
  return expenseCashBankBucket(expense) === 'bank';
}

function isCashAtBankCode(code, parentCode = '') {
  const c = String(code || '').trim().toUpperCase();
  const p = String(parentCode || '').trim().toUpperCase();
  if (p === 'CAB' || c === 'CAB' || c.startsWith('CAB-') || c === 'PAY-BANK') return true;
  return false;
}

/** Amount that belongs in Petty Cash Receipts (cash / counter collections). */
function pettyCashReceiptAmount(payment) {
  const lines = ledgerCollectionSplitLines(payment);
  // Prefer split lines (same as Z Report PAYMENT METHOD / CASH SALE). Header `amount`
  // can be 0 on pending / partial membership rows while cash was still collected in splits.
  if (lines.length) {
    let cash = 0;
    lines.forEach((split) => {
      const tender = normalizeTenderMethod(split.method || payment.method || payment.payment_method || 'cash');
      if (tender === 'Cash') cash += Number(split.amount) || 0;
    });
    return cash > 0 ? cash : 0;
  }

  const collected = saleRowOriginalPaid(payment);
  if (collected <= 0) return 0;

  const methodRaw = String(payment.method || payment.payment_method || '').trim();
  if (!methodRaw || methodRaw === '—' || methodRaw === '-') {
    // Counter sales often omit method — treat collected amount as cash
    return collected;
  }
  const tender = normalizeTenderMethod(methodRaw);
  if (tender === 'Cash') return collected;
  // Combined label e.g. "Cash + Card" without split rows: cash only if cash is the sole method
  if (/cash/i.test(methodRaw) && !/card|bank|qr|online|jazz|easy/i.test(methodRaw.replace(/cash/ig, ''))) {
    return collected;
  }
  return 0;
}

/** Amount that belongs in Bank Detail Receipts (bank / card / QR / online). */
function bankReceiptAmount(payment) {
  const lines = ledgerCollectionSplitLines(payment);
  if (lines.length) {
    let bank = 0;
    lines.forEach((split) => {
      const tender = normalizeTenderMethod(split.method || payment.method || payment.payment_method || 'cash');
      if (tender && tender !== 'Cash' && tender !== 'Other') {
        bank += Number(split.amount) || 0;
      }
    });
    return bank > 0 ? bank : 0;
  }

  const collected = saleRowOriginalPaid(payment);
  if (collected <= 0) return 0;
  const methodRaw = String(payment.method || payment.payment_method || '').trim();
  if (!methodRaw || methodRaw === '—' || methodRaw === '-') return 0;
  const tender = normalizeTenderMethod(methodRaw);
  if (tender === 'Cash' || tender === 'Other') return 0;
  return collected;
}

/**
 * CoA "Opening Balance" lines belong in Opening Balance, not as day receipts.
 * Otherwise Petty Total Receipts inflate (e.g. 30-07: 86,036 opening + sales = 147,036).
 */
function isOpeningBalanceLedgerEntry(expense) {
  const text = [
    expense?.description,
    expense?.accountTitle,
    expense?.category,
    expense?.ref,
    expense?.notes,
  ].map((v) => String(v || '').toLowerCase()).join(' ');
  return /opening\s*balance/.test(text);
}

/**
 * Whether a CoA / expense cash line belongs in Petty Cash Receipts.
 * Cash book (CIH / CPV / CRV): Debit → Receipts, Credit → Payments.
 * Other cash expenses: Debit → Payments (default).
 */
function isPettyCashCreditReceipt(expense) {
  if (isOpeningBalanceLedgerEntry(expense)) return false;

  const col = String(expense?.pettyColumn || '').toLowerCase().trim();
  if (col === 'receipts' || col === 'receipt') return true;
  if (col === 'payments' || col === 'payment') return false;

  const code = String(expense?.accountCode || expense?.code || '').toUpperCase();
  const cashInHand = code === 'CIH' || code.startsWith('CIH-') || code === 'CIH-CASH';

  // Only Cash in Hand is the cash book. CPV/CRV expense legs (ADM/SEL/…) are
  // outflows for Z Report — do not treat them as receipts just because ref is CPV.
  if (cashInHand) {
    const side = String(expense?.side || '').toLowerCase().trim();
    if (side === 'debit') return true;
    if (side === 'credit') return false;
    const debit = Number(expense?.debit) || 0;
    const credit = Number(expense?.credit) || 0;
    if (debit > 0 && credit <= 0) return true;
    return false;
  }

  // Legacy non-cash-book rows: credit → receipt
  const side = String(expense?.side || expense?.coaSide || '').toLowerCase().trim();
  if (side === 'credit') return true;
  if (side === 'debit') return false;
  const credit = Number(expense?.credit) || 0;
  const debit = Number(expense?.debit) || 0;
  if (credit > 0 && debit <= 0) return true;
  return false;
}

/**
 * CPV/CRV expense/loan legs are skipped — Cash in Hand carries the single cash effect.
 */
function isCashVoucherNonCashLeg(expense) {
  const ref = String(expense?.ref || '').toUpperCase();
  const isVoucher = /^CPV\b/.test(ref) || ref.startsWith('CPV-') || /^CPV\d/.test(ref)
    || /^CRV\b/.test(ref) || ref.startsWith('CRV-') || /^CRV\d/.test(ref);
  if (!isVoucher) return false;
  const code = String(expense?.accountCode || expense?.code || '').toUpperCase();
  const cashInHand = code === 'CIH' || code.startsWith('CIH-') || code === 'CIH-CASH';
  return !cashInHand;
}

function isBankVoucherNonBankLeg(expense) {
  const ref = String(expense?.ref || '').toUpperCase();
  const isVoucher = /^BPV\b/.test(ref) || ref.startsWith('BPV-') || /^BPV\d/.test(ref)
    || /^BRV\b/.test(ref) || ref.startsWith('BRV-') || /^BRV\d/.test(ref);
  if (!isVoucher) return false;
  const code = String(expense?.accountCode || expense?.code || '').toUpperCase();
  const parent = String(expense?.parentCode || '').toUpperCase();
  return !isCashAtBankCode(code, parent);
}

function isBankCreditReceipt(expense) {
  if (isOpeningBalanceLedgerEntry(expense)) return false;
  const col = String(expense?.pettyColumn || '').toLowerCase().trim();
  if (col === 'receipts' || col === 'receipt') return true;
  if (col === 'payments' || col === 'payment') return false;
  const code = String(expense?.accountCode || expense?.code || '').toUpperCase();
  const parent = String(expense?.parentCode || '').toUpperCase();
  const cashAtBank = isCashAtBankCode(code, parent);
  if (cashAtBank) {
    const side = String(expense?.side || '').toLowerCase().trim();
    if (side === 'debit') return true;
    if (side === 'credit') return false;
    const debit = Number(expense?.debit) || 0;
    const credit = Number(expense?.credit) || 0;
    if (debit > 0 && credit <= 0) return true;
    return false;
  }
  const side = String(expense?.side || expense?.coaSide || '').toLowerCase().trim();
  if (side === 'credit') return true;
  if (side === 'debit') return false;
  const credit = Number(expense?.credit) || 0;
  const debit = Number(expense?.debit) || 0;
  if (credit > 0 && debit <= 0) return true;
  return false;
}

/**
 * Petty Cash Detail for a report day:
 * Opening (manual) → cash sale receipts → cash CoA credits/debits + expenses → Closing (auto).
 */
export function buildPettyCashDetail({
  date,
  dateFrom = '',
  dateTo = '',
  payments = [],
  guestPassIncome: _guestPassIncome = [],
  expenses = [],
  salesRows = null,
  openingBalance = 0,
  book = 'cash',
} = {}) {
  // guestPassIncome intentionally unused — orphan guest_passes must not become cash receipts.
  const isBankBook = book === 'bank';
  const day = String(date || localDateKey()).slice(0, 10);
  const rangeFrom = String(dateFrom || day).slice(0, 10);
  const rangeTo = String(dateTo || dateFrom || day).slice(0, 10);
  const start = rangeFrom <= rangeTo ? rangeFrom : rangeTo;
  const end = rangeFrom <= rangeTo ? rangeTo : rangeFrom;
  const inSaleWindow = (value) => {
    const d = String(value || '').slice(0, 10);
    return Boolean(d) && d >= start && d <= end;
  };

  // Prefer explicit opening; else lift CoA "Opening Balance" lines into opening
  // (do not count them again as day receipts).
  let openingFromCoa = 0;
  (expenses || []).forEach((expense) => {
    if (!isOpeningBalanceLedgerEntry(expense)) return;
    const amount = Number(expense.amount) || Number(expense.debit) || 0;
    if (amount <= 0) return;
    const rowDay = paymentDate(expense) || ledgerRowDate(expense) || String(expense.date || '').slice(0, 10);
    if (rowDay && rowDay !== start) return;
    openingFromCoa = Math.max(openingFromCoa, amount);
  });
  const opening = Math.max(0, Number(openingBalance) || 0) || openingFromCoa;

  const receiptEvents = [];
  // Union salesRows + payments so Remaining Clear (and other day collections)
  // always feed Petty Cash even if a sale-report filter dropped them.
  const salePool = [];
  const seenPool = new Set();
  const pushPool = (row, index, tag) => {
    if (!row) return;
    const key = String(row.rawId || row.id || `${tag}-${row.member}-${row.amount}-${index}`);
    if (seenPool.has(key)) return;
    seenPool.add(key);
    salePool.push(row);
  };
  (Array.isArray(salesRows) ? salesRows : []).forEach((row, i) => pushPool(row, i, 'sale'));
  (payments || []).forEach((row, i) => pushPool(row, i, 'pay'));

  // Do NOT append orphan guest_passes table rows as cash receipts.
  // Those rows have no payment_method / payment — drafts or unpaid passes were
  // assumed Cash and inflated Petty Cash (e.g. 13 Oct Walk-In day pass 1,500
  // with no matching payments row). Z Report Cash already uses payments only;
  // Petty Cash must match that. Real guest-pass cash comes from salePool payments.

  const seenReceipt = new Set();
  salePool.forEach((row, index) => {
    if (isRefund(row)) return;

    // Cash only on the collection day (membership start day alone does not count).
    // Remaining Clear cash installments count on the day they were paid.
    const rowDay = paymentCollectedCalendarDay(row)
      || paymentDate(row)
      || '';
    if (!inSaleWindow(rowDay)) return;
    const cash = isBankBook
      ? bankOnReportDay(row, rowDay)
      : pettyCashOnReportDay(row, rowDay);
    if (cash <= 0) return;
    const key = String(row.rawId || row.id || `${row.member}-${cash}-${rowDay}-${index}`);
    if (seenReceipt.has(key)) return;
    seenReceipt.add(key);

    const serialPad = String(Number(String(row.gymId || '').replace(/\D/g, '')) || 0).padStart(8, '0');
    receiptEvents.push({
      id: `rcpt-${key}`,
      sort: `${rowDay || day}-1-${serialPad}-${key}`,
      date: rowDay || day,
      description: pettyCashReceiptDescription(row),
      receipts: cash,
      payments: 0,
      kind: 'sale',
      source: normalizeZPaymentSource(row),
      gymId: row.gymId || '',
    });
  });

  const paymentEvents = [];
  const seenExpense = new Set();
  (expenses || []).forEach((expense, index) => {
    if (!isPaidExpense(expense) && String(expense.status || 'paid').toLowerCase() !== 'paid') return;
    if (isOpeningBalanceLedgerEntry(expense)) return;
    const amount = Number(expense.amount) || 0;
    if (amount <= 0) return;
    const rowDay = paymentDate(expense) || ledgerRowDate(expense) || String(expense.date || '').slice(0, 10);
    if (!inSaleWindow(rowDay)) return;

    // Selling / Admin / Other expense ledgers + all expense-table rows for the day
    if (isBankBook ? !includeExpenseInBank(expense) : !includeExpenseInPettyCash(expense)) return;
    if (isBankBook ? isBankVoucherNonBankLeg(expense) : isCashVoucherNonCashLeg(expense)) return;

    const key = String(expense.id || `${expense.description}-${amount}-${rowDay}`);
    if (seenExpense.has(key)) return;
    seenExpense.add(key);

    const asReceipt = isBankBook
      ? isBankCreditReceipt(expense)
      : isPettyCashCreditReceipt(expense);
    const description = pettyCashExpenseDescription(expense);

    if (asReceipt) {
      receiptEvents.push({
        id: `rcpt-coa-${expense.id || index}`,
        sort: `${rowDay}-1c-${key}`,
        date: rowDay,
        description,
        receipts: amount,
        payments: 0,
        kind: 'coa_receipt',
        category: expense.category || '',
        source: expense.source || 'coa_ledger',
      });
      return;
    }

    const selling = isSellingExpense(expense);
    const admin = isAdminExpense(expense);
    const codePad = String(expense.accountCode || expense.code || expense.ref || key).toUpperCase();
    paymentEvents.push({
      id: `exp-${expense.id || index}`,
      sort: `${rowDay}-2-${codePad}-${key}`,
      date: rowDay,
      description,
      receipts: 0,
      payments: amount,
      kind: selling ? 'selling_expense' : admin ? 'admin_expense' : 'expense',
      category: expense.category || '',
    });
  });

  // Interleave chronologically: opening, then sales (serial) + expenses (account code)
  const events = [...receiptEvents, ...paymentEvents].sort((a, b) => String(a.sort).localeCompare(String(b.sort)));

  let running = opening;
  const rows = [
    {
      id: 'opening',
      date: '',
      description: 'Opening Balance',
      receipts: 0,
      payments: 0,
      balance: running,
      kind: 'opening',
    },
  ];

  events.forEach((event) => {
    running += Number(event.receipts) || 0;
    running -= Number(event.payments) || 0;
    rows.push({
      ...event,
      balance: running,
    });
  });

  const totalReceipts = receiptEvents.reduce((sum, r) => sum + (Number(r.receipts) || 0), 0);
  const cashSaleTotal = receiptEvents
    .filter((r) => r.kind === 'sale')
    .reduce((sum, r) => sum + (Number(r.receipts) || 0), 0);
  const totalPayments = paymentEvents.reduce((sum, r) => sum + (Number(r.payments) || 0), 0);
  const closingBalance = opening + totalReceipts - totalPayments;

  rows.push({
    id: 'closing',
    date: '',
    description: 'Closing Balance',
    receipts: totalReceipts,
    payments: totalPayments,
    balance: closingBalance,
    kind: 'closing',
  });

  return {
    date: day,
    dateFrom: start,
    dateTo: end,
    book: isBankBook ? 'bank' : 'cash',
    openingBalance: opening,
    totalReceipts,
    /** Cash collected from sales only (matches Z Report CASH SALE). */
    cashSaleTotal,
    totalPayments,
    closingBalance,
    rows,
  };
}

/** Bank / Card / QR book — same layout as Petty Cash Detail. */
export function buildBankDetail(args = {}) {
  return buildPettyCashDetail({ ...args, book: 'bank' });
}

/** True when a petty-cash line was generated from sales / CoA (not typed by hand). */
export function isAutoPettyCashRow(row) {
  if (!row) return false;
  if (row.auto === true) return true;
  const id = String(row.id || '');
  return id.startsWith('rcpt-') || id.startsWith('exp-');
}

/** Convert auto petty-cash detail lines into editable manual-row shape. */
export function pettyCashDetailToManualRows(detail) {
  return (detail?.rows || [])
    .filter((row) => row.kind !== 'opening' && row.kind !== 'closing')
    .map((row) => ({
      id: row.id,
      date: row.date || '',
      description: row.description || '',
      receipts: Number(row.receipts) > 0 ? String(row.receipts) : '',
      payments: Number(row.payments) > 0 ? String(row.payments) : '',
      auto: true,
      kind: row.kind || '',
      source: row.source || '',
      category: row.category || '',
    }));
}

/**
 * Build auto Petty Cash lines for a day:
 * receipts from sales + cash CoA credits; payments from cash CoA debits + day expenses.
 */
export function buildPettyCashAutoManualRows({
  date,
  payments = [],
  guestPassIncome = [],
  expenses = [],
  salesRows = null,
  openingBalance = 0,
} = {}) {
  const detail = buildPettyCashDetail({
    date,
    payments,
    guestPassIncome,
    expenses,
    salesRows,
    openingBalance,
  });
  return pettyCashDetailToManualRows(detail);
}

/** Replace petty-cash lines with a fresh auto build (sales cash + ledger expenses). */
export function mergePettyCashAutoRows(_existingRows = [], autoRows = []) {
  return [...(autoRows || [])];
}

// Keep empty exports for any legacy imports
export const membershipPayments = [];
export const trainerSalaries = [];
export const expenses = [];
export const outstandingPayments = [];
export const refunds = [];
export const lateFees = [];
export const paymentHistory = [];
export const branchOptions = [];
export const revenueSummary = { monthly: 0, expenses: 0, profit: 0, outstanding: 0, collected: 0 };
export const revenueChartData = { labels: [], values: [] };
export const profitReport = {
  revenue: 0,
  membershipIncome: 0,
  otherIncome: 0,
  trainerSalaries: 0,
  expenses: 0,
  refunds: 0,
  netProfit: 0,
};

/* ─── Manual Cash Payments (Cash Payment tab) ─── */

function cashPaymentStorageKey(gymId) {
  return `gym:${gymId || 'default'}:manual_cash_payments`;
}

function normalizeManualCashPayment(row = {}) {
  const payment = Math.max(0, Number(row.payment ?? row.amount) || 0);
  return {
    id: String(row.id || `CASH-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
    code: String(row.code || '').trim(),
    accountTitle: String(row.accountTitle || row.title || '').trim(),
    description: String(row.description || '').trim(),
    payment,
    date: String(row.date || localDateKey()).slice(0, 10),
    method: 'Cash',
    createdAt: String(row.createdAt || localDateKey()).slice(0, 10),
  };
}

export function loadManualCashPayments(gymId) {
  if (!gymId || typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(cashPaymentStorageKey(gymId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeManualCashPayment)
      .filter((row) => row.code && row.accountTitle && row.payment > 0)
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  } catch {
    return [];
  }
}

function saveManualCashPayments(gymId, rows) {
  if (!gymId || typeof window === 'undefined') return;
  localStorage.setItem(cashPaymentStorageKey(gymId), JSON.stringify(rows || []));
}

export function suggestNextCashPaymentCode(gymId) {
  const rows = loadManualCashPayments(gymId);
  const used = new Set(rows.map((r) => r.code));
  for (let n = 1; n <= 9999; n += 1) {
    const code = `C${String(n).padStart(4, '0')}`;
    if (!used.has(code)) return code;
  }
  return `C${Date.now().toString().slice(-4)}`;
}

export function addManualCashPayment(gymId, form) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const accountTitle = String(form.accountTitle || '').trim();
  if (!accountTitle) throw new Error('Account title is required.');

  const payment = Number(form.payment);
  if (!Number.isFinite(payment) || payment <= 0) {
    throw new Error('Enter a valid payment amount greater than zero.');
  }

  let code = String(form.code || '').trim();
  if (!code) code = suggestNextCashPaymentCode(gymId);

  const existing = loadManualCashPayments(gymId);
  if (existing.some((r) => r.code.toLowerCase() === code.toLowerCase())) {
    throw new Error(`Code ${code} already exists.`);
  }

  const row = normalizeManualCashPayment({
    code,
    accountTitle,
    description: form.description,
    payment,
    date: form.date || localDateKey(),
    createdAt: localDateKey(),
  });
  saveManualCashPayments(gymId, [row, ...existing]);
  return row;
}

export function deleteManualCashPayment(gymId, id) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const existing = loadManualCashPayments(gymId);
  const next = existing.filter((r) => r.id !== id);
  if (next.length === existing.length) {
    throw new Error('Cash payment not found.');
  }
  saveManualCashPayments(gymId, next);
  return true;
}

/* ─── Manual Bank Payments (Bank Payment tab) ─── */

function bankPaymentStorageKey(gymId) {
  return `gym:${gymId || 'default'}:manual_bank_payments`;
}

function normalizeManualBankPayment(row = {}) {
  const credit = Math.max(0, Number(row.credit) || 0);
  const debit = Math.max(0, Number(row.debit) || 0);
  return {
    id: String(row.id || `BANK-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
    code: String(row.code || '').trim(),
    accountTitle: String(row.accountTitle || row.title || '').trim(),
    description: String(row.description || '').trim(),
    credit,
    debit,
    date: String(row.date || localDateKey()).slice(0, 10),
    method: 'Bank',
    createdAt: String(row.createdAt || localDateKey()).slice(0, 10),
  };
}

export function loadManualBankPayments(gymId) {
  if (!gymId || typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(bankPaymentStorageKey(gymId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeManualBankPayment)
      .filter((row) => row.code && row.accountTitle && (row.credit > 0 || row.debit > 0))
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  } catch {
    return [];
  }
}

function saveManualBankPayments(gymId, rows) {
  if (!gymId || typeof window === 'undefined') return;
  localStorage.setItem(bankPaymentStorageKey(gymId), JSON.stringify(rows || []));
}

export function suggestNextBankPaymentCode(gymId) {
  const rows = loadManualBankPayments(gymId);
  const used = new Set(rows.map((r) => r.code));
  for (let n = 1; n <= 9999; n += 1) {
    const code = `B${String(n).padStart(4, '0')}`;
    if (!used.has(code)) return code;
  }
  return `B${Date.now().toString().slice(-4)}`;
}

export function addManualBankPayment(gymId, form) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const accountTitle = String(form.accountTitle || '').trim();
  if (!accountTitle) throw new Error('Account title is required.');

  const credit = Number(form.credit) || 0;
  const debit = Number(form.debit) || 0;
  if ((!Number.isFinite(credit) || credit < 0) || (!Number.isFinite(debit) || debit < 0)) {
    throw new Error('Enter valid credit / debit amounts.');
  }
  if (credit <= 0 && debit <= 0) {
    throw new Error('Enter a credit or debit amount greater than zero.');
  }

  let code = String(form.code || '').trim();
  if (!code) code = suggestNextBankPaymentCode(gymId);

  const existing = loadManualBankPayments(gymId);
  if (existing.some((r) => r.code.toLowerCase() === code.toLowerCase())) {
    throw new Error(`Code ${code} already exists.`);
  }

  const row = normalizeManualBankPayment({
    code,
    accountTitle,
    description: form.description,
    credit,
    debit,
    date: form.date || localDateKey(),
    createdAt: localDateKey(),
  });
  saveManualBankPayments(gymId, [row, ...existing]);
  return row;
}

export function deleteManualBankPayment(gymId, id) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const existing = loadManualBankPayments(gymId);
  const next = existing.filter((r) => r.id !== id);
  if (next.length === existing.length) {
    throw new Error('Bank payment not found.');
  }
  saveManualBankPayments(gymId, next);
  return true;
}

/* ─── Manual Card Payments (Card Payment tab) ─── */

function cardPaymentStorageKey(gymId) {
  return `gym:${gymId || 'default'}:manual_card_payments`;
}

function normalizeManualCardPayment(row = {}) {
  const credit = Math.max(0, Number(row.credit) || 0);
  const debit = Math.max(0, Number(row.debit) || 0);
  return {
    id: String(row.id || `CARD-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
    code: String(row.code || '').trim(),
    accountTitle: String(row.accountTitle || row.title || '').trim(),
    description: String(row.description || '').trim(),
    credit,
    debit,
    date: String(row.date || localDateKey()).slice(0, 10),
    method: 'Card',
    createdAt: String(row.createdAt || localDateKey()).slice(0, 10),
  };
}

export function loadManualCardPayments(gymId) {
  if (!gymId || typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(cardPaymentStorageKey(gymId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeManualCardPayment)
      .filter((row) => row.code && row.accountTitle && (row.credit > 0 || row.debit > 0))
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  } catch {
    return [];
  }
}

function saveManualCardPayments(gymId, rows) {
  if (!gymId || typeof window === 'undefined') return;
  localStorage.setItem(cardPaymentStorageKey(gymId), JSON.stringify(rows || []));
}

export function suggestNextCardPaymentCode(gymId) {
  const rows = loadManualCardPayments(gymId);
  const used = new Set(rows.map((r) => r.code));
  for (let n = 1; n <= 9999; n += 1) {
    const code = `R${String(n).padStart(4, '0')}`;
    if (!used.has(code)) return code;
  }
  return `R${Date.now().toString().slice(-4)}`;
}

export function addManualCardPayment(gymId, form) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const accountTitle = String(form.accountTitle || '').trim();
  if (!accountTitle) throw new Error('Account title is required.');

  const credit = Number(form.credit) || 0;
  const debit = Number(form.debit) || 0;
  if ((!Number.isFinite(credit) || credit < 0) || (!Number.isFinite(debit) || debit < 0)) {
    throw new Error('Enter valid credit / debit amounts.');
  }
  if (credit <= 0 && debit <= 0) {
    throw new Error('Enter a credit or debit amount greater than zero.');
  }

  let code = String(form.code || '').trim();
  if (!code) code = suggestNextCardPaymentCode(gymId);

  const existing = loadManualCardPayments(gymId);
  if (existing.some((r) => r.code.toLowerCase() === code.toLowerCase())) {
    throw new Error(`Code ${code} already exists.`);
  }

  const row = normalizeManualCardPayment({
    code,
    accountTitle,
    description: form.description,
    credit,
    debit,
    date: form.date || localDateKey(),
    createdAt: localDateKey(),
  });
  saveManualCardPayments(gymId, [row, ...existing]);
  return row;
}

export function deleteManualCardPayment(gymId, id) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const existing = loadManualCardPayments(gymId);
  const next = existing.filter((r) => r.id !== id);
  if (next.length === existing.length) {
    throw new Error('Card payment not found.');
  }
  saveManualCardPayments(gymId, next);
  return true;
}

/* ─── Manual QR Payments (QR Entries on Chart of Accounts) ─── */

function qrPaymentStorageKey(gymId) {
  return `gym:${gymId || 'default'}:manual_qr_payments`;
}

function normalizeManualQrPayment(row = {}) {
  const credit = Math.max(0, Number(row.credit) || 0);
  const debit = Math.max(0, Number(row.debit) || 0);
  return {
    id: String(row.id || `QR-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
    code: String(row.code || '').trim(),
    accountTitle: String(row.accountTitle || row.title || '').trim(),
    description: String(row.description || '').trim(),
    credit,
    debit,
    date: String(row.date || localDateKey()).slice(0, 10),
    method: 'QR',
    createdAt: String(row.createdAt || localDateKey()).slice(0, 10),
  };
}

export function loadManualQrPayments(gymId) {
  if (!gymId || typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(qrPaymentStorageKey(gymId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeManualQrPayment)
      .filter((row) => row.code && row.accountTitle && (row.credit > 0 || row.debit > 0))
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  } catch {
    return [];
  }
}

function saveManualQrPayments(gymId, rows) {
  if (!gymId || typeof window === 'undefined') return;
  localStorage.setItem(qrPaymentStorageKey(gymId), JSON.stringify(rows || []));
}

export function suggestNextQrPaymentCode(gymId) {
  const rows = loadManualQrPayments(gymId);
  const used = new Set(rows.map((r) => r.code));
  for (let n = 1; n <= 9999; n += 1) {
    const code = `Q${String(n).padStart(4, '0')}`;
    if (!used.has(code)) return code;
  }
  return `Q${Date.now().toString().slice(-4)}`;
}

export function addManualQrPayment(gymId, form) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const accountTitle = String(form.accountTitle || '').trim();
  if (!accountTitle) throw new Error('Account title is required.');

  const credit = Number(form.credit) || 0;
  const debit = Number(form.debit) || 0;
  if ((!Number.isFinite(credit) || credit < 0) || (!Number.isFinite(debit) || debit < 0)) {
    throw new Error('Enter valid credit / debit amounts.');
  }
  if (credit <= 0 && debit <= 0) {
    throw new Error('Enter a credit or debit amount greater than zero.');
  }

  let code = String(form.code || '').trim();
  if (!code) code = suggestNextQrPaymentCode(gymId);

  const existing = loadManualQrPayments(gymId);
  if (existing.some((r) => r.code.toLowerCase() === code.toLowerCase())) {
    throw new Error(`Code ${code} already exists.`);
  }

  const row = normalizeManualQrPayment({
    code,
    accountTitle,
    description: form.description,
    credit,
    debit,
    date: form.date || localDateKey(),
    createdAt: localDateKey(),
  });
  saveManualQrPayments(gymId, [row, ...existing]);
  return row;
}

export function deleteManualQrPayment(gymId, id) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const existing = loadManualQrPayments(gymId);
  const next = existing.filter((r) => r.id !== id);
  if (next.length === existing.length) {
    throw new Error('QR payment not found.');
  }
  saveManualQrPayments(gymId, next);
  return true;
}

/* ─── Manual Expenses (Expense tab) ─── */

function expenseStorageKey(gymId) {
  return `gym:${gymId || 'default'}:manual_expenses`;
}

function expenseCategoryStorageKey(gymId) {
  return `gym:${gymId || 'default'}:expense_categories`;
}

function normalizeExpenseCategoryName(name) {
  return String(name || '').trim();
}

function normalizeExpenseCategoryRecord(row, fallbackIndex = 0) {
  if (typeof row === 'string') {
    const name = normalizeExpenseCategoryName(row);
    if (!name) return null;
    return {
      name,
      codeBase: (fallbackIndex + 1) * 1000,
    };
  }
  const name = normalizeExpenseCategoryName(row?.name || row?.category || '');
  if (!name) return null;
  const parsedBase = Number(row?.codeBase);
  const codeBase = Number.isFinite(parsedBase) && parsedBase >= 1000
    ? Math.floor(parsedBase / 1000) * 1000
    : (fallbackIndex + 1) * 1000;
  return { name, codeBase };
}

/** Full category records with stable code bases. */
export function loadExpenseCategoryRecords(gymId) {
  if (!gymId || typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(expenseCategoryStorageKey(gymId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const seen = new Set();
    const records = [];
    let needsMigrate = false;
    parsed.forEach((row, index) => {
      if (typeof row === 'string') needsMigrate = true;
      const record = normalizeExpenseCategoryRecord(row, index);
      if (!record) return;
      const key = record.name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      records.push(record);
    });
    if (needsMigrate && records.length) {
      saveExpenseCategoryRecords(gymId, records);
    }
    return records;
  } catch {
    return [];
  }
}

/** Category names only (order preserved). */
export function loadExpenseCategories(gymId) {
  return loadExpenseCategoryRecords(gymId).map((r) => r.name);
}

function saveExpenseCategoryRecords(gymId, rows) {
  if (!gymId || typeof window === 'undefined') return;
  localStorage.setItem(expenseCategoryStorageKey(gymId), JSON.stringify(rows || []));
}

export function addExpenseCategory(gymId, name) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const category = normalizeExpenseCategoryName(name);
  if (!category) throw new Error('Category name is required.');
  const existing = loadExpenseCategoryRecords(gymId);
  if (existing.some((c) => c.name.toLowerCase() === category.toLowerCase())) {
    throw new Error(`Category "${category}" already exists.`);
  }
  if (existing.length >= 9) {
    throw new Error('Maximum of 9 expense categories reached (E1000–E9000).');
  }
  const usedBases = new Set(existing.map((c) => c.codeBase));
  let codeBase = 1000;
  while (usedBases.has(codeBase) && codeBase <= 9000) {
    codeBase += 1000;
  }
  if (codeBase > 9000) {
    throw new Error('No free category code range available.');
  }
  const record = { name: category, codeBase };
  saveExpenseCategoryRecords(gymId, [...existing, record]);
  return {
    category,
    codeBase,
    startCode: `E${codeBase}`,
  };
}

export function deleteExpenseCategory(gymId, name, { deleteExpenses = true } = {}) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const key = normalizeExpenseCategoryName(name).toLowerCase();
  if (!key) throw new Error('Category name is required.');

  const existing = loadExpenseCategoryRecords(gymId);
  const next = existing.filter((c) => c.name.toLowerCase() !== key);
  if (next.length === existing.length) {
    throw new Error('Category not found.');
  }

  let removedExpenses = 0;
  if (deleteExpenses) {
    const expenses = loadManualExpenses(gymId);
    const kept = expenses.filter((row) => String(row.category || '').trim().toLowerCase() !== key);
    removedExpenses = expenses.length - kept.length;
    if (removedExpenses > 0) {
      saveManualExpenses(gymId, kept);
    }
  }

  saveExpenseCategoryRecords(gymId, next);
  return { removedExpenses };
}

/**
 * Stable per-category range: 1st free slot → 1000, next → 2000, etc.
 */
/** Replace local category cache (used after loading from Supabase). */
export function syncExpenseCategoryRecords(gymId, records = []) {
  if (!gymId) return [];
  const normalized = (records || [])
    .map((row, index) => normalizeExpenseCategoryRecord(row, index))
    .filter(Boolean);
  saveExpenseCategoryRecords(gymId, normalized);
  return normalized;
}

export function getExpenseCategoryCodeBase(gymId, categoryName, categoryRecords = null) {
  const category = normalizeExpenseCategoryName(categoryName);
  const records = categoryRecords || loadExpenseCategoryRecords(gymId);
  if (!category) {
    return records[0]?.codeBase || 1000;
  }
  const found = records.find((c) => c.name.toLowerCase() === category.toLowerCase());
  if (found) return found.codeBase;
  // Unregistered / legacy category: place after highest used base
  const maxBase = records.reduce((max, c) => Math.max(max, c.codeBase || 0), 0);
  return maxBase + 1000 || 1000;
}

export function formatExpenseCategoryCodeRange(gymId, categoryName) {
  const base = getExpenseCategoryCodeBase(gymId, categoryName);
  return `E${base}`;
}

/** Categories available in the add-expense form and for green group rows. */
export function getExpenseCategoryOptions(gymId, expenses = []) {
  const seen = new Set();
  const out = [];
  const push = (name) => {
    const category = normalizeExpenseCategoryName(name);
    if (!category) return;
    const key = category.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(category);
  };
  // Registered categories first (order defines E1000 / E2000 / E3000…)
  loadExpenseCategories(gymId).forEach(push);
  (expenses || []).forEach((row) => push(row.category));
  return out;
}

function normalizeManualExpense(row = {}) {
  const amount = Math.max(0, Number(row.amount) || 0);
  const methodRaw = String(row.method || row.paymentMethod || row.type || '').trim().toLowerCase();
  const method = methodRaw === 'bank'
    || methodRaw === 'card'
    || methodRaw === 'qr'
    || methodRaw === 'online'
    || methodRaw === 'transfer'
    ? 'bank'
    : 'cash';
  return {
    id: String(row.id || `EXP-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
    code: String(row.code || row.expenseCode || '').trim(),
    accountTitle: String(row.accountTitle || row.title || '').trim(),
    description: String(row.description || '').trim(),
    category: normalizeExpenseCategoryName(row.category) || 'Other',
    branch: String(row.branch || row.branchName || '').trim(),
    amount,
    date: String(row.date || localDateKey()).slice(0, 10),
    method,
    paymentMethod: method,
    createdAt: String(row.createdAt || localDateKey()).slice(0, 10),
  };
}

/** Sort helper: E1000 < E1001 < E2000 (numeric ascending by code). */
export function compareExpenseCodes(a, b) {
  const codeA = String(a?.code ?? a?.ref ?? a ?? '').trim().toUpperCase();
  const codeB = String(b?.code ?? b?.ref ?? b ?? '').trim().toUpperCase();
  const numA = Number(codeA.replace(/\D/g, '')) || 0;
  const numB = Number(codeB.replace(/\D/g, '')) || 0;
  if (numA !== numB) return numA - numB;
  return codeA.localeCompare(codeB);
}

export function loadManualExpenses(gymId) {
  if (!gymId || typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(expenseStorageKey(gymId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeManualExpense)
      .filter((row) => row.code && row.accountTitle && row.amount > 0)
      .sort(compareExpenseCodes);
  } catch {
    return [];
  }
}

function saveManualExpenses(gymId, rows) {
  if (!gymId || typeof window === 'undefined') return;
  localStorage.setItem(expenseStorageKey(gymId), JSON.stringify(rows || []));
}

export function suggestNextExpenseCode(gymId, categoryName, expenses = null) {
  const base = getExpenseCategoryCodeBase(gymId, categoryName);
  const rows = expenses || loadManualExpenses(gymId);
  const used = new Set(
    rows.map((r) => String(r.code || '').trim().toUpperCase()).filter(Boolean),
  );
  for (let n = 0; n < 1000; n += 1) {
    const code = `E${base + n}`;
    if (!used.has(code.toUpperCase())) return code;
  }
  return `E${base + Date.now().toString().slice(-3)}`;
}

/** Map a Supabase expense row into Chart of Accounts expense-entry shape. */
export function mapApiExpenseToManual(row = {}) {
  const title = String(row.accountTitle || row.title || '').trim();
  const description = String(row.description || '').trim();
  const side = String(row.side || '').toLowerCase().trim() === 'credit' ? 'credit' : 'debit';
  const debit = Number(row.debit) || (side === 'debit' ? Number(row.amount) || 0 : 0);
  const credit = Number(row.credit) || (side === 'credit' ? Number(row.amount) || 0 : 0);
  const amount = Math.max(debit, credit, Number(row.amount) || 0);
  const pettyColumn = String(row.pettyColumn || '').toLowerCase().trim();
  const normalized = normalizeManualExpense({
    id: row.id,
    code: row.code || row.expenseCode || '',
    accountTitle: title || description || 'Expense',
    description: title ? description : '',
    category: row.category || 'Other',
    branch: row.branch || '',
    amount,
    date: row.date || row.expense_date || row.entry_date || '',
    method: row.method || row.paymentMethod || row.type || 'cash',
    paymentMethod: row.paymentMethod || row.method || row.type || 'cash',
    createdAt: row.date || row.expense_date || row.entry_date || '',
  });
  return {
    ...normalized,
    parentCode: row.parentCode || '',
    accountCode: row.accountCode || row.code || normalized.code || '',
    source: row.source || '',
    type: row.type || (normalized.method === 'bank' ? 'Bank' : 'Cash'),
    ref: row.ref || '',
    side,
    debit,
    credit,
    pettyColumn: pettyColumn === 'receipts' || pettyColumn === 'payments' ? pettyColumn : '',
  };
}

/** Merge API + local expenses (API wins on same id). Preserves CoA debit/credit side. */
export function mergeManualExpenses(apiRows = [], localRows = []) {
  const seen = new Set();
  const out = [];
  [...(apiRows || []), ...(localRows || [])].forEach((row) => {
    const normalized = normalizeManualExpense(row);
    if (!normalized.accountTitle || !(normalized.amount > 0)) return;
    const key = String(normalized.id);
    if (seen.has(key)) return;
    seen.add(key);
    const side = String(row.side || '').toLowerCase().trim() === 'credit' ? 'credit' : 'debit';
    const debit = Number(row.debit) || (side === 'debit' ? normalized.amount : 0);
    const credit = Number(row.credit) || (side === 'credit' ? normalized.amount : 0);
    const pettyColumn = String(row.pettyColumn || '').toLowerCase().trim();
    out.push({
      ...normalized,
      parentCode: row.parentCode || '',
      accountCode: row.accountCode || row.code || normalized.code || '',
      source: row.source || '',
      type: row.type || (normalized.method === 'bank' ? 'Bank' : 'Cash'),
      ref: row.ref || '',
      side,
      debit,
      credit,
      pettyColumn: pettyColumn === 'receipts' || pettyColumn === 'payments' ? pettyColumn : '',
    });
  });
  return out.sort(compareExpenseCodes);
}

/** Clear local expense cache after successful cloud sync. */
export function clearManualExpensesCache(gymId) {
  if (!gymId || typeof window === 'undefined') return;
  localStorage.removeItem(expenseStorageKey(gymId));
}

export function addManualExpense(gymId, form) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const accountTitle = String(form.accountTitle || '').trim();
  if (!accountTitle) throw new Error('Account title is required.');

  const amount = Number(form.amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('Enter a valid amount greater than zero.');
  }

  const category = normalizeExpenseCategoryName(form.category) || 'Other';
  const registered = loadExpenseCategories(gymId);
  if (!registered.some((c) => c.toLowerCase() === category.toLowerCase())) {
    throw new Error('Select a registered expense category.');
  }
  let code = String(form.code || '').trim();
  if (!code) code = suggestNextExpenseCode(gymId, category);

  const existing = loadManualExpenses(gymId);
  if (existing.some((r) => r.code.toLowerCase() === code.toLowerCase())) {
    throw new Error(`Code ${code} already exists.`);
  }

  const row = normalizeManualExpense({
    code,
    accountTitle,
    description: form.description,
    category,
    branch: form.branch,
    amount,
    date: form.date || localDateKey(),
    method: form.method || form.paymentMethod || 'cash',
    createdAt: localDateKey(),
  });
  saveManualExpenses(gymId, [row, ...existing]);
  return row;
}

export function deleteManualExpense(gymId, id) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const existing = loadManualExpenses(gymId);
  const next = existing.filter((r) => r.id !== id);
  if (next.length === existing.length) {
    throw new Error('Expense not found.');
  }
  saveManualExpenses(gymId, next);
  return true;
}

// ── Hierarchical Chart of Accounts (parent → manual children) ──────────────

/**
 * Parent accounts shown in the CoA tree menu.
 * Child accounts are created manually under each parent
 * (Payment and Sales also get live system children).
 */
export const COA_PARENT_ACCOUNTS = [
  { code: 'PAY', name: 'Payment', type: 'asset' },
  { code: 'ADV', name: 'Advances', type: 'asset' },
  { code: 'ORCV', name: 'Other Receivables', type: 'asset' },
  { code: 'CIH', name: 'Cash in Hand', type: 'asset' },
  { code: 'CHQ', name: 'Cheques in Hand', type: 'asset' },
  { code: 'CAB', name: 'Cash at Bank', type: 'asset' },
  { code: 'OFA', name: 'Operating Fixed Assets', type: 'asset' },
  { code: 'SALE', name: 'Sales', type: 'income' },
  { code: 'SRET', name: 'Sales Returns', type: 'income' },
  { code: 'COS', name: 'Cost of Sales', type: 'expense' },
  { code: 'OOI', name: 'Other Operating Income', type: 'income' },
  { code: 'ADM', name: 'Admin Expenses', type: 'expense' },
  { code: 'SEL', name: 'Selling Expenses', type: 'expense' },
  { code: 'FIN', name: 'Financial Charges', type: 'expense' },
  { code: 'OEXP', name: 'Other Expenses', type: 'expense' },
  { code: 'OINC', name: 'Other Income', type: 'income' },
  { code: 'DCR', name: 'Discount and Commission Received', type: 'income' },
  { code: 'ADJ', name: 'Adjustments', type: 'asset' },
  { code: 'LTL', name: 'Long Term Loans', type: 'liability' },
  { code: 'STL', name: 'Short Term Loans', type: 'liability' },
  { code: 'PEARL', name: 'Pearl Group', type: 'expense' },
  { code: 'TCR', name: 'Trade Creditors', type: 'liability' },
  { code: 'OPAY', name: 'Other Payables', type: 'liability' },
  { code: 'TAX', name: 'Tax Payable', type: 'liability' },
];

/** Admin / Selling / Other — ledger entries can be Cash or Bank and feed the Z Report. */
export const COA_CASH_BANK_EXPENSE_PARENTS = ['ADM', 'SEL', 'OEXP'];

/** Long / Short Term Loans — auto-fill PEARL GROUP lines on Daily Z (separate from Gym expenses). */
export const COA_Z_REPORT_LOAN_PARENTS = ['LTL', 'STL'];

/** Seeded Pearl Group children — roll up LTL/STL loan ledger for CoA display. */
export const COA_PEARL_CHILDREN = [
  {
    code: 'PEARL-GRP',
    name: 'Pearl Group',
    aliases: ['pearl group', 'pearl'],
  },
  {
    code: 'PEARL-EXP',
    name: 'Pearl Group Expenses',
    aliases: ['pearl group expenses', 'pearl expenses', 'pearl expense'],
  },
];
export function isCoaCashBankExpenseParent(code) {
  return COA_CASH_BANK_EXPENSE_PARENTS.includes(String(code || '').trim().toUpperCase());
}

export function isCoaZReportLoanParent(code) {
  return COA_Z_REPORT_LOAN_PARENTS.includes(String(code || '').trim().toUpperCase());
}
/** System children under Payment — ledger loaded from live payment streams. */
export const COA_PAYMENT_CHILDREN = [
  { code: 'PAY-CASH', name: 'Cash', stream: 'cash' },
  { code: 'PAY-CARD', name: 'Card', stream: 'card' },
  { code: 'PAY-BANK', name: 'Bank', stream: 'bank' },
  { code: 'PAY-QR', name: 'QR', stream: 'qr' },
];

/**
 * Tender ledgers under Cash in Hand / Other Receivables.
 * Live cash → CIH child; bank/card/QR → ORCV children.
 */
export const COA_CIH_CHILDREN = [
  { code: 'CIH-CASH', name: 'Cash in Hand', stream: 'cash', aliases: ['cash in hand', 'cash'] },
];

export const COA_ORCV_CHILDREN = [
  {
    code: 'ORCV-BANK',
    name: 'Bank Receivables',
    stream: 'bank',
    aliases: ['bank receivables', 'bank receivable', 'bank'],
  },
  {
    code: 'ORCV-CARD',
    name: 'Credit Card Receivables',
    stream: 'card',
    aliases: ['credit card receivables', 'credit card receivable', 'credit card', 'card receivables', 'card'],
  },
  {
    code: 'ORCV-QR',
    name: 'QR Receivables',
    stream: 'qr',
    aliases: ['qr receivables', 'qr receivable', 'qr'],
  },
];

/** All seeded tender child defs (CIH + ORCV). */
export const COA_TENDER_CHILDREN = [...COA_CIH_CHILDREN, ...COA_ORCV_CHILDREN];

/** Map legacy Payment codes (and aliases) onto CIH / ORCV tender children. */
export const COA_TENDER_CODE_ALIASES = {
  'PAY-CASH': 'CIH-CASH',
  'PAY-BANK': 'ORCV-BANK',
  'PAY-CARD': 'ORCV-CARD',
  'PAY-QR': 'ORCV-QR',
  'CIH-CASH': 'CIH-CASH',
  'ORCV-BANK': 'ORCV-BANK',
  'ORCV-CARD': 'ORCV-CARD',
  'ORCV-QR': 'ORCV-QR',
};

export function normalizeCoaAccountLabel(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function resolveTenderChildCode(codeOrName) {
  const raw = String(codeOrName || '').trim().toUpperCase();
  if (COA_TENDER_CODE_ALIASES[raw]) return COA_TENDER_CODE_ALIASES[raw];
  const label = normalizeCoaAccountLabel(codeOrName);
  if (!label) return '';
  const hit = COA_TENDER_CHILDREN.find((def) => (
    normalizeCoaAccountLabel(def.name) === label
    || (def.aliases || []).some((a) => normalizeCoaAccountLabel(a) === label)
  ));
  return hit?.code || '';
}

export function saleCategoryToChildCode(category) {
  const slug = String(category || 'Other')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'OTHER';
  return `SALE-${slug}`;
}

function coaChildStorageKey(gymId) {
  return `gym:${gymId || 'default'}:coa_child_accounts`;
}

function normalizeCoaLedgerEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const debit = Math.max(0, Number(entry.debit) || 0);
  const credit = Math.max(0, Number(entry.credit) || 0);
  if (debit <= 0 && credit <= 0) return null;
  return {
    id: String(entry.id || `le-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    date: String(entry.date || localDateKey()).slice(0, 10),
    description: String(entry.description || '').trim() || '—',
    debit,
    credit,
    ref: String(entry.ref || '').trim() || '—',
    childCode: entry.childCode ? String(entry.childCode) : undefined,
    childName: entry.childName ? String(entry.childName) : undefined,
  };
}

function normalizeCoaChildAccount(row) {
  if (!row || typeof row !== 'object') return null;
  const code = String(row.code || '').trim().toUpperCase();
  const parentCode = String(row.parentCode || '').trim().toUpperCase();
  const name = String(row.name || '').trim();
  if (!code || !parentCode || !name) return null;
  const entries = Array.isArray(row.entries)
    ? row.entries.map(normalizeCoaLedgerEntry).filter(Boolean)
    : [];
  return {
    id: String(row.id || code),
    code,
    parentCode,
    name,
    description: String(row.description || '').trim(),
    createdAt: String(row.createdAt || localDateKey()).slice(0, 10),
    entries,
  };
}

export function loadCoaChildAccounts(gymId) {
  if (!gymId || typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(coaChildStorageKey(gymId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeCoaChildAccount).filter(Boolean);
  } catch {
    return [];
  }
}

export function saveCoaChildAccounts(gymId, rows) {
  if (!gymId || typeof window === 'undefined') return;
  localStorage.setItem(coaChildStorageKey(gymId), JSON.stringify(rows || []));
}

export function getCoaParentAccounts() {
  return (COA_PARENT_ACCOUNTS || [])
    .map((p) => ({
      code: String(p.code || '').trim().toUpperCase(),
      name: String(p.name || '').trim(),
      type: String(p.type || 'asset').trim().toLowerCase(),
    }))
    .filter((p) => p.code && p.name);
}

export function suggestNextCoaChildCode(parentCode, gymIdOrUsedCodes) {
  const parent = String(parentCode || '').trim().toUpperCase() || 'CHILD';
  const prefix = parent.slice(0, 4);
  const used = new Set(
    Array.isArray(gymIdOrUsedCodes)
      ? gymIdOrUsedCodes.map((c) => String(c || '').toUpperCase())
      : loadCoaChildAccounts(gymIdOrUsedCodes).map((c) => c.code),
  );
  for (let n = 1; n < 1000; n += 1) {
    const code = `${prefix}-${String(n).padStart(3, '0')}`;
    if (!used.has(code)) return code;
  }
  return `${prefix}-${Date.now().toString().slice(-4)}`;
}

export function addCoaChildAccount(gymId, form) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const parentCode = String(form.parentCode || '').trim().toUpperCase();
  const parents = getCoaParentAccounts();
  if (!parents.some((p) => p.code === parentCode)) {
    throw new Error('Select a valid parent account first.');
  }
  const name = String(form.name || '').trim();
  if (!name) throw new Error('Child account name is required.');
  const existing = loadCoaChildAccounts(gymId);
  let code = String(form.code || '').trim().toUpperCase() || suggestNextCoaChildCode(parentCode, gymId);
  if (existing.some((c) => c.code === code)) {
    throw new Error(`Child account code ${code} already exists.`);
  }
  const row = normalizeCoaChildAccount({
    id: `child-${Date.now()}`,
    code,
    parentCode,
    name,
    description: form.description || '',
    createdAt: localDateKey(),
    entries: [],
  });
  saveCoaChildAccounts(gymId, [row, ...existing]);
  return row;
}

export function deleteCoaChildAccount(gymId, code) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const needle = String(code || '').trim().toUpperCase();
  const existing = loadCoaChildAccounts(gymId);
  const next = existing.filter((c) => c.code !== needle);
  if (next.length === existing.length) throw new Error('Child account not found.');
  saveCoaChildAccounts(gymId, next);
  return true;
}

export function addCoaChildLedgerEntry(gymId, childCode, form) {
  if (!gymId) throw new Error('No gym linked to this account.');
  const code = String(childCode || '').trim().toUpperCase();
  const existing = loadCoaChildAccounts(gymId);
  const idx = existing.findIndex((c) => c.code === code);
  if (idx < 0) throw new Error('Child account not found.');
  const entry = normalizeCoaLedgerEntry({
    ...form,
    id: `le-${Date.now()}`,
    childCode: code,
    childName: existing[idx].name,
  });
  if (!entry) throw new Error('Enter a debit or credit amount.');
  const updated = {
    ...existing[idx],
    entries: [entry, ...(existing[idx].entries || [])],
  };
  const next = [...existing];
  next[idx] = updated;
  saveCoaChildAccounts(gymId, next);
  return entry;
}

export function getCoaTreeForGym(gymId) {
  const parents = getCoaParentAccounts();
  const children = loadCoaChildAccounts(gymId);
  return parents.map((parent) => ({
    ...parent,
    children: children
      .filter((c) => c.parentCode === parent.code)
      .sort((a, b) => String(a.code).localeCompare(String(b.code))),
  }));
}

/** Ledger rows for a parent (all children) or a single child. */
export function getCoaLedgerEntries(gymId, { parentCode, childCode } = {}) {
  const children = loadCoaChildAccounts(gymId);
  let rows = [];
  if (childCode) {
    const child = children.find((c) => c.code === String(childCode).toUpperCase());
    rows = (child?.entries || []).map((e) => ({
      ...e,
      childCode: child.code,
      childName: child.name,
    }));
  } else if (parentCode) {
    const parent = String(parentCode).toUpperCase();
    children
      .filter((c) => c.parentCode === parent)
      .forEach((child) => {
        (child.entries || []).forEach((e) => {
          rows.push({
            ...e,
            childCode: child.code,
            childName: child.name,
          });
        });
      });
  }
  return rows.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

/**
 * Reception collections onto a CoA child — Debit only.
 * Credit is reserved for voucher / ledger entries the admin posts by hand.
 */
function mapCashRowsToLedger(rows = [], childMeta = {}) {
  return dedupeMappedLedgerRows((rows || []).map((p) => {
    const amount = Number(p.payment) || 0;
    if (amount <= 0) return null;
    const baseId = String(p.id || `${p.code || p.date}`);
    return {
      id: String(p.id || `${childMeta.code}-${baseId}`),
      date: String(p.date || '').slice(0, 10) || '—',
      description: [p.accountTitle, p.description].filter(Boolean).join(' — ') || 'Cash payment',
      debit: amount,
      credit: 0,
      ref: String(p.code || p.gymId || '—'),
      childCode: childMeta.code,
      childName: childMeta.name,
    };
  }).filter((e) => e && e.debit > 0));
}

/**
 * Card / Bank / QR collections onto a CoA child — Debit only.
 */
function mapTenderRowsToLedger(rows = [], childMeta = {}, label = 'Payment') {
  return dedupeMappedLedgerRows((rows || []).map((p) => {
    const amount = Number(p.debit) || Number(p.payment) || 0;
    if (amount <= 0) return null;
    const baseId = String(p.id || `${p.code || p.date}`);
    return {
      id: String(p.id || `${childMeta.code}-${baseId}`),
      date: String(p.date || '').slice(0, 10) || '—',
      description: [p.accountTitle, p.description].filter(Boolean).join(' — ') || label,
      debit: amount,
      credit: 0,
      ref: String(p.code || p.gymId || '—'),
      childCode: childMeta.code,
      childName: childMeta.name,
    };
  }).filter((e) => e && e.debit > 0));
}

/** Collapse identical ledger lines (live + manual overlap / clone fingerprints). */
function dedupeMappedLedgerRows(rows = []) {
  const seen = new Set();
  const out = [];
  (rows || []).forEach((entry) => {
    if (!entry) return;
    const desc = String(entry.description || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    const ref = String(entry.ref || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    const soft = [
      String(entry.date || '').slice(0, 10),
      ref,
      Number(entry.debit) || 0,
      Number(entry.credit) || 0,
      desc,
    ].join('|');
    const hard = String(entry.id || '');
    if (hard && seen.has(`id:${hard}`)) return;
    if (seen.has(`soft:${soft}`)) return;
    if (hard) seen.add(`id:${hard}`);
    seen.add(`soft:${soft}`);
    out.push(entry);
  });
  return out;
}

function mapSalesRowsToLedger(rows = [], childMeta = {}) {
  return (rows || []).map((p) => {
    const partialTag = p.isPartial ? ' (partial)' : '';
    const descParts = [p.member, p.item].filter((v) => v && v !== '—');
    const description = descParts.length
      ? `${descParts.join(' — ')}${partialTag}`
      : childMeta.name;
    const date = coaSaleLedgerDate(p)
      || paymentDate(p)
      || (String(p.date || '').match(/^\d{4}-\d{2}-\d{2}/)?.[0])
      || '—';
    return {
      id: String(p.id || `${childMeta.code}-${p.code || date}`),
      date,
      description,
      debit: 0,
      credit: Number(p.amount) || 0,
      ref: String(p.code || p.gymId || '—'),
      childCode: childMeta.code,
      childName: childMeta.name,
      gymId: p.gymId,
      method: p.method,
    };
  }).filter((e) => e.credit > 0);
}

function mergeLedgerEntryLists(...lists) {
  const seen = new Set();
  const rows = [];
  lists.flat().forEach((entry) => {
    if (!entry) return;
    const key = String(
      entry.id
      || `${entry.date}|${entry.ref}|${entry.debit}|${entry.credit}|${entry.description}`,
    );
    if (seen.has(key)) return;
    seen.add(key);
    rows.push(entry);
  });
  return rows.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

function findMatchingTenderChild(children = [], def) {
  const code = String(def.code || '').toUpperCase();
  const byCode = (children || []).find((c) => String(c.code || '').toUpperCase() === code);
  if (byCode) return byCode;
  const aliases = new Set([
    normalizeCoaAccountLabel(def.name),
    ...(def.aliases || []).map(normalizeCoaAccountLabel),
  ].filter(Boolean));
  return (children || []).find((c) => aliases.has(normalizeCoaAccountLabel(c.name))) || null;
}

function tenderChildHasStoredLedger(entries = []) {
  return (entries || []).some((entry) => {
    if (!entry) return false;
    if (isOpeningBalanceLedgerEntry(entry)) return false;
    const debit = Number(entry.debit) || 0;
    const credit = Number(entry.credit) || 0;
    return debit > 0 || credit > 0;
  });
}

function ledgerRowFingerprint(entry) {
  const date = String(entry?.date || '').slice(0, 10);
  const amount = Number(entry?.debit) || Number(entry?.credit) || 0;
  const desc = String(entry?.description || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  const ref = String(entry?.ref || entry?.reference || '')
    .trim()
    .toLowerCase();
  return { date, amount, desc, ref };
}

/**
 * Drop stored clones of auto collections (old Debit+Credit pairing) so the
 * same reception amount is not shown again on Credit. Keep real voucher lines.
 */
function storedEntriesExcludingLiveClones(stored = [], liveEntries = []) {
  const live = (liveEntries || []).map(ledgerRowFingerprint);
  return (stored || []).filter((entry) => {
    if (isOpeningBalanceLedgerEntry(entry)) return true;
    const fp = ledgerRowFingerprint(entry);
    if (!(fp.amount > 0) || !fp.date) return true;
    const isClone = live.some((row) => {
      if (row.date !== fp.date || Math.abs(row.amount - fp.amount) > 0.01) return false;
      if (fp.ref && row.ref && fp.ref !== '—' && row.ref !== '—' && fp.ref === row.ref) {
        return true;
      }
      if (fp.desc && row.desc && fp.desc === row.desc) return true;
      return false;
    });
    return !isClone;
  });
}

/**
 * Attach live tender rows onto CIH / ORCV as Debit (reception collections).
 * Credit stays empty unless the admin posts a voucher / ledger entry.
 */
function mergeTenderParentChildren(parentCode, children = [], defs = [], paymentStreams = {}) {
  const remaining = [...(children || [])];
  const merged = [];

  defs.forEach((def) => {
    const match = findMatchingTenderChild(remaining, def);
    const liveMeta = { code: match?.code || def.code, name: match?.name || def.name };
    const liveEntries = def.stream === 'cash'
      ? mapCashRowsToLedger(paymentStreams.cashRaw || [], liveMeta)
      : mapTenderRowsToLedger(
        paymentStreams[`${def.stream}Raw`] || [],
        liveMeta,
        `${def.name} payment`,
      );

    if (match) {
      const idx = remaining.findIndex((c) => c.code === match.code);
      if (idx >= 0) remaining.splice(idx, 1);
      const stored = storedEntriesExcludingLiveClones(match.entries || [], liveEntries);
      merged.push({
        ...match,
        description: match.description
          || `Linked debit — live ${def.name} collections`,
        entries: mergeLedgerEntryLists(liveEntries, stored),
      });
      return;
    }

    merged.push({
      id: def.code,
      code: def.code,
      parentCode,
      name: def.name,
      description: `Linked debit — live ${def.name} collections`,
      isSystem: true,
      entries: liveEntries,
    });
  });

  return [...merged, ...remaining];
}

function collectLoanLedgerEntriesFromTree(tree = []) {
  const out = [];
  (tree || []).forEach((parent) => {
    const parentCode = String(parent.code || '').toUpperCase();
    if (parentCode !== 'LTL' && parentCode !== 'STL') return;
    (parent.children || []).forEach((child) => {
      (child.entries || []).forEach((entry) => {
        if (!entry) return;
        out.push({
          ...entry,
          id: entry.id ? `pearl-${entry.id}` : undefined,
          childCode: 'PEARL-EXP',
          childName: 'Pearl Group Expenses',
          description: entry.description
            || [child.name, entry.ref].filter(Boolean).join(' — ')
            || 'Pearl Group expense',
        });
      });
    });
  });
  return dedupeMappedLedgerRows(out);
}

/**
 * Attach live Payment (Cash/Card/Bank/QR) and Sales category children to the CoA tree.
 * Auto-linked tender ledgers (PAY, CIH, ORCV) show collections on Debit only.
 * Credit on those accounts is manual voucher / ledger entries.
 * Pearl Group Expenses rolls up LTL/STL loan ledger lines for CoA totals.
 */
export function mergeCoaTreeWithLiveData(tree = [], live = {}) {
  const cash = live.cash || [];
  const card = live.card || [];
  const bank = live.bank || [];
  const qr = live.qr || [];
  const salesRows = live.salesEntries || [];
  const pearlLoanEntries = collectLoanLedgerEntriesFromTree(tree);

  const paymentStreams = {
    cash: mapCashRowsToLedger(cash, { code: 'PAY-CASH', name: 'Cash' }),
    card: mapTenderRowsToLedger(card, { code: 'PAY-CARD', name: 'Card' }, 'Card payment'),
    bank: mapTenderRowsToLedger(bank, { code: 'PAY-BANK', name: 'Bank' }, 'Bank payment'),
    qr: mapTenderRowsToLedger(qr, { code: 'PAY-QR', name: 'QR' }, 'QR payment'),
    cashRaw: cash,
    cardRaw: card,
    bankRaw: bank,
    qrRaw: qr,
  };

  const salesByCategory = new Map();
  SALE_CATEGORY_ORDER.forEach((category) => {
    salesByCategory.set(category, []);
  });
  (salesRows || []).forEach((row) => {
    const category = String(row.category || 'Membership').trim() || 'Membership';
    if (!salesByCategory.has(category)) salesByCategory.set(category, []);
    salesByCategory.get(category).push(row);
  });

  return (tree || []).map((parent) => {
    const code = String(parent.code || '').toUpperCase();
    const manualChildren = (parent.children || []).filter((c) => !c.isSystem);

    if (code === 'PAY') {
      const systemChildren = COA_PAYMENT_CHILDREN.map((def) => ({
        id: def.code,
        code: def.code,
        parentCode: 'PAY',
        name: def.name,
        description: `Live ${def.name} payment ledger`,
        isSystem: true,
        entries: paymentStreams[def.stream] || [],
      }));
      return {
        ...parent,
        children: [...systemChildren, ...manualChildren],
      };
    }

    if (code === 'CIH') {
      return {
        ...parent,
        children: mergeTenderParentChildren('CIH', manualChildren, COA_CIH_CHILDREN, paymentStreams),
      };
    }

    if (code === 'ORCV') {
      return {
        ...parent,
        children: mergeTenderParentChildren('ORCV', manualChildren, COA_ORCV_CHILDREN, paymentStreams),
      };
    }

    if (code === 'PEARL') {
      const remaining = [...manualChildren];
      const merged = COA_PEARL_CHILDREN.map((def) => {
        const match = findMatchingTenderChild(remaining, def);
        if (match) {
          const idx = remaining.findIndex((c) => c.code === match.code);
          if (idx >= 0) remaining.splice(idx, 1);
        }
        const stored = match?.entries || [];
        const isExpenses = String(def.code).toUpperCase() === 'PEARL-EXP';
        const liveEntries = isExpenses ? pearlLoanEntries : [];
        const preferStored = tenderChildHasStoredLedger(stored) && !isExpenses;
        return {
          id: match?.id || def.code,
          code: match?.code || def.code,
          parentCode: 'PEARL',
          name: match?.name || def.name,
          description: match?.description
            || (isExpenses ? 'LTL/STL loan expenses (Pearl Group)' : 'Pearl Group'),
          isSystem: !match,
          entries: preferStored
            ? dedupeMappedLedgerRows(stored)
            : mergeLedgerEntryLists(liveEntries, stored),
        };
      });
      return {
        ...parent,
        children: [...merged, ...remaining],
      };
    }

    if (code === 'SALE') {
      const systemChildren = [];
      salesByCategory.forEach((rows, category) => {
        const childCode = saleCategoryToChildCode(category);
        const meta = { code: childCode, name: category };
        systemChildren.push({
          id: childCode,
          code: childCode,
          parentCode: 'SALE',
          name: category,
          description: `Live ${category} sales`,
          isSystem: true,
          entries: mapSalesRowsToLedger(rows, meta),
        });
      });
      // Keep fixed category order first, then any extra categories
      const orderedCodes = new Set(SALE_CATEGORY_ORDER.map(saleCategoryToChildCode));
      const ordered = SALE_CATEGORY_ORDER.map((category) => (
        systemChildren.find((c) => c.name === category)
      )).filter(Boolean);
      const extras = systemChildren.filter((c) => !orderedCodes.has(c.code));
      return {
        ...parent,
        children: [...ordered, ...extras, ...manualChildren],
      };
    }

    return {
      ...parent,
      children: manualChildren,
    };
  });
}
